import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import FullScreenPage from '../components/FullScreenPage';
import QuickAddMealCard from '../components/QuickAddMealCard';
import EditQuickMealModal from '../components/EditQuickMealModal';
import { useQuery, useMutation, useQueryClient, useInfiniteQuery } from '@tanstack/react-query';
import { useAuth } from '../contexts/AuthContext';
import { useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../components/ui/sheet';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ScrollArea } from '../components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { haptics } from '../utils/haptics';
import {
  Apple, Plus, Search, Flame, Beef, Wheat, Droplets, ChevronLeft, ChevronRight, ChevronDown, ChevronUp, X,
  Bookmark, BookmarkPlus, Zap, TrendingUp, TrendingDown, Calendar, BarChart3, Target, Check,
  Pencil, Trash2, Loader2, Camera, Scan, Share2, Clock, Star, UtensilsCrossed, PieChart, BookOpen, ImagePlus,
  CheckCircle, Lock, Calculator, ArrowLeftRight, Database, Settings as SettingsIcon, Cookie
} from 'lucide-react';
import client from '../api/client';
import { useTranslation } from 'react-i18next';
import { addDays, subDays } from 'date-fns';
import { formatDate, formatDateHeader, formatDateWithMonth, formatDateForAPI } from '../lib/dateUtils';
import BarcodeScanner from '../components/BarcodeScanner';
import { isValidBarcode } from '../lib/openFoodFacts';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine, Cell
} from 'recharts';
import { Tooltip as UITooltip, TooltipTrigger, TooltipContent } from '../components/ui/tooltip';
import InfoButton from '../components/ui/info-button';

// Parse FatSecret serving_description into a display-friendly format
// "1 large" → { isCountable: true, label: "large" }
// "100 g" or "" → { isCountable: false }
const parseServingDescription = (desc) => {
  if (!desc) return { isCountable: false };
  const match = desc.match(/^1\s+(.+)$/);
  if (match) return { isCountable: true, label: match[1] };
  return { isCountable: false };
};

// Build display quantity & unit from serving data
const buildServingDisplay = (servings, servingSize, servingUnit, servingDesc) => {
  const parsed = parseServingDescription(servingDesc);
  if (parsed.isCountable) {
    return { quantity: servings, unit: parsed.label };
  }
  return { quantity: (servingSize || 100) * servings, unit: servingUnit || 'g' };
};

import ImageCropper from '../components/ImageCropper';
import DietDayStoryGenerator from '../components/DietDayStoryGenerator';
import MacroSetupWizard from '../components/MacroSetupWizard';
import AdaptiveMacroCard from '../components/AdaptiveMacroCard';
import WaterTracker from '../components/WaterTracker';
import { useCountUp } from '../hooks/useCountUp';
import useSwipe from '../hooks/useSwipe';
import { useLongPress } from '../hooks/useLongPress';
import FoodEntryActionSheet from '../components/FoodEntryActionSheet';
import { useSubscription } from '../contexts/SubscriptionContext';
import ProLockOverlay from '../components/ProLockOverlay';
import { useRoutableDialog } from '../hooks/useRoutableDialog';
import { useConfirm } from '../hooks/useConfirm';
import { toastUndo } from '../lib/toastUndo';
import { TA } from '../components/tour/tourAnchors';
import { emit } from '../components/tour/tourEventBus';

const MEAL_TYPE_KEYS = [
  { value: 'breakfast', labelKey: 'diet.breakfast', icon: '🌅' },
  { value: 'lunch', labelKey: 'diet.lunch', icon: '☀️' },
  { value: 'dinner', labelKey: 'diet.dinner', icon: '🌙' },
  { value: 'snack', labelKey: 'diet.snacks', icon: '🍎' },
];

const FoodEntryRow = ({ children, onLongPress, ...rest }) => {
  const longPress = useLongPress(onLongPress, { thresholdMs: 500 });
  return (
    <div
      onTouchStart={longPress.onTouchStart}
      onTouchMove={longPress.onTouchMove}
      onTouchEnd={longPress.onTouchEnd}
      onTouchCancel={longPress.onTouchCancel}
      {...rest}
    >
      {children}
    </div>
  );
};

const MacroBar = ({ label, current, target, color, icon: Icon, displayCurrent }) => {
  const percentage = Math.min((current / target) * 100, 100);
  const isOver = current > target;
  const shown = displayCurrent ?? current;
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between text-sm">
        <div className="flex items-center gap-2">
          <Icon className={`h-4 w-4 ${color}`} />
          <span className="font-medium">{label}</span>
        </div>
        <span className={isOver ? 'text-red-500' : ''}>
          {Math.round(shown)}g / {Math.round(target)}g
        </span>
      </div>
      <Progress value={percentage} className={`h-2 ${isOver ? '[&>div]:bg-red-500' : ''}`} />
    </div>
  );
};

export default function DietPage() {
  const { t, i18n } = useTranslation();
  const MEAL_TYPES = useMemo(() => MEAL_TYPE_KEYS.map(m => ({ ...m, label: t(m.labelKey) })), [t]);
  const { user, refreshUser } = useAuth();
  const confirm = useConfirm();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const onBehalfOf = searchParams.get('on_behalf_of');
  const clientName = searchParams.get('client_name');
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [searchQuery, setSearchQuery] = useState('');
  const [searchLanguage, setSearchLanguage] = useState(i18n?.language?.startsWith('pt') ? 'pt' : 'en');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [showAddFoodDialog, setShowAddFoodDialog] = useState(false);
  const [selectedMealType, setSelectedMealType] = useState('breakfast');
  const [selectedFood, setSelectedFood] = useState(null);
  const [selectedServing, setSelectedServing] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [quantityInput, setQuantityInput] = useState('1');

  // Keep the raw input string in sync when quantity is changed from outside
  // (e.g. +/- buttons, food selection resets). Only update if the numeric
  // value actually differs from what's currently in the input, so typing
  // "2." or "2," doesn't get stripped back to "2".
  useEffect(() => {
    const parsed = parseFloat(String(quantityInput).replace(',', '.'));
    if (parsed !== quantity) {
      setQuantityInput(String(quantity));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quantity]);
  const [inlineEditIdx, setInlineEditIdx] = useState(null);
  const [inlineEditQty, setInlineEditQty] = useState(1);
  const [pendingFoods, setPendingFoods] = useState([]);
  const [showCustomFood, setShowCustomFood] = useState(false);
  const [showCustomFoodMore, setShowCustomFoodMore] = useState(false);
  const [editingFoodId, setEditingFoodId] = useState(null);
  const emptyCustomFood = {
    // Basic (required)
    name: '', calories: '', protein: '', carbs: '', fat: '', serving_size: '100', serving_unit: 'g',
    barcode: '',
    // More details — optional, mirrors the catalogue food schema
    name_en: '', name_pt: '', category: '', subcategory: '', brand: '', cooking_method: '',
    fiber: '', sugar: '', sodium: '', saturated_fat: '', cholesterol: '', potassium: '',
    allergens: [], preferences: [],
  };
  const [customFood, setCustomFood] = useState(emptyCustomFood);

  // FatSecret search state
  const [fatsecretResults, setFatsecretResults] = useState([]);
  const [offResults, setOffResults] = useState([]);
  const mergedResults = React.useMemo(() => [
    ...fatsecretResults,
    ...offResults,
  ], [fatsecretResults, offResults]);
  const [fatsecretLoading, setFatsecretLoading] = useState(false);
  const [communityResults, setCommunityResults] = useState([]);
  const [communityLoading, setCommunityLoading] = useState(false);
  const [fatsecretDetail, setFatsecretDetail] = useState(null);
  const [showNutritionDetail, setShowNutritionDetail] = useState(false);

  // FatSecret autocomplete state
  const [autocompleteResults, setAutocompleteResults] = useState([]);
  const [showAutocomplete, setShowAutocomplete] = useState(false);

  // FatSecret image recognition state (multiple foods from photo)
  const [photoFoods, setPhotoFoods] = useState([]);

  // Meal Templates state
  const [showSaveTemplateDialog, setShowSaveTemplateDialog] = useState(false);
  const [templateName, setTemplateName] = useState('');
  const [templateMealType, setTemplateMealType] = useState('breakfast');

  // Move/Copy meal state
  const [showMoveMealDialog, setShowMoveMealDialog] = useState(false);
  const [moveMealSource, setMoveMealSource] = useState(null);
  const [moveMealAction, setMoveMealAction] = useState('move'); // 'move' | 'copy'
  const [moveMealTarget, setMoveMealTarget] = useState(null);

  // Weekly Summary state
  const [showWeeklySummary, setShowWeeklySummary] = useState(false);
  const [showNutritionInsights, setShowNutritionInsights] = useState(false);
  const [insightsTab, setInsightsTab] = useState('calories');
  const [enrichedEntries, setEnrichedEntries] = useState(null);
  // Session cache for micros lookups by food_name. Survives multiple Insights opens
  // without re-fetching foods we've already enriched in this session.
  const microsCacheRef = useRef({});
  const [quickMealTarget, setQuickMealTarget] = useState(null);

  // Add Food dialog tab
  const [addFoodTab, setAddFoodTab] = useState('history');

  // Recipe state (FatSecret Premier)
  const [recipeResults, setRecipeResults] = useState([]);
  const [recipeLoading, setRecipeLoading] = useState(false);
  const [recipeQuery, setRecipeQuery] = useState('');
  const [selectedRecipe, setSelectedRecipe] = useState(null);
  const [recipeDetail, setRecipeDetail] = useState(null);
  const [recipeDetailLoading, setRecipeDetailLoading] = useState(false);

  // Recent Foods History — paginated via react-query so duplicate refetches
  // (e.g. mutation onSuccess fired in rapid succession) are deduplicated and cached.
  const recentScrollRef = useRef(null);

  // Edit Diet Log state
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editEntries, setEditEntries] = useState([]);

  // Barcode Scanner state
  const [showBarcodeScanner, setShowBarcodeScanner] = useState(false);
  const [scanningProduct, setScanningProduct] = useState(false);
  const [scannedProduct, setScannedProduct] = useState(null);

  // Photo meal scan state
  const [photoLoading, setPhotoLoading] = useState(false);
  const [photoResult, setPhotoResult] = useState(null);
  // Per-item state for AI multi-entry photo scans (v3.8.55)
  // [{ food_id, name, name_pt, originalGrams, grams, unit, calories, protein, carbs, fat, fiber, sugar, selected }]
  const [photoItems, setPhotoItems] = useState([]);
  const [photoMealGroupId, setPhotoMealGroupId] = useState(null);
  const [photoNotes, setPhotoNotes] = useState('');

  // Share preview state
  const [showSharePreview, setShowSharePreview] = useState(false);
  const [sharePreviewData, setSharePreviewData] = useState(null);
  const [sharePhoto, setSharePhoto] = useState(null);
  const [sharingPost, setSharingPost] = useState(false);

  // Complete Day state
  const [showCompleteConfirm, setShowCompleteConfirm] = useState(false);
  const [showUnlockConfirm, setShowUnlockConfirm] = useState(false);
  const [showDietDayStory, setShowDietDayStory] = useState(false);
  const [showMacroWizard, setShowMacroWizard] = useState(false);
  const [showMacroLockWarning, setShowMacroLockWarning] = useState(false);
  const [unlockingMacros, setUnlockingMacros] = useState(false);
  const [completingDay, setCompletingDay] = useState(false);
  const [unlockingDay, setUnlockingDay] = useState(false);
  const [showDietActivatePrompt, setShowDietActivatePrompt] = useState(false);
  const [showDietSettingsSheet, setShowDietSettingsSheet] = useState(false);
  const [showDietPlanProgress, setShowDietPlanProgress] = useState(false);
  // {entry, mealType} when a food row is long-pressed; null otherwise
  const [actionEntry, setActionEntry] = useState(null);

  // Food detail preview state
  const [previewFood, setPreviewFood] = useState(null);
  const [showFoodPreview, setShowFoodPreview] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);

  // ── Routable dialogs (URL-sync layer — ADDITIVE over local useState above) ──
  const addFoodDialog    = useRoutableDialog('add');
  const editEntryDialog  = useRoutableDialog('editEntry');
  const previewDialog    = useRoutableDialog('preview');
  const customFoodDialog = useRoutableDialog('customFood');
  const insightsDialog   = useRoutableDialog('panel');
  const editQuickMealDialog = useRoutableDialog('editQuickMeal');

  // Edit-quick-meal modal (local state — URL sync layered on below)
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [showEditQuickMeal, setShowEditQuickMeal] = useState(false);

  const openEditTemplate = (template) => {
    setEditingTemplate(template);
    setShowEditQuickMeal(true);
    editQuickMealDialog.open(template.id);
  };
  const closeEditTemplate = () => {
    setShowEditQuickMeal(false);
    editQuickMealDialog.close();
  };

  const dateStr = formatDateForAPI(selectedDate);

  const { data: foodLogData, isLoading: loading } = useQuery({
    queryKey: ['foodLog', dateStr, onBehalfOf],
    queryFn: async () => {
      const logUrl = onBehalfOf ? `/food-logs?client_id=${onBehalfOf}&date=${dateStr}` : `/food-logs/${dateStr}`;
      const res = await client.get(logUrl);
      return onBehalfOf ? (Array.isArray(res.data) ? res.data[0] || null : res.data) : res.data;
    },
  });
  const foodLog = foodLogData ?? null;

  const { data: foods = [] } = useQuery({
    queryKey: ['foods'],
    queryFn: async () => {
      const res = await client.get('/foods');
      return res.data;
    },
    staleTime: 30 * 60 * 1000, // custom foods list rarely changes
  });

  // Catalogue browse: fetches foods from the entire database (optionally filtered by category + search term).
  // Used by the Database tab (always) and by the Search tab when the user picks a category with no search text.
  // Debounce the search term so we don't hammer the backend on every keystroke.
  const [catalogueSearch, setCatalogueSearch] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setCatalogueSearch(searchQuery || ''), 300);
    return () => clearTimeout(t);
  }, [searchQuery]);

  const { data: catalogueFoods = [], isFetching: catalogueLoading } = useQuery({
    queryKey: ['foodsCatalogue', selectedCategory, catalogueSearch, searchLanguage],
    queryFn: async () => {
      const params = new URLSearchParams({ scope: 'all', language: searchLanguage });
      if (selectedCategory && selectedCategory !== 'all') params.set('category', selectedCategory);
      if (catalogueSearch && catalogueSearch.trim().length > 0) params.set('search', catalogueSearch.trim());
      const res = await client.get(`/foods?${params}`);
      return res.data;
    },
    enabled:
      addFoodTab === 'database' ||
      (addFoodTab === 'search' && selectedCategory !== 'all' && (!searchQuery || searchQuery.length < 2)),
    staleTime: 10 * 60 * 1000,
  });

  const { data: categoriesData } = useQuery({
    queryKey: ['foodCategories'],
    queryFn: async () => {
      const res = await client.get('/foods/categories');
      return res.data.categories || [];
    },
    staleTime: Infinity, // categories are static
  });
  const categories = categoriesData ?? [];

  const { data: dietSuggestions = [] } = useQuery({
    queryKey: ['dietSuggestions', user?.id],
    queryFn: async () => {
      const res = await client.get(`/diet-suggestions?client_id=${user.id}`);
      return res.data.filter(s => s.active !== false);
    },
    enabled: !!user,
    staleTime: 10 * 60 * 1000, // diet suggestions change infrequently
  });

  // Derived from today's active diet suggestion (if any)
  const todaySuggestion = useMemo(() => {
    const dayOfWeek = selectedDate.toLocaleDateString('en-US', { weekday: 'long' }).toLowerCase();
    return dietSuggestions.find(s =>
      s.days_of_week?.includes(dayOfWeek) || s.day_of_week === dayOfWeek
    ) || null;
  }, [selectedDate, dietSuggestions]);

  const activeMealList = MEAL_TYPES;

  // Reset selected meal type if it's no longer valid in the current meal list
  useEffect(() => {
    if (activeMealList.length > 0 && !activeMealList.some(m => m.value === selectedMealType)) {
      setSelectedMealType(activeMealList[0].value);
    }
  }, [activeMealList]);

  // Auto-show macro wizard if macros not yet customized
  useEffect(() => {
    if (user && !user.macros_customized) setShowMacroWizard(true);
  }, [user]);

  const { hasFeature, loading: subLoading } = useSubscription();

  const { data: mealTemplates = [] } = useQuery({
    queryKey: ['mealTemplates'],
    queryFn: async () => {
      const res = await client.get('/meal-templates');
      return res.data;
    },
    staleTime: 30 * 60 * 1000, // templates rarely change
  });

  const { data: weeklySummary = null } = useQuery({
    queryKey: ['weeklySummary'],
    queryFn: async () => {
      const res = await client.get('/nutrition-summary/weekly');
      return res.data;
    },
    staleTime: 2 * 60 * 1000, // refreshes after logging food
  });

  const { data: dietPlanProgress = null, isFetching: dietPlanProgressLoading } = useQuery({
    queryKey: ['dietPlanProgress', onBehalfOf],
    queryFn: async () => {
      const url = onBehalfOf ? `/diet/plan-progress?client_id=${onBehalfOf}` : '/diet/plan-progress';
      const res = await client.get(url);
      return res.data;
    },
    enabled: showDietPlanProgress,
    staleTime: 60 * 1000,
  });

  const {
    data: recentFoodsData,
    isLoading: recentFoodsLoading,
    fetchNextPage: fetchMoreRecentFoodsPage,
    hasNextPage: hasMoreRecentFoods,
    isFetchingNextPage: recentFoodsLoadingMore,
  } = useInfiniteQuery({
    queryKey: ['recentFoods'],
    queryFn: async ({ pageParam = 0 }) => {
      const res = await client.get(`/foods/recent?limit=20&offset=${pageParam}`);
      return res.data;
    },
    getNextPageParam: (lastPage, pages) => {
      const loaded = pages.reduce((sum, p) => sum + (p.foods?.length || 0), 0);
      return loaded < (lastPage.total || 0) ? loaded : undefined;
    },
    staleTime: 5 * 60 * 1000,
    initialPageParam: 0,
  });

  const recentFoods = recentFoodsData?.pages.flatMap(p => p.foods || []) || [];
  const recentFoodsTotal = recentFoodsData?.pages[0]?.total || 0;
  const recentFoodsOffset = recentFoods.length;
  const loadMoreRecentFoods = () => {
    if (hasMoreRecentFoods && !recentFoodsLoadingMore) fetchMoreRecentFoodsPage();
  };


  const filteredFoods = useMemo(() => foods.filter(food => {
    const normalize = (s) => s.toLowerCase().replace(/[-_\s]+/g, ' ').replace(/([a-z])([A-Z])/g, '$1 $2').trim();
    const queryNorm = normalize(searchQuery);
    const foodNorm = normalize(food.name);
    const queryWords = queryNorm.split(/\s+/).filter(Boolean);
    const matchesSearch = queryWords.length === 0 || queryWords.every(w => foodNorm.includes(w));
    const matchesCategory = selectedCategory === 'all' || food.category === selectedCategory;
    return matchesSearch && matchesCategory;
  }), [foods, searchQuery, selectedCategory]);

  // [BACKUP] Old debounced search (without autocomplete):
  // The old search used a 600ms debounce then called /foods/fatsecret/search directly.
  // Also had auto-translation via food_translations.py on the backend.
  // If you need to restore, revert this useEffect to use a single timer with 600ms delay.

  // Autocomplete disabled — diet search is local DB only (no FatSecret)
  useEffect(() => {
    setAutocompleteResults([]);
    setShowAutocomplete(false);
  }, [searchQuery, searchLanguage]);

  // Full search: community DB first, then FatSecret fallback
  const fatsecretSearchRef = useRef(0);
  useEffect(() => {
    if (!searchQuery || searchQuery.length < 2) {
      setCommunityResults([]);
      setFatsecretResults([]);
      setOffResults([]);
      setFatsecretLoading(false);
      setCommunityLoading(false);
      return;
    }
    const requestId = ++fatsecretSearchRef.current;
    setFatsecretLoading(true);
    setCommunityLoading(true);

    const timer = setTimeout(async () => {
      // 1) Search community foods (local DB)
      try {
        const cParams = new URLSearchParams({
          query: searchQuery,
          language: searchLanguage,
          max_results: '20',
        });
        const cRes = await client.get(`/community-foods/search?${cParams}`);
        if (requestId === fatsecretSearchRef.current) {
          setCommunityResults(cRes.data.results || []);
          setCommunityLoading(false);
        }
      } catch {
        if (requestId === fatsecretSearchRef.current) {
          setCommunityResults([]);
          setCommunityLoading(false);
        }
      }

      // FatSecret search disabled — diet uses local DB only
      if (requestId === fatsecretSearchRef.current) {
        setFatsecretResults([]);
        setOffResults([]);
        setFatsecretLoading(false);
        setShowAutocomplete(false);
      }
    }, 600);

    return () => clearTimeout(timer);
  }, [searchQuery, searchLanguage]);

  // ── Routable dialog sync effects (URL → local state) ──

  // 1. ?add=<mealType> → showAddFoodDialog + selectedMealType
  useEffect(() => {
    if (addFoodDialog.id) {
      setShowAddFoodDialog(true);
      setSelectedMealType(addFoodDialog.id);
    } else {
      setShowAddFoodDialog(false);
    }
  }, [addFoodDialog.id]);

  // 2. ?editEntry=log → showEditDialog (populates editEntries from current foodLog)
  useEffect(() => {
    if (editEntryDialog.id) {
      if (foodLog?.entries?.length) {
        setEditEntries(JSON.parse(JSON.stringify(foodLog.entries)));
      }
      setShowEditDialog(true);
    } else {
      setShowEditDialog(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editEntryDialog.id]);

  // 3. ?preview=<entryIndex> → showFoodPreview (opens preview for that log entry)
  useEffect(() => {
    if (previewDialog.id !== null && foodLog?.entries) {
      const idx = parseInt(previewDialog.id, 10);
      const entry = foodLog.entries[idx];
      if (entry) {
        openFoodPreview(entry, idx);
      } else {
        setShowFoodPreview(true);
      }
    } else if (previewDialog.id === null) {
      setShowFoodPreview(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previewDialog.id, foodLog]);

  // 4. ?customFood=<foodId> → showCustomFood + editingFoodId (edit-existing only)
  useEffect(() => {
    if (customFoodDialog.id) {
      setEditingFoodId(customFoodDialog.id);
      setShowCustomFood(true);
    } else {
      // Only clear editingFoodId when URL param is gone; leave showCustomFood as-is
      // (new-compose path manages showCustomFood locally without touching URL)
      setEditingFoodId(null);
    }
  }, [customFoodDialog.id]);

  // 5. ?panel=insights → showNutritionInsights
  useEffect(() => {
    setShowNutritionInsights(insightsDialog.id === 'insights');
  }, [insightsDialog.id]);

  // 6. ?editQuickMeal=<templateId> → open EditQuickMealModal for that meal template
  useEffect(() => {
    if (editQuickMealDialog.id) {
      const tpl = mealTemplates.find(m => m.id === editQuickMealDialog.id);
      if (tpl) {
        setEditingTemplate(tpl);
        setShowEditQuickMeal(true);
      } else if (mealTemplates.length > 0) {
        // Param points at a template that doesn't exist (deleted / not ours) — strip it
        editQuickMealDialog.close();
        setShowEditQuickMeal(false);
      }
    } else {
      setShowEditQuickMeal(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editQuickMealDialog.id, mealTemplates]);

  // Pick an autocomplete suggestion → set it as the search query to trigger full search
  const pickAutocomplete = useCallback((suggestion) => {
    setSearchQuery(suggestion);
    setShowAutocomplete(false);
  }, []);

  // Select a FatSecret food — fetch full detail with servings
  const selectFatsecretFood = async (fsFood) => {
    try {
      const res = await client.get(`/foods/fatsecret/${fsFood.fatsecret_id}`);
      const detail = res.data;
      
      // Save to our local DB
      const saveRes = await client.post('/foods/fatsecret/save', detail);
      const localFood = saveRes.data;
      
      // Find the best initial serving: default > matching search hint > first
      const servings = detail.servings || [];
      let initialServing = servings.find(s => s.is_default) || null;
      if (!initialServing && fsFood.serving_hint) {
        initialServing = servings.find(s => s.description === fsFood.serving_hint) || null;
      }
      if (!initialServing) {
        initialServing = servings[0] || null;
      }
      
      // Set as selected with serving options — apply initial serving macros
      setFatsecretDetail(detail);
      setSelectedServing(initialServing);
      setSelectedFood({
        ...localFood,
        servings: servings,
        // Override macros with the initial serving's values
        ...(initialServing ? {
          serving_size: initialServing.metric_amount || initialServing.amount,
          serving_unit: initialServing.metric_unit || initialServing.unit,
          serving_description: initialServing.description,
          calories: initialServing.calories,
          protein: initialServing.protein,
          carbs: initialServing.carbs,
          fat: initialServing.fat,
          saturated_fat: initialServing.saturated_fat,
          fiber: initialServing.fiber,
          sugar: initialServing.sugar,
          sodium: initialServing.sodium,
          potassium: initialServing.potassium,
        } : {}),
      });
      setQuantity(1);
    } catch {
      toast.error(t('diet.failedLoadFoodDetails'));
    }
  };

  // Select an Open Food Facts food — data already complete, skip FatSecret detail fetch
  const selectOpenFoodFactsFood = async (offFood) => {
    try {
      const saveRes = await client.post('/foods/fatsecret/save', offFood);
      const localFood = saveRes.data;
      const servings = offFood.servings || [];
      const initialServing = servings[0] || null;
      setFatsecretDetail(null);
      setSelectedServing(initialServing);
      setSelectedFood({
        ...localFood,
        servings,
        ...(initialServing ? {
          serving_size: initialServing.metric_amount,
          serving_unit: initialServing.metric_unit,
          serving_description: initialServing.description,
          calories: initialServing.calories,
          protein: initialServing.protein,
          carbs: initialServing.carbs,
          fat: initialServing.fat,
          saturated_fat: initialServing.saturated_fat,
          fiber: initialServing.fiber,
          sugar: initialServing.sugar,
          sodium: initialServing.sodium,
          potassium: initialServing.potassium,
        } : {}),
      });
      setQuantity(1);
    } catch {
      toast.error(t('diet.failedLoadFoodDetails'));
    }
  };

  // Update macros when serving changes
  const handleServingChange = (servingId) => {
    const serving = fatsecretDetail?.servings?.find(s => s.serving_id === servingId)
      || selectedFood?.servings?.find(s => s.serving_id === servingId);
    if (serving) {
      setSelectedServing(serving);
      setSelectedFood(prev => prev ? {
        ...prev,
        serving_size: serving.metric_amount || serving.amount,
        serving_unit: serving.metric_unit || serving.unit,
        serving_description: serving.description,
        calories: serving.calories,
        protein: serving.protein,
        carbs: serving.carbs,
        fat: serving.fat,
        saturated_fat: serving.saturated_fat,
        fiber: serving.fiber,
        sugar: serving.sugar,
        sodium: serving.sodium,
        potassium: serving.potassium,
      } : prev);
      setQuantity(1);
    }
  };

  const addFoodToQueue = () => {
    if (!selectedFood) return;
    const servingSize = selectedServing?.metric_amount || selectedFood.serving_size || 100;
    const servingUnit = selectedServing?.metric_unit || selectedFood.serving_unit || 'g';
    const servingDesc = selectedServing?.description || '';
    const display = buildServingDisplay(quantity, servingSize, servingUnit, servingDesc);
    const entry = {
      food_id: selectedFood.id,
      food_name: selectedFood.name,
      quantity: display.quantity,
      unit: display.unit,
      serving_size: servingSize,
      serving_unit: servingUnit,
      serving_description: servingDesc,
      servings: quantity,
      calories: selectedFood.calories * quantity,
      protein: selectedFood.protein * quantity,
      carbs: selectedFood.carbs * quantity,
      fat: selectedFood.fat * quantity,
      saturated_fat: (selectedFood.saturated_fat || 0) * quantity,
      fiber: (selectedFood.fiber || 0) * quantity,
      sugar: (selectedFood.sugar || 0) * quantity,
      sodium: (selectedFood.sodium || 0) * quantity,
      cholesterol: (selectedFood.cholesterol || 0) * quantity,
      potassium: (selectedFood.potassium || 0) * quantity,
      is_coach_recommended: false,
      meal_type: selectedMealType,
    };
    setPendingFoods(prev => [...prev, entry]);
    toast.success(t('diet.addedToQueue', { name: selectedFood.name }));
    setSelectedFood(null);
    setSelectedServing(null);
    setFatsecretDetail(null);
    setQuantity(1);
    setSearchQuery('');
  };

  const updateDietSettingsMutation = useMutation({
    mutationFn: (settings) => client.put('/diet/settings', settings),
    onSuccess: () => {
      refreshUser();
      toast.success(t('diet.settingsUpdated'));
    },
    onError: (err) => {
      toast.error(err.response?.data?.detail || t('diet.settingsError'));
    },
  });

  const setWaterMutation = useMutation({
    mutationFn: ({ date, water_ml }) => client.put(`/food-logs/${date}/water`, { water_ml }),
    onMutate: async ({ date, water_ml }) => {
      await queryClient.cancelQueries({ queryKey: ['foodLog', date, onBehalfOf] });
      const prev = queryClient.getQueryData(['foodLog', date, onBehalfOf]);
      queryClient.setQueryData(['foodLog', date, onBehalfOf], (old) => old ? { ...old, water_ml } : old);
      return { prev };
    },
    onError: (_err, vars, ctx) => {
      if (ctx?.prev) queryClient.setQueryData(['foodLog', vars.date, onBehalfOf], ctx.prev);
      toast.error(t('common.error'));
    },
    onSettled: (_data, _err, vars) => {
      queryClient.invalidateQueries({ queryKey: ['foodLog', vars.date, onBehalfOf] });
      queryClient.invalidateQueries({ queryKey: ['dietPlanProgress', onBehalfOf] });
    },
  });

  const [waterTargetDraft, setWaterTargetDraft] = useState('');
  useEffect(() => {
    if (showDietSettingsSheet) setWaterTargetDraft(String(user?.water_target_ml ?? 2000));
  }, [showDietSettingsSheet, user?.water_target_ml]);

  const setWaterTargetMutation = useMutation({
    mutationFn: (water_target_ml) => client.put('/users/me/water-target', { water_target_ml }),
    onSuccess: () => {
      refreshUser();
      toast.success(t('water.savedTarget'));
    },
  });

  const saveAllPendingFoodsMutation = useMutation({
    mutationFn: async (newEntries) => {
      const foodLogUrl = onBehalfOf ? `/food-logs?on_behalf_of=${onBehalfOf}` : `/food-logs`;
      await client.post(foodLogUrl, {
        date: formatDateForAPI(selectedDate),
        entries: newEntries,
      });
    },
    onSuccess: () => {
      toast.success(pendingFoods.length > 1 ? t('diet.foodsLoggedPlural', { count: pendingFoods.length }) : t('diet.foodsLogged', { count: pendingFoods.length }));
      addFoodDialog.close();
      setSelectedFood(null);
      setSelectedServing(null);
      setFatsecretDetail(null);
      setFatsecretResults([]);
      setOffResults([]);
      setQuantity(1);
      setSearchQuery('');
      setPendingFoods([]);
      queryClient.invalidateQueries({ queryKey: ['foodLog', dateStr, onBehalfOf] });
      queryClient.invalidateQueries({ queryKey: ['dailyStreak'] });
      queryClient.invalidateQueries({ queryKey: ['weeklySummary'] });
      queryClient.invalidateQueries({ queryKey: ['recentFoods'] });
    },
    onError: () => {
      toast.error(t('diet.failedSaveFoods'));
    },
  });

  const saveAllPendingFoods = () => {
    if (pendingFoods.length === 0) return;
    const newEntries = [...(foodLog?.entries || []), ...pendingFoods];
    saveAllPendingFoodsMutation.mutate(newEntries);
  };

  const moveMealMutation = useMutation({
    mutationFn: async ({ source, target, action }) => {
      const currentEntries = foodLog?.entries || [];
      const sourceEntries = currentEntries.filter(e => e.meal_type === source);
      const movedEntries = sourceEntries.map(e => ({ ...e, meal_type: target }));
      let newEntries;
      if (action === 'move') {
        newEntries = [
          ...currentEntries.filter(e => e.meal_type !== source),
          ...movedEntries,
        ];
      } else {
        newEntries = [...currentEntries, ...movedEntries];
      }
      const url = onBehalfOf ? `/food-logs?on_behalf_of=${onBehalfOf}` : `/food-logs`;
      await client.post(url, {
        date: formatDateForAPI(selectedDate),
        entries: newEntries,
      });
      return { source, target, action, count: sourceEntries.length };
    },
    onSuccess: ({ target, action, count }) => {
      haptics.success();
      const targetLabel = MEAL_TYPES.find(m => m.value === target)?.label || target;
      const verb = action === 'move' ? t('diet.movedToast') : t('diet.copiedToast');
      toast.success(`${count} ${count === 1 ? t('diet.itemSingular') : t('diet.itemPlural')} ${verb} ${targetLabel}`);
      setShowMoveMealDialog(false);
      setMoveMealSource(null);
      setMoveMealTarget(null);
      setMoveMealAction('move');
      queryClient.invalidateQueries({ queryKey: ['foodLog', dateStr, onBehalfOf] });
      queryClient.invalidateQueries({ queryKey: ['dailyStreak'] });
      queryClient.invalidateQueries({ queryKey: ['weeklySummary'] });
    },
    onError: () => toast.error(t('diet.moveMealError')),
  });

  const removePendingFood = (index) => {
    setPendingFoods(prev => prev.filter((_, i) => i !== index));
  };

  const addFoodToLogMutation = useMutation({
    mutationFn: async ({ foodName, mealType, newEntries }) => {
      const foodLogUrl = onBehalfOf ? `/food-logs?on_behalf_of=${onBehalfOf}` : `/food-logs`;
      await client.post(foodLogUrl, {
        date: formatDateForAPI(selectedDate),
        entries: newEntries,
      });
      return { foodName, mealType };
    },
    onSuccess: ({ foodName, mealType }) => {
      haptics.success();
      toast.success(t('diet.addedToMeal', { name: foodName, meal: mealType }));
      emit('diet.food-added', { added: true });
      addFoodDialog.close();
      setSelectedFood(null);
      setSelectedServing(null);
      setFatsecretDetail(null);
      setFatsecretResults([]);
      setOffResults([]);
      setQuantity(1);
      setSearchQuery('');
      queryClient.invalidateQueries({ queryKey: ['foodLog', dateStr, onBehalfOf] });
      queryClient.invalidateQueries({ queryKey: ['dailyStreak'] });
      queryClient.invalidateQueries({ queryKey: ['weeklySummary'] });
      queryClient.invalidateQueries({ queryKey: ['recentFoods'] });
    },
    onError: () => {
      toast.error(t('diet.failedAddFood'));
    },
  });

  // Legacy single-add for photo scan results
  const addFoodToLog = () => {
    if (!selectedFood) return;

    const servingSize = selectedServing?.metric_amount || selectedFood.serving_size || 100;
    const servingUnit = selectedServing?.metric_unit || selectedFood.serving_unit || 'g';
    const servingDesc = selectedServing?.description || '';
    const display = buildServingDisplay(quantity, servingSize, servingUnit, servingDesc);

    const entry = {
      food_id: selectedFood.id,
      food_name: selectedFood.name,
      quantity: display.quantity,
      unit: display.unit,
      serving_size: servingSize,
      serving_unit: servingUnit,
      serving_description: servingDesc,
      servings: quantity,
      calories: selectedFood.calories * quantity,
      protein: selectedFood.protein * quantity,
      carbs: selectedFood.carbs * quantity,
      fat: selectedFood.fat * quantity,
      saturated_fat: (selectedFood.saturated_fat || 0) * quantity,
      fiber: (selectedFood.fiber || 0) * quantity,
      sugar: (selectedFood.sugar || 0) * quantity,
      sodium: (selectedFood.sodium || 0) * quantity,
      cholesterol: (selectedFood.cholesterol || 0) * quantity,
      potassium: (selectedFood.potassium || 0) * quantity,
      is_coach_recommended: false,
      meal_type: selectedMealType,
    };

    const newEntries = [...(foodLog?.entries || []), entry];
    addFoodToLogMutation.mutate({ foodName: selectedFood.name, mealType: selectedMealType, newEntries });
  };

  // Accepts either a bare entries array (legacy callers) or
  // { entries, silent } — silent suppresses the success toast so a caller can
  // show its own (e.g. the Undo toast) instead of stacking two.
  const removeFoodFromLogMutation = useMutation({
    mutationFn: async (payload) => {
      const newEntries = Array.isArray(payload) ? payload : payload.entries;
      const foodLogUrl = onBehalfOf ? `/food-logs?on_behalf_of=${onBehalfOf}` : `/food-logs`;
      await client.post(foodLogUrl, {
        date: formatDateForAPI(selectedDate),
        entries: newEntries,
      });
    },
    onSuccess: (_data, payload) => {
      if (Array.isArray(payload) || !payload.silent) toast.success(t('diet.foodRemoved'));
      queryClient.invalidateQueries({ queryKey: ['foodLog', dateStr, onBehalfOf] });
      queryClient.invalidateQueries({ queryKey: ['dailyStreak'] });
      queryClient.invalidateQueries({ queryKey: ['weeklySummary'] });
    },
    onError: () => {
      toast.error(t('diet.failedRemoveFood'));
    },
  });

  // Confirm first, then remove with a real Undo: the endpoint takes the whole
  // entries array, so undo just re-sends the list as it was before.
  const removeFoodFromLog = async (index) => {
    const previousEntries = foodLog?.entries || [];
    const removedName = previousEntries[index]?.food_name;

    const ok = await confirm({
      title: t('confirm.deleteTitle', 'Delete this?'),
      description: t('confirm.deleteBody'),
      confirmLabel: t('confirm.deleteConfirm', 'Delete'),
      destructive: true,
    });
    if (!ok) return;

    const newEntries = previousEntries.filter((_, i) => i !== index);
    removeFoodFromLogMutation.mutate({ entries: newEntries, silent: true });

    toastUndo(
      t('undo.foodRemoved', 'Food removed'),
      () => removeFoodFromLogMutation.mutate({ entries: previousEntries, silent: true }),
      { description: removedName }
    );
  };

  // Long-press action handlers — wired into FoodEntryActionSheet
  const handleEditEntry = useCallback((entry) => {
    if (foodLog?.completed) { setShowUnlockConfirm(true); return; }
    const idx = foodLog?.entries?.findIndex(e => e === entry);
    if (idx >= 0) {
      setInlineEditIdx(idx);
      setInlineEditQty(entry.servings || 1);
    }
  }, [foodLog]);

  const handleDuplicateEntry = useCallback((entry, targetMeal) => {
    if (foodLog?.completed) { setShowUnlockConfirm(true); return; }
    const newEntry = { ...entry, meal_type: targetMeal };
    const newEntries = [...(foodLog?.entries || []), newEntry];
    // Duplicating reuses the same "replace the whole log" endpoint; silence its
    // removal toast, which would otherwise say "Food removed" after an ADD.
    removeFoodFromLogMutation.mutate({ entries: newEntries, silent: true });
  }, [foodLog, removeFoodFromLogMutation]);

  const handleDeleteEntry = useCallback((entry) => {
    if (foodLog?.completed) { setShowUnlockConfirm(true); return; }
    const idx = foodLog?.entries?.findIndex(e => e === entry);
    if (idx >= 0) removeFoodFromLog(idx);
  }, [foodLog, removeFoodFromLog]);

  const openFoodPreview = async (entry, entryIndex) => {
    const preview = {
      name: entry.food_name,
      calories: Math.round(entry.calories),
      protein: Math.round(entry.protein * 10) / 10,
      carbs: Math.round(entry.carbs * 10) / 10,
      fat: Math.round(entry.fat * 10) / 10,
      serving_size: entry.serving_size || entry.quantity,
      serving_unit: entry.serving_unit || entry.unit || 'g',
      serving_description: entry.serving_description || `${entry.quantity} ${entry.unit || 'g'}`,
      quantity: entry.quantity,
      servings: entry.servings || 1,
      _entry_index: entryIndex,
    };
    setPreviewFood(preview);
    setShowFoodPreview(true);
    previewDialog.open(String(entryIndex));
    setPreviewLoading(true);

    // Try to fetch full food details from DB (by id, then by name fallback)
    try {
      const foodId = entry.food_id || 'unknown';
      const nameParam = encodeURIComponent(entry.food_name);
      const res = await client.get(`/foods/${foodId}?name=${nameParam}`);
      if (res.data) {
        const servings = entry.servings || 1;
        setPreviewFood({
          ...res.data,
          _logged_servings: servings,
          _logged_calories: Math.round(entry.calories),
          _logged_protein: Math.round(entry.protein * 10) / 10,
          _logged_carbs: Math.round(entry.carbs * 10) / 10,
          _logged_fat: Math.round(entry.fat * 10) / 10,
          _logged_quantity: entry.quantity,
          _logged_unit: entry.unit,
          _entry_index: entryIndex,
        });
      }
    } catch {
      // Keep showing entry data if fetch fails
    } finally {
      setPreviewLoading(false);
    }
  };

  // Enrich food log entries with micronutrient data from DB.
  // Single bulk POST /foods/batch-micros call replaces the prior N parallel HTTP requests
  // (one per unique food name). Successful lookups are cached on a ref so subsequent
  // Insights panel opens for the same foods skip the network entirely.
  const enrichEntriesWithMicros = async (entries) => {
    const hasMicros = entries.some(e => e.fiber || e.sugar || e.sodium || e.saturated_fat || e.cholesterol);
    if (hasMicros) {
      setEnrichedEntries(entries);
      return;
    }
    const uniqueNames = [...new Set(entries.map(e => e.food_name).filter(Boolean))];
    const cacheMisses = uniqueNames.filter(n => !(n in microsCacheRef.current));
    if (cacheMisses.length > 0) {
      try {
        const res = await client.post('/foods/batch-micros', { names: cacheMisses });
        Object.assign(microsCacheRef.current, res.data || {});
        // Mark misses as resolved (null) so we don't retry within this session.
        cacheMisses.forEach(n => { if (!(n in microsCacheRef.current)) microsCacheRef.current[n] = null; });
      } catch { /* leave cache as-is; entries stay un-enriched on this attempt */ }
    }
    const enriched = entries.map(e => {
      const dbFood = microsCacheRef.current[e.food_name];
      if (!dbFood) return e;
      const servings = e.servings || 1;
      return {
        ...e,
        saturated_fat: e.saturated_fat || (dbFood.saturated_fat || 0) * servings,
        fiber: e.fiber || (dbFood.fiber || 0) * servings,
        sugar: e.sugar || (dbFood.sugar || 0) * servings,
        sodium: e.sodium || (dbFood.sodium || 0) * servings,
        cholesterol: e.cholesterol || (dbFood.cholesterol || 0) * servings,
        potassium: e.potassium || (dbFood.potassium || 0) * servings,
      };
    });
    setEnrichedEntries(enriched);
  };

  // Barcode Scanner Handlers — routed by searchLanguage flag
  const handleBarcodeScanned = async (barcode) => {
    if (!isValidBarcode(barcode)) {
      toast.error(t('diet.scannerError', 'Invalid barcode format'));
      return;
    }

    setScanningProduct(true);
    setShowBarcodeScanner(false);
    toast.loading(t('diet.scanningProduct', 'Scanning product...'), { id: 'scanning' });

    try {
      {
        // FatSecret barcode lookup — language/region based on flag
        const lang = searchLanguage === 'pt' ? 'pt' : 'en';
        const region = searchLanguage === 'pt' ? 'PT' : 'US';
        const res = await client.post('/foods/fatsecret/barcode', {
          barcode,
          language: lang,
          region,
        });

        if (res.data.found && res.data.food) {
          const food = res.data.food;
          toast.success(t('diet.productFound', 'Product found!'), { id: 'scanning' });
          setScannedProduct(food);

          await client.post('/foods/custom', {
            name: food.name,
            brand: food.brand || '',
            barcode,
            category: 'Scanned Foods',
            serving_size: parseFloat(food.serving_size) || 100,
            serving_unit: food.serving_unit || 'g',
            calories: food.calories || 0,
            protein: food.protein || 0,
            carbs: food.carbs || 0,
            fat: food.fat || 0,
            fiber: food.fiber || 0,
            sugar: food.sugar || 0,
            sodium: food.sodium || 0,
          });

          setSelectedFood({
            id: food.food_id || `scanned_${barcode}`,
            name: food.name,
            brand: food.brand || '',
            barcode,
            source: 'fatsecret',
            calories: food.calories || 0,
            protein: food.protein || 0,
            carbs: food.carbs || 0,
            fat: food.fat || 0,
            saturated_fat: food.saturated_fat || 0,
            fiber: food.fiber || 0,
            sugar: food.sugar || 0,
            sodium: food.sodium || 0,
            cholesterol: food.cholesterol || 0,
            potassium: food.potassium || 0,
            servings: food.servings || [],
          });
          if (food.servings?.length) {
            setSelectedServing(food.servings[0]);
            setFatsecretDetail(food);
          }
          setQuantity(1);
          addFoodDialog.open(selectedMealType);
        } else {
          toast.error(t('diet.productNotFound', 'Product not found'), { id: 'scanning' });
          setCustomFood({
            ...customFood,
            name: '',
            barcode: barcode
          });
          setShowCustomFood(true);
        }
      }
    } catch (err) {
      console.error('Barcode scan error:', err);
      toast.error(t('diet.scanError', 'Failed to scan product'), { id: 'scanning' });
    } finally {
      setScanningProduct(false);
    }
  };

  const updateFoodMutation = useMutation({
    mutationFn: async ({ id, payload }) => {
      const { data } = await client.put(`/foods/${id}`, payload);
      return data;
    },
    onSuccess: () => {
      toast.success(t('diet.foodUpdated'));
      setShowCustomFood(false);
      setShowCustomFoodMore(false);
      setEditingFoodId(null);
      customFoodDialog.close();
      setCustomFood(emptyCustomFood);
      queryClient.invalidateQueries({ queryKey: ['foods'] });
    },
    onError: (err) => {
      toast.error(err?.response?.data?.detail || t('diet.failedUpdateFood'));
    },
  });

  const deleteFoodMutation = useMutation({
    mutationFn: async (id) => {
      await client.delete(`/foods/${id}`);
    },
    onSuccess: () => {
      toast.success(t('diet.foodDeleted'));
      queryClient.invalidateQueries({ queryKey: ['foods'] });
      queryClient.invalidateQueries({ queryKey: ['foodCategories'] });
    },
    onError: (err) => {
      toast.error(err?.response?.data?.detail || t('diet.failedDeleteFood'));
    },
  });

  const handleEditMyFood = (food) => {
    setEditingFoodId(food.id);
    setCustomFood({
      // Basic
      name: food.name || '',
      calories: String(food.calories ?? ''),
      protein: String(food.protein ?? ''),
      carbs: String(food.carbs ?? ''),
      fat: String(food.fat ?? ''),
      serving_size: String(food.serving_size ?? '100'),
      serving_unit: food.serving_unit || 'g',
      barcode: food.barcode || '',
      // More details (optional — left empty when the food doesn't have them)
      name_en: food.name_en || '',
      name_pt: food.name_pt || '',
      category: food.category && food.category !== 'Your Foods' ? food.category : '',
      subcategory: food.subcategory || '',
      brand: food.brand || '',
      cooking_method: food.cooking_method || '',
      fiber: food.fiber != null ? String(food.fiber) : '',
      sugar: food.sugar != null ? String(food.sugar) : '',
      sodium: food.sodium != null ? String(food.sodium) : '',
      saturated_fat: food.saturated_fat != null ? String(food.saturated_fat) : '',
      cholesterol: food.cholesterol != null ? String(food.cholesterol) : '',
      potassium: food.potassium != null ? String(food.potassium) : '',
      allergens: Array.isArray(food.allergens) ? food.allergens : [],
      preferences: Array.isArray(food.preferences) ? food.preferences : [],
    });
    setShowCustomFood(true);
    customFoodDialog.open(food.id);
    // Auto-open "More" if any extended field is populated
    const hasMore = food.name_en || food.name_pt || food.brand || food.subcategory || food.cooking_method ||
      food.fiber != null || food.sugar != null || food.sodium != null || food.saturated_fat != null ||
      food.cholesterol != null || food.potassium != null ||
      (Array.isArray(food.allergens) && food.allergens.length > 0) ||
      (Array.isArray(food.preferences) && food.preferences.length > 0);
    setShowCustomFoodMore(!!hasMore);
  };

  const handleDeleteMyFood = async (food) => {
    const ok = await confirm({
      title: t('diet.confirmDeleteFood', { name: food.name }),
      confirmLabel: t('common.delete'),
      cancelLabel: t('common.cancel'),
      destructive: true,
    });
    if (!ok) return;
    deleteFoodMutation.mutate(food.id);
  };

  // Build a rich community-submission payload from the current customFood state.
  // The user fills in the basic section; if they expand "More" and add richer
  // fields (category, brand, detailed nutrition, allergens, etc.) we forward them
  // all to /community-foods/submit so that when an admin approves the doc, it lands
  // in db.foods with the same shape as seeded catalogue entries.
  const buildCommunityPayload = () => {
    const nameFallback = customFood.name.trim();
    const name_en = customFood.name_en.trim() || (searchLanguage === 'en' ? nameFallback : '');
    const name_pt = customFood.name_pt.trim() || (searchLanguage !== 'en' ? nameFallback : '');
    const numOrNull = (v) => {
      if (v === '' || v == null) return null;
      const n = parseFloat(v);
      return Number.isFinite(n) ? n : null;
    };
    const payload = {
      name_en,
      name_pt,
      category: customFood.category || 'Other',
      subcategory: customFood.subcategory.trim() || null,
      brand: customFood.brand.trim() || null,
      barcode: (customFood.barcode || '').trim() || null,
      cooking_method: customFood.cooking_method || null,
      calories: parseFloat(customFood.calories) || 0,
      protein: parseFloat(customFood.protein) || 0,
      carbs: parseFloat(customFood.carbs) || 0,
      fat: parseFloat(customFood.fat) || 0,
      fiber: numOrNull(customFood.fiber),
      sugar: numOrNull(customFood.sugar),
      sodium: numOrNull(customFood.sodium),
      saturated_fat: numOrNull(customFood.saturated_fat),
      cholesterol: numOrNull(customFood.cholesterol),
      potassium: numOrNull(customFood.potassium),
      allergens: customFood.allergens,
      preferences: customFood.preferences,
      serving_size: parseFloat(customFood.serving_size) || 100,
      serving_unit: customFood.serving_unit || 'g',
      serving_description: `${customFood.serving_size || 100} ${customFood.serving_unit || 'g'}`,
    };
    // Strip keys with null/empty-array so the approved catalogue doc matches the
    // lean shape of seeded foods (which only carry fields that were actually filled).
    Object.keys(payload).forEach((k) => {
      const v = payload[k];
      if (v === null) delete payload[k];
      if (Array.isArray(v) && v.length === 0) delete payload[k];
    });
    return payload;
  };

  const createCustomFoodMutation = useMutation({
    mutationFn: async (payload) => {
      // 1. Save as personal food in the user's own foods collection.
      //    The payload here already carries every "More" field the user filled
      //    in, so their personal copy is never dumbed-down.
      await client.post('/foods', payload);
      // 2. Also submit to community_foods for admin review.
      //    Awaited + caught here so silent failures surface in the console.
      try {
        await client.post('/community-foods/submit', buildCommunityPayload());
      } catch (err) {
        console.error('Community submit failed:', err?.response?.data || err?.message || err);
      }
    },
    onSuccess: () => {
      toast.success(t('diet.savedToYourFoods', { name: customFood.name }));
      setShowCustomFood(false);
      setShowCustomFoodMore(false);
      setCustomFood(emptyCustomFood);
      queryClient.invalidateQueries({ queryKey: ['foods'] });
      queryClient.invalidateQueries({ queryKey: ['foodCategories'] });
    },
    onError: () => {
      toast.error(t('diet.failedCreateCustomFood'));
    },
  });

  const handleCreateCustomFood = () => {
    const numOrNull = (v) => {
      if (v === '' || v == null) return null;
      const n = parseFloat(v);
      return Number.isFinite(n) ? n : null;
    };
    // Personal copy — always lands in the user's "Your Foods" category on edit/create.
    // Forward every filled-in "More" field so the personal copy matches what's
    // also being sent for community review.
    const payload = {
      name: customFood.name,
      category: 'Your Foods',
      serving_size: parseFloat(customFood.serving_size) || 100,
      serving_unit: customFood.serving_unit,
      calories: parseFloat(customFood.calories) || 0,
      protein: parseFloat(customFood.protein) || 0,
      carbs: parseFloat(customFood.carbs) || 0,
      fat: parseFloat(customFood.fat) || 0,
      is_custom: true,
    };
    // Optional extended fields (only add when filled — keep the payload lean)
    if (customFood.barcode && customFood.barcode.trim()) payload.barcode = customFood.barcode.trim();
    if (customFood.name_en.trim()) payload.name_en = customFood.name_en.trim();
    if (customFood.name_pt.trim()) payload.name_pt = customFood.name_pt.trim();
    if (customFood.subcategory.trim()) payload.subcategory = customFood.subcategory.trim();
    if (customFood.brand.trim()) payload.brand = customFood.brand.trim();
    if (customFood.cooking_method) payload.cooking_method = customFood.cooking_method;
    const fiber = numOrNull(customFood.fiber);
    const sugar = numOrNull(customFood.sugar);
    const sodium = numOrNull(customFood.sodium);
    const satFat = numOrNull(customFood.saturated_fat);
    const cholesterol = numOrNull(customFood.cholesterol);
    const potassium = numOrNull(customFood.potassium);
    if (fiber != null) payload.fiber = fiber;
    if (sugar != null) payload.sugar = sugar;
    if (sodium != null) payload.sodium = sodium;
    if (satFat != null) payload.saturated_fat = satFat;
    if (cholesterol != null) payload.cholesterol = cholesterol;
    if (potassium != null) payload.potassium = potassium;
    if (customFood.allergens.length) payload.allergens = customFood.allergens;
    if (customFood.preferences.length) payload.preferences = customFood.preferences;

    if (editingFoodId) {
      updateFoodMutation.mutate({ id: editingFoodId, payload });
    } else {
      createCustomFoodMutation.mutate(payload);
    }
  };

  // ==================== MEAL TEMPLATE HANDLERS ====================

  const saveMealTemplateMutation = useMutation({
    mutationFn: async ({ name, meal_type, foods }) => {
      await client.post('/meal-templates', { name, meal_type, foods });
    },
    onSuccess: () => {
      toast.success(t('diet.savedAsQuickMeal', { name: templateName }));
      setShowSaveTemplateDialog(false);
      setTemplateName('');
      queryClient.invalidateQueries({ queryKey: ['mealTemplates'] });
    },
    onError: () => {
      toast.error(t('diet.failedSaveMealTemplate'));
    },
  });

  const handleSaveMealTemplate = () => {
    if (!templateName.trim()) return;

    const mealEntries = foodLog?.entries?.filter(e => (e.meal_type || '').toLowerCase() === (templateMealType || '').toLowerCase()) || [];
    if (mealEntries.length === 0) {
      toast.error(t('diet.noFoodsInMeal'));
      return;
    }

    const templateFoods = mealEntries.map(e => ({
      food_id: e.food_id,
      food_name: e.food_name,
      quantity: e.quantity,
      unit: e.unit,
      calories: e.calories / e.quantity,
      protein: e.protein / e.quantity,
      carbs: e.carbs / e.quantity,
      fat: e.fat / e.quantity,
    }));

    saveMealTemplateMutation.mutate({ name: templateName, meal_type: templateMealType, foods: templateFoods });
  };

  const handleAddSuggestionMeal = (suggestionMeal) => {
    const newFoods = suggestionMeal.foods.map(food => {
      const servings = food.servings || 1;
      const servingSize = food.serving_size;   // may be null for old suggestions
      const servingUnit = food.serving_unit;
      const servingDesc = food.serving_description;
      const display = buildServingDisplay(servings, servingSize, servingUnit, servingDesc);
      return {
        food_name: food.food_name,
        calories: (food.calories || 0) * servings,
        protein: (food.protein || 0) * servings,
        carbs: (food.carbs || 0) * servings,
        fat: (food.fat || 0) * servings,
        quantity: display.quantity,
        unit: display.unit,
        serving_size: servingSize,
        serving_unit: servingUnit,
        serving_description: servingDesc,
        servings,
        meal_type: (suggestionMeal.meal_name || suggestionMeal.meal_type || '').toLowerCase(),
        is_coach_recommended: true,
      };
    });
    const existingEntries = foodLog?.entries || [];
    const newEntries = [...existingEntries, ...newFoods];
    addFoodToLogMutation.mutate({
      foodName: t('diet.coachsPlan'),
      mealType: (suggestionMeal.meal_name || suggestionMeal.meal_type || '').toLowerCase(),
      newEntries,
    });
  };

  const handleQuickAdd = ({ entry, entries, saveAsTemplate }) => {
    const mt = (selectedMealType || 'lunch').toLowerCase();
    const incoming = Array.isArray(entries) && entries.length > 0
      ? entries.map(e => ({ ...e, meal_type: mt }))
      : [{ ...entry, meal_type: mt }];
    const existingEntries = foodLog?.entries || [];
    const newEntries = [...existingEntries, ...incoming];
    const toastName = incoming.length === 1
      ? incoming[0].food_name
      : t('diet.foodsDetectedPlural', { count: incoming.length });

    addFoodToLogMutation.mutate(
      { foodName: toastName, mealType: mt, newEntries },
      {
        onSuccess: () => {
          // Template save is only supported for single-item quick-adds right now
          if (saveAsTemplate && incoming.length === 1) {
            const one = incoming[0];
            saveMealTemplateMutation.mutate({
              name: one.food_name,
              meal_type: mt,
              foods: [
                {
                  food_id: null,
                  food_name: one.food_name,
                  quantity: 1,
                  unit: 'serving',
                  calories: one.calories,
                  protein: one.protein,
                  carbs: one.carbs,
                  fat: one.fat,
                },
              ],
            });
          }
        },
      }
    );
  };

  const logTemplateMutation = useMutation({
    mutationFn: async ({ templateId, mt }) => {
      await client.post(`/meal-templates/${templateId}/log?date=${dateStr}&meal_type=${mt}`);
      return { mt };
    },
    onSuccess: ({ mt }, variables) => {
      toast.success(t('diet.templateLoggedTo', { name: variables.templateName, meal: MEAL_TYPES.find(m => m.value === mt)?.label || mt }));
      addFoodDialog.close();
      setQuickMealTarget(null);
      queryClient.invalidateQueries({ queryKey: ['foodLog', dateStr, onBehalfOf] });
      queryClient.invalidateQueries({ queryKey: ['weeklySummary'] });
    },
    onError: () => {
      toast.error(t('diet.failedLogMeal'));
    },
  });

  const handleLogTemplate = (template, overrideMealType) => {
    const mt = overrideMealType || template.meal_type;
    logTemplateMutation.mutate({ templateId: template.id, mt, templateName: template.name });
  };

  const deleteTemplateMutation = useMutation({
    mutationFn: async (templateId) => {
      await client.delete(`/meal-templates/${templateId}`);
    },
    onSuccess: () => {
      toast.success(t('diet.quickMealDeleted'));
      queryClient.invalidateQueries({ queryKey: ['mealTemplates'] });
    },
    onError: () => {
      toast.error(t('diet.failedDelete'));
    },
  });

  const handleDeleteTemplate = (templateId) => {
    deleteTemplateMutation.mutate(templateId);
  };

  // ==================== EDIT / DELETE DIET LOG ====================

  const openEditLog = () => {
    if (!foodLog?.entries?.length) {
      toast.error(t('diet.noEntriesToEdit'));
      return;
    }
    setEditEntries(JSON.parse(JSON.stringify(foodLog.entries)));
    editEntryDialog.open('log');
  };

  const updateEditEntry = (index, field, value) => {
    setEditEntries(prev => {
      const copy = [...prev];
      copy[index] = { ...copy[index], [field]: value };
      return copy;
    });
  };

  const removeEditEntry = (index) => {
    setEditEntries(prev => prev.filter((_, i) => i !== index));
  };

  const inlineEditSaveMutation = useMutation({
    mutationFn: async (updatedEntries) => {
      const foodLogUrl = onBehalfOf ? `/food-logs?on_behalf_of=${onBehalfOf}` : `/food-logs`;
      await client.post(foodLogUrl, {
        date: formatDateForAPI(selectedDate),
        entries: updatedEntries,
      });
    },
    onSuccess: () => {
      toast.success(t('diet.entryUpdated'));
      setInlineEditIdx(null);
      queryClient.invalidateQueries({ queryKey: ['foodLog', dateStr, onBehalfOf] });
      queryClient.invalidateQueries({ queryKey: ['weeklySummary'] });
    },
    onError: () => {
      toast.error(t('diet.failedUpdateEntry'));
    },
  });

  const handleInlineEditSave = (entryIndex, newServings) => {
    if (!foodLog?.entries?.[entryIndex]) return;
    const entry = foodLog.entries[entryIndex];
    const baseServings = entry.servings || entry.quantity || 1;
    const ratio = newServings / baseServings;
    const updatedEntries = foodLog.entries.map((e, i) => {
      if (i !== entryIndex) return e;
      const display = buildServingDisplay(newServings, e.serving_size, e.serving_unit, e.serving_description);
      return {
        ...e,
        servings: newServings,
        quantity: display.quantity,
        unit: display.unit,
        calories: e.calories * ratio,
        protein: e.protein * ratio,
        carbs: e.carbs * ratio,
        fat: e.fat * ratio,
      };
    });
    inlineEditSaveMutation.mutate(updatedEntries);
  };

  const saveEditLogMutation = useMutation({
    mutationFn: async (entries) => {
      const foodLogUrl = onBehalfOf ? `/food-logs?on_behalf_of=${onBehalfOf}` : `/food-logs`;
      await client.post(foodLogUrl, {
        date: formatDateForAPI(selectedDate),
        entries,
      });
    },
    onSuccess: () => {
      toast.success(t('diet.dietLogUpdated'));
      editEntryDialog.close();
      queryClient.invalidateQueries({ queryKey: ['foodLog', dateStr, onBehalfOf] });
      queryClient.invalidateQueries({ queryKey: ['weeklySummary'] });
    },
    onError: () => {
      toast.error(t('diet.failedUpdateDietLog'));
    },
  });

  const handleSaveEditLog = () => {
    saveEditLogMutation.mutate(editEntries);
  };

  const deleteLogMutation = useMutation({
    mutationFn: async (logId) => {
      await client.delete(`/food-logs/${logId}`);
    },
    onSuccess: () => {
      toast.success(t('diet.dietLogDeleted'));
      editEntryDialog.close();
      queryClient.invalidateQueries({ queryKey: ['foodLog', dateStr, onBehalfOf] });
      queryClient.invalidateQueries({ queryKey: ['weeklySummary'] });
    },
    onError: () => {
      toast.error(t('diet.failedDeleteDietLog'));
    },
  });

  const handleDeleteLog = async () => {
    if (!foodLog?.id) return;
    const ok = await confirm({
      title: t('diet.confirmDeleteDietLog'),
      confirmLabel: t('common.delete'),
      cancelLabel: t('common.cancel'),
      destructive: true,
    });
    if (!ok) return;
    deleteLogMutation.mutate(foodLog.id);
  };

  // ==================== PHOTO MEAL SCAN (FatSecret Image Recognition) ====================
  // [BACKUP] Old photo scan used AI vision via /foods/analyze-photo endpoint (Gemini).
  // The old handler called axios.post(`${API}/foods/analyze-photo`, { image_data, mime_type })
  // and returned a single food with macros. If FatSecret image recognition stops working,
  // restore handlePhotoScan to call /foods/analyze-photo instead of /foods/fatsecret/image-recognition.

  const handlePhotoScan = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPhotoLoading(true);
    setPhotoResult(null);
    setPhotoFoods([]);
    setPhotoItems([]);
    setPhotoMealGroupId(null);
    setPhotoNotes('');
    try {
      // Compress image before sending
      const { compressImage } = await import('../utils/compressImage');
      const compressedDataUrl = await compressImage(file, { maxDimension: 1024, quality: 0.6 });
      const base64 = compressedDataUrl.split(',')[1];

      try {
        const aiRes = await client.post('/foods/analyze-photo', {
          image_data: base64,
        });
        const ai = aiRes.data;
        const rawItems = Array.isArray(ai.items) ? ai.items : [];
        if (rawItems.length === 0) {
          toast.error(t('diet.photoAnalysisFailed'));
          return;
        }
        const items = rawItems.map(it => ({
          food_id: it.food_id,
          name: it.food_name || 'Detected food',
          name_pt: it.food_name_pt || '',
          originalGrams: Number(it.serving_size) || 100,
          grams: Number(it.serving_size) || 100,
          unit: it.serving_unit || 'g',
          calories: Number(it.calories) || 0,
          protein: Number(it.protein) || 0,
          carbs: Number(it.carbs) || 0,
          fat: Number(it.fat) || 0,
          fiber: Number(it.fiber) || 0,
          sugar: Number(it.sugar) || 0,
          selected: true,
        }));
        setPhotoItems(items);
        setPhotoMealGroupId(ai.meal_group_id || null);
        setPhotoNotes(ai.notes || '');
        setPhotoResult({
          food_count: items.length,
          source: 'ai_vision',
        });
        toast.success(t(items.length === 1 ? 'diet.foodsDetected' : 'diet.foodsDetectedPlural', { count: items.length }));
      } catch (aiErr) {
        toast.error(aiErr.response?.data?.detail || t('diet.photoAnalysisFailed'));
      }
    } catch {
      toast.error(t('diet.failedReadImage'));
    } finally {
      setPhotoLoading(false);
      // Reset file inputs so the same file can be selected again
      const inputs = document.querySelectorAll('input[type="file"][accept="image/*"]');
      inputs.forEach(inp => { inp.value = ''; });
    }
  };

  const togglePhotoItem = (idx) => {
    setPhotoItems(prev => prev.map((it, i) => i === idx ? { ...it, selected: !it.selected } : it));
  };
  const updatePhotoItemGrams = (idx, newGrams) => {
    const g = Math.max(0, Math.round(Number(newGrams) || 0));
    setPhotoItems(prev => prev.map((it, i) => i === idx ? { ...it, grams: g } : it));
  };
  const addPhotoItemsToLog = () => {
    const selected = photoItems.filter(it => it.selected && it.grams > 0);
    if (selected.length === 0) return;
    const newEntries = [...(foodLog?.entries || [])];
    for (const it of selected) {
      const scale = it.originalGrams > 0 ? it.grams / it.originalGrams : 1;
      newEntries.push({
        food_id: it.food_id,
        food_name: it.name,
        quantity: it.grams,
        unit: it.unit,
        serving_size: it.originalGrams,
        serving_unit: it.unit,
        serving_description: '',
        servings: scale,
        calories: it.calories * scale,
        protein: it.protein * scale,
        carbs: it.carbs * scale,
        fat: it.fat * scale,
        saturated_fat: 0,
        fiber: it.fiber * scale,
        sugar: it.sugar * scale,
        sodium: 0,
        cholesterol: 0,
        potassium: 0,
        is_coach_recommended: false,
        meal_type: selectedMealType,
      });
    }
    addFoodToLogMutation.mutate({
      foodName: selected.length === 1 ? selected[0].name : t('diet.foodsDetectedPlural', { count: selected.length }),
      mealType: selectedMealType,
      newEntries,
    });
    // Reset photo state so a re-open lands on the empty camera view
    setPhotoItems([]);
    setPhotoResult(null);
    setPhotoNotes('');
    setPhotoMealGroupId(null);
  };

  // Select a food from the photo recognition results
  const selectPhotoFood = (food) => {
    setSelectedFood({
      id: food.fatsecret_id,
      name: food.food_entry_name || food.food_name,
      category: 'Photo Scan',
      serving_size: food.serving_size || 100,
      serving_unit: food.serving_unit || 'g',
      calories: food.calories,
      protein: food.protein,
      carbs: food.carbs,
      fat: food.fat,
      saturated_fat: food.saturated_fat || 0,
      fiber: food.fiber || 0,
      sugar: food.sugar || 0,
      sodium: food.sodium || 0,
      cholesterol: food.cholesterol || 0,
      potassium: food.potassium || 0,
      servings: food.servings || [],
    });
    if (food.servings?.length) {
      setSelectedServing(food.servings[0]);
      setFatsecretDetail({ servings: food.servings });
    }
    setQuantity(1);
    toast.success(t('diet.selectedFood', { name: food.food_entry_name || food.food_name }));
  };

  // ==================== RECIPES (FatSecret Premier) ====================

  const searchRecipes = async (query) => {
    setRecipeLoading(true);
    try {
      const params = new URLSearchParams({ query: query || '', max_results: '20' });
      const res = await client.get(`/recipes/search?${params}`);
      setRecipeResults(res.data.results || []);
    } catch {
      toast.error(t('diet.failedSearchRecipes'));
    } finally {
      setRecipeLoading(false);
    }
  };

  const handleCompleteDay = async () => {
    setCompletingDay(true);
    try {
      await client.post('/food-log/complete', { date: dateStr });
      queryClient.invalidateQueries({ queryKey: ['foodLog', dateStr, onBehalfOf] });
      setShowCompleteConfirm(false);
      setShowDietDayStory(true);
    } catch {
      toast.error(t('diet.failedCompleteDay'));
    } finally {
      setCompletingDay(false);
    }
  };

  const handleUnlockDay = async () => {
    setUnlockingDay(true);
    try {
      await client.post('/food-log/unlock', { date: dateStr });
      queryClient.invalidateQueries({ queryKey: ['foodLog', dateStr, onBehalfOf] });
      setShowUnlockConfirm(false);
    } catch {
      toast.error(t('diet.failedUnlockDay'));
    } finally {
      setUnlockingDay(false);
    }
  };

  const openRecipeDetail = async (recipe) => {
    setSelectedRecipe(recipe);
    setRecipeDetailLoading(true);
    try {
      const res = await client.get(`/recipes/${recipe.recipe_id}`);
      setRecipeDetail(res.data);
    } catch {
      toast.error(t('diet.failedLoadRecipeDetails'));
    } finally {
      setRecipeDetailLoading(false);
    }
  };

  // Animated readouts for the calorie ring + macro bars. Hooks must run before
  // any early-return so the order is stable across renders.
  const animatedCalories = useCountUp(Math.round(foodLog?.total_calories || 0), 600);
  const animatedProtein = useCountUp(Math.round(foodLog?.total_protein || 0), 600);
  const animatedCarbs = useCountUp(Math.round(foodLog?.total_carbs || 0), 600);
  const animatedFat = useCountUp(Math.round(foodLog?.total_fat || 0), 600);
  const [ringPulse, setRingPulse] = useState(false);
  const lastCalRef = useRef(foodLog?.total_calories || 0);
  const dateSwipe = useSwipe(
    () => setSelectedDate(prev => addDays(prev, 1)),
    () => setSelectedDate(prev => subDays(prev, 1)),
    40
  );
  useEffect(() => {
    const cal = foodLog?.total_calories || 0;
    if (lastCalRef.current !== cal) {
      lastCalRef.current = cal;
      setRingPulse(true);
      const id = setTimeout(() => setRingPulse(false), 500);
      return () => clearTimeout(id);
    }
  }, [foodLog?.total_calories]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
      </div>
    );
  }

  const totals = {
    calories: foodLog?.total_calories || 0,
    protein: foodLog?.total_protein || 0,
    carbs: foodLog?.total_carbs || 0,
    fat: foodLog?.total_fat || 0,
  };

  const targets = {
    calories: foodLog?.target_calories || 2000,
    protein: foodLog?.target_protein || 150,
    carbs: foodLog?.target_carbs || 200,
    fat: foodLog?.target_fat || 70,
  };

  const caloriePercentage = Math.min((totals.calories / targets.calories) * 100, 100);
  const calorieDiff = targets.calories - totals.calories;

  if (subLoading) return null;

  if (!hasFeature('diet')) {
    return (
      <ProLockOverlay feature="diet">
        <div className="p-4 space-y-4">
          <h1 className="text-2xl font-bold">{t('diet.dietAndNutrition')}</h1>
          <div className="h-32 bg-muted rounded-lg" />
          <div className="h-32 bg-muted rounded-lg" />
          <div className="h-32 bg-muted rounded-lg" />
        </div>
      </ProLockOverlay>
    );
  }

  return (
    <div className="space-y-6 pb-20 md:pb-0" data-testid="diet-page">
      {onBehalfOf && clientName && (
        <div className="flex items-center gap-3 px-4 py-3 rounded-lg border-2 border-primary/30 bg-primary/10" data-testid="on-behalf-banner">
          <Apple className="h-5 w-5 text-primary shrink-0" />
          <p className="text-sm font-medium">{t('diet.loggingOnBehalfOf')} <span className="text-primary font-bold">{decodeURIComponent(clientName)}</span></p>
        </div>
      )}
      {/* Header with Date Navigation */}
      <div className="flex flex-col gap-2">
        {/* Row 1: Title + action buttons */}
        <div className="flex items-center justify-between gap-2">
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
            {t('diet.yourNutrition')} <span className="text-primary">{t('diet.nutritionLabel')}</span>
          </h1>
          <div className="flex items-center gap-1 shrink-0">
            <Button
              variant={showNutritionInsights ? 'default' : 'outline'}
              size="icon"
              onClick={() => { insightsDialog.open('insights'); enrichEntriesWithMicros(foodLog?.entries || []); }}
              className={`h-11 w-11 md:h-8 md:w-8 ${showNutritionInsights ? 'bg-primary hover:bg-primary/90' : ''}`}
              data-testid="nutrition-insights-btn"
              title={t('diet.nutritionInsightsTitle')}
              tooltip={t('tooltips.nutritionInsights')}
            >
              <PieChart className="h-4 w-4" />
            </Button>
            <Button
              variant={showWeeklySummary ? 'default' : 'outline'}
              size="icon"
              onClick={() => setShowWeeklySummary(!showWeeklySummary)}
              className={`h-11 w-11 md:h-8 md:w-8 ${showWeeklySummary ? 'bg-primary hover:bg-primary/90' : ''}`}
              data-testid="toggle-weekly-summary-btn"
              title={t('diet.weekly')}
              tooltip={t('tooltips.weeklySummary')}
            >
              <BarChart3 className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="icon"
              onClick={() => setShowDietSettingsSheet(true)}
              className="h-11 w-11 md:h-8 md:w-8"
              data-testid="diet-settings-btn"
              title={t('diet.dietSettings')}
              tooltip={t('diet.dietSettings')}
            >
              <SettingsIcon className="h-4 w-4" />
            </Button>
            <InfoButton contentKey="macroTargets" />
          </div>
        </div>
        {/* Row 2: Date navigation — full width, centered */}
        <div className="flex items-center justify-between w-full">
          <Button variant="outline" size="icon" className="h-11 w-11 md:h-8 md:w-8 shrink-0" onClick={() => setSelectedDate(prev => subDays(prev, 1))} data-testid="prev-day-btn" tooltip={t('tooltips.previousDay')}>
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <div
            className="flex-1 mx-2 py-1.5 rounded-lg bg-muted text-center select-none touch-pan-y"
            onTouchStart={dateSwipe.onTouchStart}
            onTouchMove={dateSwipe.onTouchMove}
            onTouchEnd={dateSwipe.onTouchEnd}
          >
            <p className="font-semibold text-sm">{formatDateHeader(selectedDate)}</p>
          </div>
          <Button variant="outline" size="icon" className="h-11 w-11 md:h-8 md:w-8 shrink-0" onClick={() => setSelectedDate(prev => addDays(prev, 1))} data-testid="next-day-btn" tooltip={t('tooltips.nextDay')}>
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* ==================== WEEKLY NUTRITION SUMMARY ==================== */}
      {showWeeklySummary && weeklySummary && (
        <WeeklySummaryPanel summary={weeklySummary} />
      )}

      <div className="grid gap-6">
        {/* Main Food Log Column */}
        <div className="space-y-6">
          {/* Adaptive Macros status card — renders enable CTA if disabled */}
          <AdaptiveMacroCard clientId={onBehalfOf || null} />
          {/* Calorie Summary Card — sticky on mobile so it stays visible while scrolling meals */}
          <Card className="border-border/50 overflow-hidden sm:static sticky top-[60px] z-20 bg-card">
            <div className="h-2 bg-gradient-to-r from-primary to-primary/90" style={{ width: `${caloriePercentage}%` }} />
            <CardContent className="!pt-3 !pb-3 sm:!pt-6 sm:!pb-6 !px-3 sm:!px-6">
              <div className="flex flex-row items-center gap-3 sm:gap-6">
                <div className="shrink-0">
                  <div className={`relative w-36 h-36 sm:w-40 sm:h-40 md:w-48 md:h-48 transition-all duration-500 ${ringPulse ? 'scale-[1.04] drop-shadow-[0_0_12px_rgba(255,140,66,0.5)]' : ''}`}>
                    {(() => {
                      const C = 553;
                      const pCal = (totals.protein || 0) * 4;
                      const cCal = (totals.carbs || 0) * 4;
                      const fCal = (totals.fat || 0) * 9;
                      const targetCal = targets.calories || 1;
                      let pLen = (pCal / targetCal) * C;
                      let cLen = (cCal / targetCal) * C;
                      let fLen = (fCal / targetCal) * C;
                      const sumLen = pLen + cLen + fLen;
                      if (sumLen > C) {
                        const scale = C / sumLen;
                        pLen *= scale; cLen *= scale; fLen *= scale;
                      }
                      return (
                        <svg className="w-full h-full transform -rotate-90">
                          <circle cx="50%" cy="50%" r="45%" className="fill-none stroke-muted stroke-[8]" />
                          {/* Protein (red) */}
                          <circle cx="50%" cy="50%" r="45%" className="fill-none stroke-[8]" stroke="#ef4444" strokeDasharray={`${pLen} ${C - pLen}`} strokeDashoffset={0} />
                          {/* Carbs (yellow) */}
                          <circle cx="50%" cy="50%" r="45%" className="fill-none stroke-[8]" stroke="#eab308" strokeDasharray={`${cLen} ${C - cLen}`} strokeDashoffset={-pLen} />
                          {/* Fat (amber) */}
                          <circle cx="50%" cy="50%" r="45%" className="fill-none stroke-[8]" stroke="#f59e0b" strokeDasharray={`${fLen} ${C - fLen}`} strokeDashoffset={-(pLen + cLen)} />
                        </svg>
                      );
                    })()}
                    <div className="absolute inset-0 flex flex-col items-center justify-center">
                      <Flame className="h-6 w-6 md:h-8 md:w-8 text-primary mb-1" />
                      <span className="text-3xl md:text-4xl font-bold font-mono leading-none">{Math.round(animatedCalories)}</span>
                      <span className="text-xs md:text-sm text-muted-foreground leading-tight mt-0.5">/ {Math.round(targets.calories)}</span>
                      <Badge variant={calorieDiff >= 0 ? 'secondary' : 'destructive'} className="mt-1 sm:mt-1.5 text-[10px] sm:text-xs px-1.5 py-0 sm:px-2 sm:py-0.5">
                        {calorieDiff >= 0 ? `${Math.round(calorieDiff)} ${t('diet.remaining')}` : `${Math.round(Math.abs(calorieDiff))} ${t('diet.over')}`}
                      </Badge>
                    </div>
                  </div>
                </div>
                <div className="w-full flex-1 space-y-2 sm:space-y-3 md:space-y-4 min-w-0">
                  <MacroBar label={t('diet.protein')} current={totals.protein} displayCurrent={animatedProtein} target={targets.protein} color="text-red-500" icon={Beef} />
                  <MacroBar label={t('diet.carbs')} current={totals.carbs} displayCurrent={animatedCarbs} target={targets.carbs} color="text-yellow-500" icon={Wheat} />
                  <MacroBar label={t('diet.fat')} current={totals.fat} displayCurrent={animatedFat} target={targets.fat} color="text-amber-500" icon={Cookie} />
                </div>
              </div>
            </CardContent>
          </Card>


          {/* Diet Plan Progress Bar */}
          {foodLog?.diet_end_date && (() => {
            const endDate = new Date(foodLog.diet_end_date);
            const startDate = foodLog.diet_start_date ? new Date(foodLog.diet_start_date) : null;
            const today = new Date();
            const daysRemaining = Math.ceil((endDate - today) / 86400000);
            const totalDays = startDate ? Math.ceil((endDate - startDate) / 86400000) : null;
            const elapsed = totalDays && startDate ? Math.ceil((today - startDate) / 86400000) : null;
            const pct = totalDays && elapsed !== null ? Math.min(100, Math.max(0, Math.round((elapsed / totalDays) * 100))) : null;
            const isExpired = daysRemaining < 0;
            const isCompleted = foodLog?.diet_status === 'completed';

            if (isExpired || isCompleted) {
              return (
                <div className="mx-0 mb-0 px-3 py-2 rounded-lg bg-muted/50 border border-border text-xs text-muted-foreground text-center">
                  {t('diet.dietPlanCompleted')}
                </div>
              );
            }

            return (
              <button
                type="button"
                onClick={() => setShowDietPlanProgress(true)}
                className="w-full px-3 py-2.5 rounded-lg border border-border/50 bg-muted/30 overflow-hidden text-left hover:bg-muted/50 transition-colors"
                data-testid="diet-plan-progress-btn"
              >
                <div className="flex justify-between items-center mb-1.5 min-w-0">
                  <span className="text-xs font-medium shrink-0">{t('diet.dietPlanProgress')}</span>
                  <span className="text-xs text-muted-foreground truncate ml-2">{t('diet.daysRemaining', { count: daysRemaining })}</span>
                </div>
                {pct !== null && (
                  <div className="w-full h-2 bg-muted rounded-full overflow-hidden">
                    <div className="h-full rounded-full transition-all bg-primary" style={{ width: `${pct}%` }} />
                  </div>
                )}
              </button>
            );
          })()}

          {/* Food Log Section */}
          <div className="space-y-4">
            {activeMealList.map((mealType, mealIdx) => {
              const mealEntries = foodLog?.entries?.filter(e => (e.meal_type || '').toLowerCase() === mealType.value.toLowerCase()) || [];
              const mealCalories = mealEntries.reduce((acc, e) => acc + e.calories, 0);
              const mealProtein = mealEntries.reduce((acc, e) => acc + (e.protein || 0), 0);
              const mealCarbs = mealEntries.reduce((acc, e) => acc + (e.carbs || 0), 0);
              const mealFat = mealEntries.reduce((acc, e) => acc + (e.fat || 0), 0);

              return (
                <Card key={mealType.value} className="border-border/50 overflow-hidden" data-testid={`meal-card-${mealType.value}`}>
                  <CardHeader style={{ padding: '12px 12px 8px 12px' }}>
                    <div className="flex items-center justify-between" style={{ maxWidth: '100%', overflow: 'hidden' }}>
                      <div className="flex items-center gap-1.5" style={{ minWidth: 0, overflow: 'hidden', flexShrink: 1 }}>
                        <span className="text-lg" style={{ flexShrink: 0 }}>{mealType.icon}</span>
                        <span className="text-base font-semibold" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{mealType.label}</span>
                      </div>
                      <div className="flex items-center gap-0.5" style={{ flexShrink: 0 }}>
                        <Badge variant="outline" className="text-xs px-1.5 gap-1.5">
                          <span>{Math.round(mealCalories)} kcal</span>
                          {mealEntries.length > 0 && (
                            <>
                              <span className="hidden sm:inline text-blue-400">P:{Math.round(mealProtein)}</span>
                              <span className="hidden sm:inline text-yellow-400">C:{Math.round(mealCarbs)}</span>
                              <span className="hidden sm:inline text-red-400">F:{Math.round(mealFat)}</span>
                            </>
                          )}
                        </Badge>
                        {mealEntries.length > 0 && (
                          <>
                          <UITooltip>
                            <TooltipTrigger asChild>
                              <button
                                className="inline-flex items-center justify-center h-11 w-11 md:h-8 md:w-8 rounded-md text-blue-500 hover:text-blue-600 hover:bg-blue-500/10"
                                onClick={() => {
                                  setSharePreviewData({
                                    meal_type: mealType.value,
                                    meal_label: mealType.label,
                                    meal_icon: mealType.icon,
                                    date: formatDateForAPI(selectedDate),
                                    calories: mealCalories,
                                    protein: mealProtein,
                                    carbs: mealCarbs,
                                    fat: mealFat,
                                    foods: mealEntries.map(e => ({
                                      name: e.food_name, calories: Math.round(e.calories),
                                      protein: Math.round(e.protein || 0), carbs: Math.round(e.carbs || 0), fat: Math.round(e.fat || 0),
                                      amount: Math.round((e.serving_size || 100) * (e.servings || e.quantity || 1)),
                                      unit: e.serving_unit || e.unit || 'g',
                                    }))
                                  });
                                  setSharePhoto(null);
                                  setShowSharePreview(true);
                                }}
                                data-testid={`share-meal-${mealType.value}`} aria-label={t('tooltips.shareMeal')}
                              >
                                <Share2 className="h-4 w-4" />
                              </button>
                            </TooltipTrigger>
                            <TooltipContent>{t('tooltips.shareMeal')}</TooltipContent>
                          </UITooltip>
                          <UITooltip>
                            <TooltipTrigger asChild>
                              <button
                                className="inline-flex items-center justify-center h-11 w-11 md:h-8 md:w-8 rounded-md text-primary hover:text-primary hover:bg-primary/10"
                                onClick={() => {
                                  setTemplateMealType(mealType.value);
                                  setTemplateName(`My ${mealType.label}`);
                                  setShowSaveTemplateDialog(true);
                                }}
                                data-testid={`save-meal-template-${mealType.value}`}
                                aria-label={t('tooltips.saveAsTemplate')}
                              >
                                <BookmarkPlus className="h-4 w-4" />
                              </button>
                            </TooltipTrigger>
                            <TooltipContent>{t('tooltips.saveAsTemplate')}</TooltipContent>
                          </UITooltip>
                          <UITooltip>
                            <TooltipTrigger asChild>
                              <button
                                className={`inline-flex items-center justify-center h-11 w-11 md:h-8 md:w-8 rounded-md text-purple-500 hover:text-purple-600 hover:bg-purple-500/10 ${foodLog?.completed ? 'opacity-40' : ''}`}
                                onClick={() => {
                                  if (foodLog?.completed) { setShowUnlockConfirm(true); return; }
                                  setMoveMealSource(mealType.value);
                                  setMoveMealAction('move');
                                  setMoveMealTarget(null);
                                  setShowMoveMealDialog(true);
                                }}
                                data-testid={`move-meal-${mealType.value}`} aria-label={t('tooltips.moveMeal')}
                              >
                                <ArrowLeftRight className="h-4 w-4" />
                              </button>
                            </TooltipTrigger>
                            <TooltipContent>{t('tooltips.moveMeal')}</TooltipContent>
                          </UITooltip>
                          </>
                        )}
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent style={{ paddingLeft: 12, paddingRight: 12, paddingTop: 0 }}>
                    {mealEntries.length === 0 && (
                      <p className="text-sm text-muted-foreground text-center pt-4">{t('diet.noFoodLogged')}</p>
                    )}
                    {mealEntries.length > 0 && (
                      <div className="space-y-2">
                        {mealEntries.map((entry, index) => {
                          const globalIdx = foodLog.entries.indexOf(entry);
                          const isEditing = inlineEditIdx === globalIdx;
                          return (
                          <FoodEntryRow
                            key={index}
                            className={`p-3 rounded-lg overflow-hidden ${isEditing ? 'bg-primary/10 border border-primary/30' : 'bg-muted/50'}`}
                            onLongPress={() => setActionEntry({ entry, mealType: mealType.value })}
                          >
                            {isEditing ? (
                              /* Inline Edit Mode */
                              <div className="space-y-2">
                                <p className="font-medium text-base md:text-sm truncate">{entry.food_name}</p>
                                <div className="flex items-center gap-2">
                                  <span className="text-base md:text-xs text-muted-foreground shrink-0">{t('diet.servingsLabel')}</span>
                                  <div className="flex items-center gap-1">
                                    <Button variant="outline" size="icon" className="h-11 w-11 md:h-8 md:w-8" onClick={() => setInlineEditQty(Math.max(0.5, Math.round((inlineEditQty - 0.5) * 100) / 100))}>-</Button>
                                    <Input
                                      type="number"
                                      step="0.01"
                                      min="0"
                                      value={inlineEditQty}
                                      onChange={(e) => {
                                        const val = e.target.value;
                                        if (val === '') { setInlineEditQty(0); return; }
                                        const num = parseFloat(val);
                                        if (!isNaN(num)) setInlineEditQty(num);
                                      }}
                                      onBlur={(e) => {
                                        const num = parseFloat(e.target.value);
                                        if (isNaN(num) || num < 0.01) setInlineEditQty(0.5);
                                      }}
                                      onFocus={(e) => e.target.select()}
                                      className="w-16 h-11 md:h-8 text-center text-sm font-medium px-1"
                                    />
                                    <Button variant="outline" size="icon" className="h-11 w-11 md:h-8 md:w-8" onClick={() => setInlineEditQty(Math.round((inlineEditQty + 0.5) * 100) / 100)}>+</Button>
                                  </div>
                                  <span className="text-xs text-muted-foreground bg-muted/50 px-2 py-0.5 rounded">
                                    {(() => { const d = buildServingDisplay(inlineEditQty, entry.serving_size, entry.serving_unit, entry.serving_description); return `= ${Math.round(d.quantity * 10) / 10} ${d.unit}`; })()}
                                  </span>
                                </div>
                                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                  <span className="text-primary font-semibold">{Math.round((entry.calories / (entry.servings || entry.quantity || 1)) * inlineEditQty)} kcal</span>
                                  <span>P:{Math.round((entry.protein / (entry.servings || entry.quantity || 1)) * inlineEditQty)}g</span>
                                  <span>C:{Math.round((entry.carbs / (entry.servings || entry.quantity || 1)) * inlineEditQty)}g</span>
                                  <span>F:{Math.round((entry.fat / (entry.servings || entry.quantity || 1)) * inlineEditQty)}g</span>
                                </div>
                                <div className="flex gap-2 justify-end">
                                  <Button variant="ghost" size="sm" className="h-10 md:h-8 text-xs" onClick={() => setInlineEditIdx(null)}>{t('common.cancel')}</Button>
                                  <Button size="sm" className="h-10 md:h-8 text-xs bg-primary hover:bg-primary/90" onClick={() => handleInlineEditSave(globalIdx, inlineEditQty)} data-testid={`inline-edit-save-${globalIdx}`}>
                                    <Check className="h-3 w-3 mr-1" /> {t('common.save')}
                                  </Button>
                                </div>
                              </div>
                            ) : (
                              /* Display Mode */
                              <div className="flex items-center gap-1">
                                <div className="cursor-pointer flex-1" onClick={() => openFoodPreview(entry, globalIdx)} data-testid={`food-entry-preview-${index}`}>
                                  <p className="font-medium hover:text-primary transition-colors"><span className="sm:hidden">{entry.food_name.length > 28 ? entry.food_name.slice(0, 28) + '…' : entry.food_name}</span><span className="hidden sm:inline">{entry.food_name}</span></p>
                                  <p className="text-sm text-muted-foreground">{Math.round((entry.quantity || 0) * 100) / 100} {entry.unit} - {Math.round(entry.calories)} kcal · P:{Math.round(entry.protein)} C:{Math.round(entry.carbs)} F:{Math.round(entry.fat)}</p>
                                </div>
                                <div className="flex items-center shrink-0">
                                  <UITooltip>
                                    <TooltipTrigger asChild>
                                      <button onClick={() => {
                                        if (foodLog?.completed) { setShowUnlockConfirm(true); return; }
                                        setInlineEditIdx(globalIdx); setInlineEditQty(entry.servings || 1);
                                      }} className={`inline-flex items-center justify-center h-11 w-11 md:h-8 md:w-8 rounded-md hover:bg-accent active:bg-accent text-muted-foreground hover:text-primary ${foodLog?.completed ? 'opacity-40' : ''}`} data-testid={`edit-entry-${globalIdx}`} aria-label={t('tooltips.editEntry')}>
                                        <Pencil className="h-3.5 w-3.5" />
                                      </button>
                                    </TooltipTrigger>
                                    <TooltipContent>{t('tooltips.editEntry')}</TooltipContent>
                                  </UITooltip>
                                  <UITooltip>
                                    <TooltipTrigger asChild>
                                      <button onClick={() => {
                                        if (foodLog?.completed) { setShowUnlockConfirm(true); return; }
                                        removeFoodFromLog(globalIdx);
                                      }} className={`inline-flex items-center justify-center h-11 w-11 md:h-8 md:w-8 rounded-md hover:bg-accent active:bg-accent text-destructive ${foodLog?.completed ? 'opacity-40' : ''}`} data-testid={`delete-entry-${globalIdx}`} aria-label={t('tooltips.removeEntry')}>
                                        <X className="h-4 w-4" />
                                      </button>
                                    </TooltipTrigger>
                                    <TooltipContent>{t('tooltips.removeEntry')}</TooltipContent>
                                  </UITooltip>
                                </div>
                              </div>
                            )}
                          </FoodEntryRow>
                          );
                        })}
                      </div>
                    )}
                    <div className={`flex items-center justify-center gap-1.5 ${mealEntries.length === 0 ? 'pt-3 pb-2' : 'pt-3 pb-1'}`}>
                      <Button
                        variant="outline"
                        size="sm"
                        className="min-h-[44px] md:min-h-0 md:h-8 px-3 md:px-2.5 text-sm md:text-xs border-amber-500/40 text-amber-500 hover:bg-amber-500/10 hover:text-amber-600 font-medium whitespace-nowrap"
                        onClick={() => {
                          if (foodLog?.completed) { setShowUnlockConfirm(true); return; }
                          setAddFoodTab('quick');
                          setPendingFoods([]);
                          addFoodDialog.open(mealType.value);
                        }}
                        data-testid={`ai-quick-${mealType.value}`}
                      >
                        <Zap className="h-3.5 w-3.5 mr-1" />
                        Quick
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="min-h-[44px] md:min-h-0 md:h-8 px-3 md:px-2.5 text-sm md:text-xs border-violet-500/40 text-violet-500 hover:bg-violet-500/10 hover:text-violet-600 font-medium whitespace-nowrap"
                        onClick={() => {
                          if (foodLog?.completed) { setShowUnlockConfirm(true); return; }
                          setAddFoodTab('photo');
                          setPendingFoods([]);
                          addFoodDialog.open(mealType.value);
                        }}
                        data-testid={`ai-photo-${mealType.value}`}
                      >
                        <Camera className="h-3.5 w-3.5 mr-1" />
                        Photo
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="min-h-[44px] md:min-h-0 md:h-8 px-3 md:px-2.5 text-sm md:text-xs border-primary/40 text-primary hover:bg-primary/10 hover:text-primary font-semibold whitespace-nowrap"
                        onClick={() => {
                          if (foodLog?.completed) { setShowUnlockConfirm(true); return; }
                          setAddFoodTab('history');
                          setPendingFoods([]);
                          addFoodDialog.open(mealType.value);
                        }}
                        data-testid={`add-food-${mealType.value}`}
                        {...(mealIdx === 0 ? { 'data-tour-anchor': TA.DIET_ADD_FOOD_BTN } : {})}
                      >
                        <Plus className="h-3.5 w-3.5 mr-1" />
                        {t('diet.addFood')}
                      </Button>
                    </div>

                    {/* Ghost suggestion strip */}
                    {(() => {
                      const mealOptions = (todaySuggestion?.meals || []).filter(
                        m => (m.meal_name || m.meal_type || '').toLowerCase() === mealType.value.toLowerCase()
                          && m.foods?.length > 0
                      );
                      if (mealOptions.length === 0) return null;

                      // Hide strip if any option's foods are already logged
                      const alreadyAdded = mealOptions.some(opt =>
                        opt.foods.some(sf => mealEntries.some(e => e.food_name === sf.food_name))
                      );
                      if (alreadyAdded) return null;

                      // Single option with no name — legacy behaviour unchanged
                      if (mealOptions.length === 1 && !mealOptions[0].option_name) {
                        const suggestionMeal = mealOptions[0];
                        const stripKcal = suggestionMeal.foods.reduce(
                          (sum, f) => sum + (f.calories || 0) * (f.servings || 1), 0
                        );
                        return (
                          <div className="mt-3 rounded-md bg-muted/30 border-l-2 border-primary/30 px-3 py-2 space-y-1">
                            <div className="flex items-center justify-between">
                              <span className="text-xs font-medium text-muted-foreground">
                                {t('diet.coachsPlanHeader')} · ~{Math.round(stripKcal)} kcal
                              </span>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-9 md:h-7 px-2 text-xs text-primary hover:text-[#e05200] hover:bg-primary/90"
                                onClick={() => handleAddSuggestionMeal(suggestionMeal)}
                              >
                                <Plus className="h-3 w-3 mr-1" />
                                {t('diet.addAll')}
                              </Button>
                            </div>
                            {suggestionMeal.foods.map((food, fIdx) => {
                              const s = food.servings || 1;
                              const d = buildServingDisplay(s, food.serving_size, food.serving_unit, food.serving_description);
                              return (
                                <p key={fIdx} className="text-xs text-muted-foreground opacity-70 italic pl-1">
                                  {food.food_name} · {Math.round(d.quantity * 10) / 10} {d.unit} · {Math.round((food.calories || 0) * s)} kcal
                                </p>
                              );
                            })}
                          </div>
                        );
                      }

                      // Multiple options — pick-one list
                      return (
                        <div className="mt-3 rounded-md bg-muted/30 border-l-2 border-primary/30 px-3 py-2 space-y-1">
                          <span className="text-xs font-medium text-muted-foreground block mb-1">{t('diet.coachsPlanHeader')}</span>
                          {mealOptions.map((opt, oIdx) => {
                            const optKcal = opt.foods.reduce((sum, f) => sum + (f.calories || 0) * (f.servings || 1), 0);
                            const preview = opt.foods.slice(0, 2).map(f => f.food_name).join(', ')
                              + (opt.foods.length > 2 ? ` +${opt.foods.length - 2}` : '');
                            return (
                              <button
                                key={oIdx}
                                type="button"
                                className="w-full text-left px-2 py-1.5 rounded hover:bg-primary/90 active:bg-primary/10 transition-colors group"
                                onClick={() => handleAddSuggestionMeal(opt)}
                              >
                                <span className="text-xs font-medium text-foreground group-hover:text-primary">
                                  {opt.option_name || t('diet.optionLabel', { letter: String.fromCharCode(65 + oIdx) })}
                                </span>
                                <span className="text-xs text-muted-foreground ml-1.5">
                                  · ~{Math.round(optKcal)} kcal
                                </span>
                                <span className="text-xs text-muted-foreground/60 ml-1.5 italic">
                                  {preview}
                                </span>
                              </button>
                            );
                          })}
                        </div>
                      );
                    })()}
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* Water tracker — opt-in, sits after the meals so it doesn't crowd the food log */}
          {user?.water_tracking_enabled && (
            <WaterTracker
              currentMl={foodLog?.water_ml || 0}
              targetMl={user?.water_target_ml || 2000}
              onChange={(water_ml) => setWaterMutation.mutate({ date: dateStr, water_ml })}
            />
          )}

          {/* Complete Day Button */}
          {(foodLog?.entries?.length || 0) > 0 && (
            <div className="flex items-center gap-2 pt-2">
              {foodLog?.completed ? (
                <>
                  <div className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-green-500/10 border border-green-500/30 text-green-500 text-sm font-semibold flex-1" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                    <CheckCircle className="h-4 w-4 flex-shrink-0" />
                    {t('diet.dayCompleted')}
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-muted-foreground hover:text-foreground px-3"
                    onClick={() => setShowUnlockConfirm(true)}
                  >
                    <Lock className="h-4 w-4 mr-1" />
                    {t('diet.unlockBtn')}
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-11 w-11 md:h-9 md:w-9 text-primary hover:bg-primary/90"
                    onClick={() => setShowDietDayStory(true)}
                    title="Share nutrition day"
                    tooltip={t('tooltips.shareDay')}
                  >
                    <Share2 className="h-4 w-4" />
                  </Button>
                </>
              ) : (
                <Button
                  className="flex-1 bg-green-600 hover:bg-green-700 text-white font-semibold"
                  onClick={() => setShowCompleteConfirm(true)}
                  disabled={completingDay}
                >
                  <CheckCircle className="h-4 w-4 mr-2" />
                  {t('diet.completeDay')}
                </Button>
              )}
            </div>
          )}
        </div>


      </div>

      {/* ==================== ADD FOOD / QUICK MEALS DIALOG ==================== */}
      <FullScreenPage open={showAddFoodDialog} onClose={() => addFoodDialog.close()} title={t('diet.addFoodTitle')} noPadding flexContent
        rightAction={
          <Select value={selectedMealType} onValueChange={(v) => { setSelectedMealType(v); addFoodDialog.open(v); }}>
            <SelectTrigger className="h-11 md:h-8 w-36 text-xs" data-testid="meal-type-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {activeMealList.map(type => (
                <SelectItem key={type.value} value={type.value}>
                  {type.icon} {type.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        }
      >
        {/* Fixed Header: Search + Tabs */}
        <div className="px-4 pt-3 pb-2 space-y-2 shrink-0 border-b border-border/50">
            {/* Search Input — always visible */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setSelectedFood(null);
                  setFatsecretDetail(null);
                  setSelectedServing(null);
                  if (e.target.value.length >= 2 && addFoodTab !== 'search' && addFoodTab !== 'myfoods' && addFoodTab !== 'database') {
                    setAddFoodTab('search');
                  }
                }}
                onFocus={() => { if (searchLanguage === 'en' && autocompleteResults.length) setShowAutocomplete(true); }}
                placeholder={t('diet.searchFoods')}
                className="pl-10 pr-24 h-9"
                data-testid="food-search-input"
                data-tour-anchor={TA.DIET_PICKER_SEARCH}
                autoComplete="off"
              />
              {/* Language flag toggle + loading spinner */}
              <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1">
                {fatsecretLoading && <Loader2 className="h-4 w-4 animate-spin text-primary" />}
                <button
                  type="button"
                  onClick={() => {
                    if (searchLanguage !== 'en') {
                      setSearchLanguage('en');
                      setSearchQuery('');
                      setFatsecretResults([]);
                      setOffResults([]);
                      setAutocompleteResults([]);
                      setShowAutocomplete(false);
                      setSelectedFood(null);
                      setFatsecretDetail(null);
                      setSelectedServing(null);
                    }
                  }}
                  className={`text-base leading-none px-1 py-0.5 rounded transition-all ${searchLanguage === 'en' ? 'ring-2 ring-primary bg-primary/10 scale-110' : 'opacity-50 hover:opacity-80'}`}
                  title={t('tooltips.searchEnglish', 'Search in English (FatSecret)')}
                  data-testid="flag-en"
                >
                  🇬🇧
                </button>
                <button
                  type="button"
                  onClick={() => {
                    if (searchLanguage !== 'pt') {
                      setSearchLanguage('pt');
                      setSearchQuery('');
                      setFatsecretResults([]);
                      setOffResults([]);
                      setAutocompleteResults([]);
                      setShowAutocomplete(false);
                      setSelectedFood(null);
                      setFatsecretDetail(null);
                      setSelectedServing(null);
                    }
                  }}
                  className={`text-base leading-none px-1 py-0.5 rounded transition-all ${searchLanguage === 'pt' ? 'ring-2 ring-primary bg-primary/10 scale-110' : 'opacity-50 hover:opacity-80'}`}
                  title={t('tooltips.searchPortuguese', 'Pesquisar em Português (Open Food Facts)')}
                  data-testid="flag-pt"
                >
                  🇵🇹
                </button>
              </div>
              {/* Autocomplete dropdown — only in English mode */}
              {searchLanguage === 'en' && showAutocomplete && autocompleteResults.length > 0 && !fatsecretLoading && (
                <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-background border border-border rounded-lg shadow-lg max-h-48 overflow-auto" data-testid="autocomplete-dropdown">
                  {autocompleteResults.map((s, i) => (
                    <button
                      key={i}
                      className="w-full text-left px-3 py-2 text-sm hover:bg-muted active:bg-muted transition-colors flex items-center gap-2 border-b border-border/30 last:border-0"
                      onClick={() => pickAutocomplete(s)}
                      data-testid={`autocomplete-item-${i}`}
                    >
                      <Search className="h-3 w-3 text-muted-foreground shrink-0" />
                      <span>{s}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
            {/* Tab Bar */}
            <Tabs value={addFoodTab} onValueChange={(v) => { setAddFoodTab(v); setPhotoResult(null); setPhotoFoods([]); setPhotoItems([]); setPhotoMealGroupId(null); setPhotoNotes(''); setSelectedRecipe(null); setRecipeDetail(null); }} className="w-full">
              <TabsList className="grid w-full grid-cols-4 sm:grid-cols-7 h-auto sm:h-8" data-testid="add-food-tabs">
                <TabsTrigger value="database" className="text-xs px-1 gap-1" data-testid="tab-database">
                  <Database className="h-3.5 w-3.5" /> {t('diet.tabDatabase')}
                </TabsTrigger>
                <TabsTrigger value="history" className="text-xs px-1 gap-1" data-testid="tab-history">
                  <Clock className="h-3.5 w-3.5" /> {t('diet.tabHistory')}
                </TabsTrigger>
                <TabsTrigger value="myfoods" className="text-xs px-1 gap-1" data-testid="tab-my-foods">
                  <UtensilsCrossed className="h-3.5 w-3.5" /> {t('diet.tabMyFoods')}
                </TabsTrigger>
                <TabsTrigger value="photo" className="text-xs px-1 gap-1" data-testid="tab-photo-scan">
                  <Camera className="h-3.5 w-3.5" /> {t('diet.tabPhoto')}
                </TabsTrigger>
                <TabsTrigger value="scan" className="text-xs px-1 gap-1" data-testid="tab-scan-barcode">
                  <Scan className="h-3.5 w-3.5" /> {t('diet.tabScan')}
                </TabsTrigger>
                <TabsTrigger value="recipes" className="text-xs px-1 gap-1" data-testid="tab-recipes">
                  <BookOpen className="h-3.5 w-3.5" /> {t('diet.tabRecipes')}
                </TabsTrigger>
                <TabsTrigger value="quick" className="text-xs px-1 gap-1" data-testid="tab-quick-meals">
                  <Zap className="h-3.5 w-3.5" /> {t('diet.tabQuick')}
                </TabsTrigger>
              </TabsList>
            </Tabs>
          </div>

          {/* Scrollable Content Area */}
          <div className="flex-1 overflow-auto px-4 py-2">

            {/* ---- HISTORY TAB ---- */}
            {addFoodTab === 'history' && (
              <div className="space-y-1" data-testid="history-tab-content">
                {recentFoods.length === 0 && !recentFoodsLoading && (
                  <div className="text-center py-10 text-muted-foreground">
                    <Clock className="h-10 w-10 mx-auto mb-3 opacity-25" />
                    <p className="text-sm font-medium">{t('diet.noRecentFoods')}</p>
                    <p className="text-xs mt-1">{t('diet.noRecentFoodsHint')}</p>
                  </div>
                )}
                {recentFoods.filter(rf => {
                  if (!searchQuery || searchQuery.length < 2) return true;
                  return rf.food_name.toLowerCase().includes(searchQuery.toLowerCase());
                }).map((rf, idx) => (
                  <div
                    key={`${rf.food_name}-${idx}`}
                    className={`p-2.5 rounded-lg cursor-pointer transition-colors ${selectedFood?.name === rf.food_name && selectedFood?._fromHistory ? 'bg-primary/10 border border-primary' : 'bg-muted/30 hover:bg-muted/60'}`}
                    onClick={() => {
                      // Toggle: click again to deselect
                      if (selectedFood?.name === rf.food_name && selectedFood?._fromHistory) {
                        setSelectedFood(null);
                        setFatsecretDetail(null);
                        setSelectedServing(null);
                        return;
                      }
                      const servings = rf.servings || 1;
                      setSelectedFood({
                        id: rf.food_id,
                        name: rf.food_name,
                        calories: Math.round(rf.calories / servings),
                        protein: +(rf.protein / servings).toFixed(1),
                        carbs: +(rf.carbs / servings).toFixed(1),
                        fat: +(rf.fat / servings).toFixed(1),
                        saturated_fat: +((rf.saturated_fat || 0) / servings).toFixed(2),
                        fiber: +((rf.fiber || 0) / servings).toFixed(1),
                        sugar: +((rf.sugar || 0) / servings).toFixed(1),
                        sodium: +((rf.sodium || 0) / servings).toFixed(1),
                        cholesterol: +((rf.cholesterol || 0) / servings).toFixed(1),
                        potassium: +((rf.potassium || 0) / servings).toFixed(1),
                        serving_size: rf.serving_size || 100,
                        serving_unit: rf.serving_unit || 'g',
                        serving_description: `${rf.serving_size || 100}${rf.serving_unit || 'g'}`,
                        _fromHistory: true,
                      });
                      setFatsecretDetail(null);
                      setSelectedServing(null);
                      setQuantity(1);
                    }}
                    data-testid={`recent-food-${idx}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="min-w-0 flex-1">
                        <p className="font-medium text-sm truncate">{rf.food_name}</p>
                        <p className="text-xs text-muted-foreground">
                          {rf.serving_size || 100}{rf.serving_unit || 'g'} per serving · {Math.round(rf.calories)} kcal · P:{Math.round(rf.protein)}g C:{Math.round(rf.carbs)}g F:{Math.round(rf.fat)}g
                        </p>
                      </div>
                      <div className="flex items-center gap-1.5 shrink-0 ml-2">
                        <Badge variant="outline" className="text-xs h-5">x{rf.log_count}</Badge>
                      </div>
                    </div>
                  </div>
                ))}
                {recentFoodsLoading && (
                  <div className="flex items-center justify-center py-4 gap-2 text-muted-foreground">
                    <Loader2 className="h-4 w-4 animate-spin" /> {t('common.loading')}
                  </div>
                )}
                {!recentFoodsLoading && recentFoodsOffset < recentFoodsTotal && (
                  <Button variant="ghost" className="w-full text-xs text-primary hover:text-primary" onClick={loadMoreRecentFoods} data-testid="load-more-recent">
                    {t('diet.loadMore', { count: recentFoodsTotal - recentFoodsOffset })}
                  </Button>
                )}
              </div>
            )}

            {/* ---- MY FOODS TAB ---- */}
            {addFoodTab === 'myfoods' && (
              <div className="space-y-2" data-testid="myfoods-tab-content">
                {/* Category Filter */}
                <div className="flex flex-wrap gap-1.5">
                  <Badge variant={selectedCategory === 'all' ? 'default' : 'outline'} className="cursor-pointer text-xs" onClick={() => setSelectedCategory('all')}>{t('common.all')}</Badge>
                  {categories.map(cat => (
                    <Badge key={cat} variant={selectedCategory === cat ? 'default' : 'outline'} className="cursor-pointer text-xs" onClick={() => setSelectedCategory(cat)}>{cat}</Badge>
                  ))}
                  <Badge variant="outline" className="cursor-pointer border-dashed border-primary text-primary text-xs" onClick={() => { addFoodDialog.close(); setShowCustomFood(true); }} data-testid="create-custom-food-btn">{t('diet.customBadge')}</Badge>
                </div>
                <div className="space-y-1">
                  {/* Always-visible "Add New Food" button as the first item */}
                  <button
                    type="button"
                    onClick={() => { addFoodDialog.close(); setShowCustomFood(true); }}
                    className="w-full p-2.5 rounded-lg border border-dashed border-[#FF5C00]/60 bg-[#FF5C00]/5 hover:bg-[#FF5C00]/10 transition-colors flex items-center gap-2.5 text-[#FF5C00]"
                    data-testid="add-new-food-btn"
                  >
                    <div className="h-8 w-8 rounded-lg bg-[#FF5C00]/15 flex items-center justify-center shrink-0">
                      <Plus className="h-4 w-4" />
                    </div>
                    <div className="text-left">
                      <p className="font-medium text-sm">{t('diet.addNewFood')}</p>
                      <p className="text-xs opacity-80">{t('diet.createCustomFoodEntry')}</p>
                    </div>
                  </button>
                  {filteredFoods.length === 0 && (
                    <div className="text-center py-8 text-muted-foreground">
                      <UtensilsCrossed className="h-8 w-8 mx-auto mb-2 opacity-25" />
                      <p className="text-sm">{t('diet.noFoodsInDatabase')}</p>
                      <p className="text-xs mt-1">{t('diet.noFoodsInDatabaseHint')}</p>
                    </div>
                  )}
                  {filteredFoods.map(food => {
                    const isOwner = food.created_by === user?.id || food.submitted_by === user?.id;
                    return (
                    <div
                      key={food.id}
                      className={`p-2.5 rounded-lg cursor-pointer transition-colors ${selectedFood?.id === food.id && !selectedFood?._fromHistory ? 'bg-primary/10 border border-primary' : 'bg-muted/30 hover:bg-muted/60'}`}
                      onClick={() => { setSelectedFood(food); setFatsecretDetail(null); setSelectedServing(null); setQuantity(1); }}
                      data-testid={`food-item-${food.id}`}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <div className="min-w-0 flex-1">
                          <p className="font-medium text-sm truncate">{food.name}</p>
                          <p className="text-xs text-muted-foreground">
                            {food.serving_description || (food.serving_size != null && food.serving_unit ? `${food.serving_size}${food.serving_unit}` : 'serving')} · {food.calories} kcal · P:{food.protein}g C:{food.carbs}g F:{food.fat}g
                          </p>
                        </div>
                        <div className="flex items-center gap-1 shrink-0">
                          {food.source === 'fatsecret' && <Badge className="bg-green-500/10 text-green-400 border-green-500/30 text-xs">FatSecret</Badge>}
                          {food.brand && <Badge variant="outline" className="text-xs">{food.brand}</Badge>}
                          {isOwner && (
                            <>
                              <button
                                type="button"
                                onClick={(e) => { e.stopPropagation(); handleEditMyFood(food); }}
                                className="h-11 w-11 md:h-8 md:w-8 rounded-md flex items-center justify-center text-muted-foreground hover:text-primary hover:bg-primary/10 transition-colors"
                                title="Edit"
                                data-testid={`edit-food-${food.id}`}
                              >
                                <Pencil className="h-4 w-4" />
                              </button>
                              <button
                                type="button"
                                onClick={(e) => { e.stopPropagation(); handleDeleteMyFood(food); }}
                                disabled={deleteFoodMutation.isPending}
                                className="h-11 w-11 md:h-8 md:w-8 rounded-md flex items-center justify-center text-muted-foreground hover:text-red-500 hover:bg-red-500/10 transition-colors disabled:opacity-50"
                                title="Delete"
                                data-testid={`delete-food-${food.id}`}
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* ---- DATABASE TAB (browse the full catalogue) ---- */}
            {addFoodTab === 'database' && (
              <div className="space-y-2" data-testid="database-tab-content">
                {/* Category Filter */}
                <div className="flex flex-wrap gap-1.5">
                  <Badge variant={selectedCategory === 'all' ? 'default' : 'outline'} className="cursor-pointer text-xs" onClick={() => setSelectedCategory('all')}>{t('common.all')}</Badge>
                  {categories.map(cat => (
                    <Badge key={cat} variant={selectedCategory === cat ? 'default' : 'outline'} className="cursor-pointer text-xs" onClick={() => setSelectedCategory(cat)}>{cat}</Badge>
                  ))}
                </div>
                <div className="space-y-1">
                  {catalogueLoading && (
                    <div className="flex items-center justify-center py-6 gap-2 text-muted-foreground">
                      <Loader2 className="h-4 w-4 animate-spin" /> {t('common.loading')}
                    </div>
                  )}
                  {!catalogueLoading && catalogueFoods.length === 0 && (
                    <div className="text-center py-8 text-muted-foreground">
                      <Database className="h-8 w-8 mx-auto mb-2 opacity-25" />
                      <p className="text-sm">{t('diet.noFoodsInCategory')}</p>
                      <p className="text-xs mt-1">{t('diet.noFoodsInCategoryHint')}</p>
                    </div>
                  )}
                  {!catalogueLoading && catalogueFoods.length > 0 && (
                    <div className="flex items-center gap-2 px-1 pt-2 pb-1">
                      <Star className="h-4 w-4 text-[#FF5C00] fill-[#FF5C00]" />
                      <span className="text-xs font-semibold text-[#FF5C00] uppercase tracking-wide">
                        trainedbyRS Foods
                      </span>
                    </div>
                  )}
                  {!catalogueLoading && catalogueFoods.map((food, idx) => (
                    <div
                      key={`catalogue-${food.id}`}
                      className={`p-3 rounded-lg cursor-pointer transition-colors ${
                        idx === 0
                          ? 'bg-[#FF5C00]/10 border border-[#FF5C00]/30 hover:bg-[#FF5C00]/20'
                          : 'bg-card hover:bg-muted active:bg-muted border border-border/50'
                      }`}
                      onClick={() => { setSelectedFood(food); setFatsecretDetail(null); setSelectedServing(null); setQuantity(1); }}
                      data-testid={`database-food-${food.id}`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm truncate">
                            {food.name}
                            {food.brand ? <span className="text-[#FF5C00]"> - {food.brand}</span> : null}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {food.serving_description || `${food.serving_size || 100}${food.serving_unit || 'g'}`} · {Math.round(food.calories)} kcal · P:{food.protein}g C:{food.carbs}g F:{food.fat}g
                          </p>
                          {food.category && (
                            <p className="text-xs text-muted-foreground/70">{food.category}</p>
                          )}
                        </div>
                        <div className="flex items-center gap-1.5 ml-2">
                          {food.brand ? (
                            <span className="text-[10px] px-1.5 py-0.5 rounded border border-[#FF5C00]/30 text-[#FF5C00]">Brand</span>
                          ) : (
                            <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#FF5C00]/10 text-[#FF5C00] border border-[#FF5C00]/30">Generic</span>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* ---- SEARCH TAB (FatSecret) ---- */}
            {addFoodTab === 'search' && (
              <div className="space-y-2" data-testid="search-tab-content">
                {/* Category filter — browse the full catalogue by category */}
                <div className="flex flex-wrap gap-1.5">
                  <Badge variant={selectedCategory === 'all' ? 'default' : 'outline'} className="cursor-pointer text-xs" onClick={() => setSelectedCategory('all')}>{t('common.all')}</Badge>
                  {categories.map(cat => (
                    <Badge key={cat} variant={selectedCategory === cat ? 'default' : 'outline'} className="cursor-pointer text-xs" onClick={() => setSelectedCategory(cat)}>{cat}</Badge>
                  ))}
                  <Badge variant="outline" className="cursor-pointer border-dashed border-primary text-primary text-xs" onClick={() => { addFoodDialog.close(); setShowCustomFood(true); }} data-testid="create-custom-food-btn-search">{t('diet.customBadge')}</Badge>
                </div>
                {/* Community + FatSecret results */}
                {searchQuery && searchQuery.length >= 2 ? (
                  <>
                    {/* === trainedbyRS Foods Section === */}
                    {communityResults.length > 0 && (
                      <div className="space-y-1">
                        <div className="flex items-center gap-2 px-1 pt-2 pb-1">
                          <Star className="h-4 w-4 text-[#FF5C00] fill-[#FF5C00]" />
                          <span className="text-xs font-semibold text-[#FF5C00] uppercase tracking-wide">
                            trainedbyRS Foods
                          </span>
                        </div>
                        {communityResults.map((food, idx) => (
                          <div
                            key={`community-${food.id}`}
                            className={`p-3 rounded-lg cursor-pointer transition-colors ${
                              idx === 0
                                ? 'bg-[#FF5C00]/10 border border-[#FF5C00]/30 hover:bg-[#FF5C00]/20'
                                : 'bg-card hover:bg-muted active:bg-muted border border-border/50'
                            }`}
                            onClick={() => {
                              setFatsecretDetail(null);
                              setSelectedServing(null);
                              setSelectedFood({
                                _id: food.id,
                                name: food.name,
                                calories: food.calories,
                                protein: food.protein,
                                carbs: food.carbs,
                                fat: food.fat,
                                saturated_fat: food.saturated_fat,
                                serving_size: food.serving_size || 100,
                                serving_unit: food.serving_unit || 'g',
                                serving_description: food.serving_description,
                                servings: food.servings || [],
                                source: 'community',
                              });
                              setQuantity(1);
                            }}
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex-1 min-w-0">
                                <p className="font-medium text-sm truncate">
                                  {food.brand ? <span className="text-[#FF5C00]">{food.brand}</span> : null}
                                  {food.brand ? ' ' : ''}{food.name}
                                </p>
                                <p className="text-xs text-muted-foreground">
                                  {food.serving_description || `${food.serving_size || 100}${food.serving_unit || 'g'}`} · {Math.round(food.calories)} kcal · P:{food.protein}g C:{food.carbs}g F:{food.fat}g
                                </p>
                                {food.category && (
                                  <p className="text-xs text-muted-foreground/70">{food.category}</p>
                                )}
                              </div>
                              <div className="flex items-center gap-1.5 ml-2">
                                {food.brand ? (
                                  <span className="text-[10px] px-1.5 py-0.5 rounded border border-[#FF5C00]/30 text-[#FF5C00]">Brand</span>
                                ) : (
                                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#FF5C00]/10 text-[#FF5C00] border border-[#FF5C00]/30">Generic</span>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* === Divider between community and FatSecret === */}
                    {communityResults.length > 0 && mergedResults.length > 0 && (
                      <div className="px-1 py-2">
                        <div className="h-px bg-border/50" />
                      </div>
                    )}

                    {mergedResults.length > 0 && (
                      <>
                        {/* "More Results" header when community results exist */}
                        {communityResults.length > 0 && (
                          <div className="flex items-center gap-2 px-1 pt-1 pb-1">
                            <div className="h-2 w-2 rounded-full bg-green-500" />
                            <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                              {t('diet.moreResults')}
                            </span>
                          </div>
                        )}
                        {/* Best Correspondence — first result with star (shown only when no community results) */}
                        {communityResults.length === 0 && (
                          <>
                            <p className="text-xs text-muted-foreground px-2 py-1 uppercase tracking-wide font-semibold flex items-center gap-1">
                              <Star className="h-3 w-3 text-yellow-500 fill-yellow-500" /> {t('diet.bestMatch')}
                            </p>
                            {(() => {
                              const first = mergedResults[0];
                              const isOff = first.source === 'openfoodfacts';
                              return (
                                <div
                                  className={`p-3 rounded-lg cursor-pointer transition-colors border-2 ${
                                    (isOff ? selectedFood?.off_id === first.off_id : selectedFood?.fatsecret_id === first.fatsecret_id)
                                      ? 'bg-primary/10 border-primary'
                                      : 'border-yellow-500/30 bg-yellow-500/5 hover:bg-yellow-500/10'
                                  }`}
                                  onClick={() => isOff ? selectOpenFoodFactsFood(first) : selectFatsecretFood(first)}
                                  data-testid="best-match-food"
                                >
                                  <div className="flex items-center gap-2.5">
                                    {first.image_url && (
                                      <img src={first.image_url_small || first.image_url} alt="" className="w-10 h-10 rounded-lg object-cover shrink-0 border border-border/50" loading="lazy" />
                                    )}
                                    <div className="flex-1 min-w-0">
                                      <div className="flex items-center justify-between gap-2">
                                        <p className="font-semibold text-sm truncate">
                                          {first.name}
                                          {first.brand && <span className="text-muted-foreground font-normal text-xs"> ({first.brand})</span>}
                                        </p>
                                        {isOff
                                          ? <Badge className="bg-blue-500/10 text-blue-400 border-blue-500/30 text-xs shrink-0">🇵🇹</Badge>
                                          : <Badge className="bg-green-500/10 text-green-400 border-green-500/30 text-xs shrink-0">{first.food_type}</Badge>
                                        }
                                      </div>
                                      <p className="text-xs text-muted-foreground">
                                        {first.serving_hint && `${first.serving_hint} · `}{first.calories} kcal · P:{first.protein}g C:{first.carbs}g F:{first.fat}g
                                      </p>
                                    </div>
                                  </div>
                                </div>
                              );
                            })()}

                            {/* Other Results */}
                            {mergedResults.length > 1 && (
                              <p className="text-xs text-muted-foreground px-2 py-1 uppercase tracking-wide font-semibold mt-2 flex items-center gap-1">
                                <span className="inline-block w-2 h-2 rounded-full bg-green-500" /> {t('diet.moreResults')}
                              </p>
                            )}
                          </>
                        )}
                        {(communityResults.length > 0 ? mergedResults : mergedResults.slice(1)).map(fs => {
                          const isOff = fs.source === 'openfoodfacts';
                          const itemKey = isOff ? `off-${fs.off_id}` : `fs-${fs.fatsecret_id}`;
                          const isSelected = isOff
                            ? selectedFood?.off_id === fs.off_id
                            : selectedFood?.fatsecret_id === fs.fatsecret_id;
                          return (
                            <div
                              key={itemKey}
                              className={`p-2.5 rounded-lg cursor-pointer transition-colors ${isSelected ? 'bg-primary/10 border border-primary' : 'bg-muted/30 hover:bg-muted/60'}`}
                              onClick={() => isOff ? selectOpenFoodFactsFood(fs) : selectFatsecretFood(fs)}
                              data-testid={itemKey}
                            >
                              <div className="flex items-center gap-2.5">
                                {fs.image_url && (
                                  <img src={fs.image_url_small || fs.image_url} alt="" className="w-9 h-9 rounded-lg object-cover shrink-0 border border-border/50" loading="lazy" />
                                )}
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center justify-between gap-2">
                                    <p className="font-medium text-sm truncate">{fs.name} {fs.brand && <span className="text-muted-foreground font-normal">({fs.brand})</span>}</p>
                                    {isOff
                                      ? <Badge className="bg-blue-500/10 text-blue-400 border-blue-500/30 text-xs shrink-0">🇵🇹</Badge>
                                      : <Badge className="bg-green-500/10 text-green-400 border-green-500/30 text-xs shrink-0">{fs.food_type}</Badge>
                                    }
                                  </div>
                                  <p className="text-xs text-muted-foreground">
                                    {fs.serving_hint && `${fs.serving_hint} · `}{fs.calories} kcal · P:{fs.protein}g C:{fs.carbs}g F:{fs.fat}g
                                  </p>
                                  <div className="flex items-center gap-1 mt-0.5 flex-wrap">
                                    {fs.preferences?.map(p => (
                                      <span key={p} className="text-xs px-1.5 py-0.5 rounded-full bg-emerald-500/15 text-emerald-400 font-medium">{p}</span>
                                    ))}
                                    {fs.allergens?.length > 0 && fs.allergens.map(a => (
                                      <span key={a} className="text-xs px-1.5 py-0.5 rounded-full bg-red-500/15 text-red-400 font-medium">{a}</span>
                                    ))}
                                    {fs.sub_categories?.length > 0 && (
                                      <span className="text-xs text-muted-foreground/60">{fs.sub_categories.join(' · ')}</span>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </>
                    )}

                    {(fatsecretLoading || communityLoading) && (
                      <div className="flex items-center justify-center py-6 gap-2 text-muted-foreground">
                        <Loader2 className="h-4 w-4 animate-spin" /> {t('diet.searching')}
                      </div>
                    )}
                    {!fatsecretLoading && !communityLoading && mergedResults.length === 0 && communityResults.length === 0 && (
                      <div className="text-center py-8 text-muted-foreground">
                        <Search className="h-8 w-8 mx-auto mb-2 opacity-25" />
                        <p className="text-sm">{t('diet.noResultsFor', { query: searchQuery })}</p>
                        <p className="text-xs mt-1">{t('diet.tryDifferentSearch')}</p>
                      </div>
                    )}

                    {/* "Can't find food?" prompt */}
                    {searchQuery.length >= 2 && !fatsecretLoading && !communityLoading &&
                      communityResults.length === 0 && mergedResults.length <= 3 && (
                      <div className="text-center py-4">
                        <p className="text-sm text-muted-foreground mb-2">
                          {t('diet.cantFindFood', "Can't find what you're looking for?")}
                        </p>
                        <button
                          className="px-4 py-2 text-sm rounded-lg border border-[#FF5C00]/50 text-[#FF5C00] hover:bg-[#FF5C00]/10 transition-colors"
                          onClick={() => { addFoodDialog.close(); setShowCustomFood(true); }}
                        >
                          {t('diet.addYourOwnFood')}
                        </button>
                      </div>
                    )}
                  </>
                ) : selectedCategory !== 'all' ? (
                  // Browse the full catalogue by category
                  <div className="space-y-1" data-testid="catalogue-browse">
                    {catalogueLoading && (
                      <div className="flex items-center justify-center py-6 gap-2 text-muted-foreground">
                        <Loader2 className="h-4 w-4 animate-spin" /> {t('common.loading')}
                      </div>
                    )}
                    {!catalogueLoading && catalogueFoods.length === 0 && (
                      <div className="text-center py-8 text-muted-foreground">
                        <UtensilsCrossed className="h-8 w-8 mx-auto mb-2 opacity-25" />
                        <p className="text-sm">{t('diet.noFoodsInCategory')}</p>
                      </div>
                    )}
                    {!catalogueLoading && catalogueFoods.map(food => (
                      <div
                        key={food.id}
                        className={`p-2.5 rounded-lg cursor-pointer transition-colors ${selectedFood?.id === food.id && !selectedFood?._fromHistory ? 'bg-primary/10 border border-primary' : 'bg-muted/30 hover:bg-muted/60'}`}
                        onClick={() => { setSelectedFood(food); setFatsecretDetail(null); setSelectedServing(null); setQuantity(1); }}
                        data-testid={`catalogue-food-${food.id}`}
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-medium text-sm">{food.name}</p>
                            <p className="text-xs text-muted-foreground">
                              {food.serving_description || (food.serving_size != null && food.serving_unit ? `${food.serving_size}${food.serving_unit}` : 'serving')} · {food.calories} kcal · P:{food.protein}g C:{food.carbs}g F:{food.fat}g
                            </p>
                          </div>
                          {food.source === 'fatsecret' && <Badge className="bg-green-500/10 text-green-400 border-green-500/30 text-xs">FatSecret</Badge>}
                          {food.brand && <Badge variant="outline" className="text-xs">{food.brand}</Badge>}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-10 text-muted-foreground">
                    <Search className="h-10 w-10 mx-auto mb-3 opacity-25" />
                    <p className="text-sm font-medium">{t('diet.searchMillionsFoods')}</p>
                    <p className="text-xs mt-1">{t('diet.poweredByFatSecret')}</p>
                    <p className="text-xs mt-3 opacity-70">{t('diet.pickCategoryAbove')}</p>
                  </div>
                )}
              </div>
            )}

            {/* ---- PHOTO SCAN TAB (FatSecret Image Recognition) ---- */}
            {addFoodTab === 'photo' && (
              <div className="space-y-4" data-testid="photo-tab-content">
                {!photoResult && !photoLoading && (
                  <div className="flex flex-col items-center justify-center py-8 gap-4">
                    <div className="w-24 h-24 rounded-full bg-primary/10 flex items-center justify-center">
                      <Camera className="h-12 w-12 text-primary" />
                    </div>
                    <div className="text-center">
                      <p className="font-medium">{t('diet.takeOrUploadPhoto')}</p>
                      <p className="text-sm text-muted-foreground mt-1">{t('diet.aiIdentifyFoods')}</p>
                    </div>
                    <div className="flex gap-3">
                      <label htmlFor="meal-photo-camera" className="cursor-pointer">
                        <div className="flex items-center gap-2 px-5 py-2.5 bg-primary hover:bg-primary/90 text-white rounded-lg font-medium text-base md:text-sm transition-colors" data-testid="photo-camera-btn">
                          <Camera className="h-4 w-4" /> {t('diet.camera')}
                        </div>
                        <input id="meal-photo-camera" type="file" accept="image/*" capture="environment" className="sr-only" onChange={handlePhotoScan} data-testid="meal-photo-input" />
                      </label>
                      <label htmlFor="meal-photo-gallery" className="cursor-pointer">
                        <div className="flex items-center gap-2 px-5 py-2.5 border border-primary text-primary hover:bg-primary/90 rounded-lg font-medium text-base md:text-sm transition-colors" data-testid="photo-gallery-btn">
                          <ImagePlus className="h-4 w-4" /> {t('diet.gallery')}
                        </div>
                        <input id="meal-photo-gallery" type="file" accept="image/*" className="sr-only" onChange={handlePhotoScan} data-testid="meal-gallery-input" />
                      </label>
                    </div>
                  </div>
                )}
                {photoLoading && (
                  <div className="flex flex-col items-center justify-center py-12 gap-3">
                    <Loader2 className="h-10 w-10 animate-spin text-primary" />
                    <p className="text-sm font-medium">{t('diet.analyzingMeal')}</p>
                    <p className="text-xs text-muted-foreground">{t('diet.identifyingFoods')}</p>
                  </div>
                )}

                {/* AI Vision — per-item review (v3.8.55) */}
                {photoResult && !photoLoading && photoItems.length > 0 && (() => {
                  const selectedCount = photoItems.filter(it => it.selected).length;
                  const totals = photoItems.reduce((acc, it) => {
                    if (!it.selected) return acc;
                    const scale = it.originalGrams > 0 ? it.grams / it.originalGrams : 1;
                    return {
                      calories: acc.calories + it.calories * scale,
                      protein: acc.protein + it.protein * scale,
                      carbs: acc.carbs + it.carbs * scale,
                      fat: acc.fat + it.fat * scale,
                    };
                  }, { calories: 0, protein: 0, carbs: 0, fat: 0 });
                  return (
                    <div className="space-y-3">
                      {/* Header */}
                      <div className="flex items-start justify-between">
                        <div className="flex-1 min-w-0">
                          <p className="font-bold text-base leading-tight">
                            {t(photoItems.length === 1 ? 'diet.foodsDetected' : 'diet.foodsDetectedPlural', { count: photoItems.length })}
                          </p>
                          <p className="text-xs text-muted-foreground mt-0.5">{t('diet.photoReviewHint')}</p>
                        </div>
                        <Badge className="bg-purple-500/10 text-purple-600 border-purple-500/30 text-xs shrink-0 ml-2" variant="outline">
                          {t('diet.aiVision')}
                        </Badge>
                      </div>

                      {/* Selected-items macro totals */}
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                        <div className="rounded-xl p-3 bg-primary/10 text-center">
                          <p className="text-lg font-black text-primary">{Math.round(totals.calories)}</p>
                          <p className="text-xs text-muted-foreground font-medium uppercase tracking-wide">kcal</p>
                        </div>
                        <div className="rounded-xl p-3 bg-red-500/10 text-center">
                          <p className="text-lg font-black text-red-500">{Math.round(totals.protein)}g</p>
                          <p className="text-xs text-muted-foreground font-medium uppercase tracking-wide">{t('diet.protein')}</p>
                        </div>
                        <div className="rounded-xl p-3 bg-yellow-500/10 text-center">
                          <p className="text-lg font-black text-yellow-500">{Math.round(totals.carbs)}g</p>
                          <p className="text-xs text-muted-foreground font-medium uppercase tracking-wide">{t('diet.carbs')}</p>
                        </div>
                        <div className="rounded-xl p-3 bg-blue-500/10 text-center">
                          <p className="text-lg font-black text-blue-500">{Math.round(totals.fat)}g</p>
                          <p className="text-xs text-muted-foreground font-medium uppercase tracking-wide">{t('diet.fat')}</p>
                        </div>
                      </div>

                      {/* Per-item rows: checkbox + editable grams + live scaled macros */}
                      <div className="rounded-xl border border-border/50 overflow-hidden">
                        <div className="px-3 py-2 bg-muted/30 border-b border-border/50">
                          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t('diet.detectedFoods')}</p>
                        </div>
                        <div className="divide-y divide-border/30">
                          {photoItems.map((item, i) => {
                            const scale = item.originalGrams > 0 ? item.grams / item.originalGrams : 1;
                            const kcal = Math.round(item.calories * scale);
                            const p = Math.round(item.protein * scale);
                            const c = Math.round(item.carbs * scale);
                            const f = Math.round(item.fat * scale);
                            return (
                              <div key={i} className={`px-3 py-2.5 ${item.selected ? '' : 'opacity-50'}`} data-testid={`photo-item-${i}`}>
                                <div className="flex items-start gap-2">
                                  <input
                                    type="checkbox"
                                    checked={item.selected}
                                    onChange={() => togglePhotoItem(i)}
                                    className="mt-1 h-4 w-4 accent-primary cursor-pointer shrink-0"
                                    data-testid={`photo-item-check-${i}`}
                                    aria-label={item.name}
                                  />
                                  <div className="flex-1 min-w-0">
                                    <p className="text-base md:text-sm font-medium leading-snug">{item.name}</p>
                                    <div className="flex items-center gap-2 mt-1 flex-wrap">
                                      <input
                                        type="number"
                                        inputMode="numeric"
                                        value={item.grams}
                                        min={0}
                                        onChange={(e) => updatePhotoItemGrams(i, e.target.value)}
                                        disabled={!item.selected}
                                        className="w-16 h-11 md:h-7 px-2 text-base md:text-xs text-right rounded border border-border bg-background disabled:cursor-not-allowed"
                                        data-testid={`photo-item-grams-${i}`}
                                      />
                                      <span className="text-xs text-muted-foreground">{item.unit}</span>
                                      <span className="text-xs text-muted-foreground ml-auto">
                                        {kcal} kcal · P:{p}g C:{c}g F:{f}g
                                      </span>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>

                      {/* AI notes (portion reasoning, cooking method caveats) */}
                      {photoNotes && (
                        <p className="text-[11px] text-muted-foreground italic px-1 leading-snug">{photoNotes}</p>
                      )}

                      {/* Actions */}
                      <div className="flex gap-2 pt-1">
                        <label htmlFor="meal-photo-retry-cam" className="flex-1 cursor-pointer">
                          <div className="flex items-center justify-center gap-2 px-4 py-2.5 border border-border rounded-lg text-base md:text-sm font-medium hover:bg-muted active:bg-muted transition-colors">
                            <Camera className="h-4 w-4" /> {t('diet.camera')}
                          </div>
                          <input id="meal-photo-retry-cam" type="file" accept="image/*" capture="environment" className="sr-only" onChange={handlePhotoScan} />
                        </label>
                        <label htmlFor="meal-photo-retry-gal" className="flex-1 cursor-pointer">
                          <div className="flex items-center justify-center gap-2 px-4 py-2.5 border border-border rounded-lg text-base md:text-sm font-medium hover:bg-muted active:bg-muted transition-colors">
                            <ImagePlus className="h-4 w-4" /> {t('diet.gallery')}
                          </div>
                          <input id="meal-photo-retry-gal" type="file" accept="image/*" className="sr-only" onChange={handlePhotoScan} />
                        </label>
                        <Button
                          className="flex-1 bg-primary hover:bg-primary/90 font-semibold"
                          onClick={addPhotoItemsToLog}
                          disabled={selectedCount === 0 || addFoodToLogMutation.isPending}
                          data-testid="add-photo-items-btn"
                        >
                          <Plus className="h-4 w-4 mr-1" />
                          {t(selectedCount === 1 ? 'diet.logOneItem' : 'diet.logNItems', { count: selectedCount })}
                        </Button>
                      </div>
                    </div>
                  );
                })()}
              </div>
            )}

            {/* ---- SCAN TAB ---- */}
            {addFoodTab === 'scan' && (
              <div className="flex flex-col items-center justify-center py-10 text-center space-y-5" data-testid="scan-tab-content">
                <div className="relative">
                  <div className="w-24 h-24 rounded-full bg-primary/10 flex items-center justify-center">
                    <Scan className="h-12 w-12 text-primary" />
                  </div>
                </div>
                <div className="space-y-1">
                  <h3 className="text-base font-semibold">{t('diet.scanBarcode')}</h3>
                  <p className="text-xs text-muted-foreground max-w-sm">{t('diet.scannerInstructions')}</p>
                </div>
                <Button
                  onClick={() => { addFoodDialog.close(); setShowBarcodeScanner(true); }}
                  className="bg-primary hover:bg-primary/90"
                  data-testid="open-camera-btn"
                >
                  <Camera className="mr-2 h-4 w-4" /> {t('diet.scanBarcode')}
                </Button>
              </div>
            )}

            {/* ---- RECIPES TAB (FatSecret Premier) ---- */}
            {addFoodTab === 'recipes' && (
              <div className="space-y-3" data-testid="recipes-tab-content">
                {!selectedRecipe ? (
                  <>
                    {/* Recipe Search */}
                    <div className="flex gap-2">
                      <Input
                        value={recipeQuery}
                        onChange={(e) => setRecipeQuery(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') searchRecipes(recipeQuery); }}
                        placeholder={t('diet.searchRecipesPlaceholder')}
                        className="h-9 text-sm"
                        data-testid="recipe-search-input"
                      />
                      <Button size="sm" onClick={() => searchRecipes(recipeQuery)} disabled={recipeLoading} className="bg-primary hover:bg-primary/90 h-9 px-4" data-testid="recipe-search-btn">
                        {recipeLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
                      </Button>
                    </div>

                    {/* Recipe Results */}
                    {recipeResults.length > 0 ? (
                      <div className="space-y-2">
                        {recipeResults.map(recipe => (
                          <div
                            key={recipe.recipe_id}
                            className="p-3 rounded-lg bg-muted/30 hover:bg-muted/60 cursor-pointer transition-colors border border-border/30"
                            onClick={() => openRecipeDetail(recipe)}
                            data-testid={`recipe-item-${recipe.recipe_id}`}
                          >
                            <div className="flex gap-3">
                              {recipe.image && (
                                <img src={recipe.image} alt="" className="w-16 h-16 rounded-lg object-cover shrink-0" loading="lazy" />
                              )}
                              <div className="flex-1 min-w-0">
                                <p className="font-semibold text-sm truncate">{recipe.name}</p>
                                <p className="text-xs text-muted-foreground line-clamp-2 mt-0.5">{recipe.description}</p>
                                <div className="flex items-center gap-3 mt-1.5 text-xs">
                                  <span className="font-medium text-primary">{Math.round(recipe.calories)} kcal</span>
                                  <span>P:{Math.round(recipe.protein)}g</span>
                                  <span>C:{Math.round(recipe.carbs)}g</span>
                                  <span>F:{Math.round(recipe.fat)}g</span>
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : !recipeLoading ? (
                      <div className="text-center py-10 text-muted-foreground">
                        <BookOpen className="h-10 w-10 mx-auto mb-3 opacity-25" />
                        <p className="text-sm font-medium">{t('diet.searchThousandsRecipes')}</p>
                        <p className="text-xs mt-1">{t('diet.findHealthyMeals')}</p>
                      </div>
                    ) : null}
                    {recipeLoading && (
                      <div className="flex items-center justify-center py-8 gap-2 text-muted-foreground">
                        <Loader2 className="h-4 w-4 animate-spin" /> {t('diet.searchingRecipes')}
                      </div>
                    )}
                  </>
                ) : (
                  /* Recipe Detail View */
                  <div className="space-y-3">
                    <button
                      className="flex items-center gap-1 text-sm text-primary hover:underline"
                      onClick={() => { setSelectedRecipe(null); setRecipeDetail(null); }}
                      data-testid="back-to-recipes"
                    >
                      <ChevronLeft className="h-4 w-4" /> {t('diet.backToResults')}
                    </button>

                    {recipeDetailLoading ? (
                      <div className="flex items-center justify-center py-8 gap-2 text-muted-foreground">
                        <Loader2 className="h-4 w-4 animate-spin" /> {t('diet.loadingRecipe')}
                      </div>
                    ) : recipeDetail ? (
                      <div className="space-y-3">
                        <div className="flex gap-3">
                          {recipeDetail.image && (
                            <img src={recipeDetail.image} alt="" className="w-20 h-20 rounded-xl object-cover shrink-0" />
                          )}
                          <div>
                            <p className="font-bold text-base">{recipeDetail.name}</p>
                            <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{recipeDetail.description}</p>
                            <div className="flex gap-3 mt-1 text-xs text-muted-foreground">
                              {recipeDetail.prep_time && <span>{t('diet.recipePrep', { time: recipeDetail.prep_time })}</span>}
                              {recipeDetail.cook_time && <span>{t('diet.recipeCook', { time: recipeDetail.cook_time })}</span>}
                              {recipeDetail.servings && <span>{t('diet.recipeServes', { count: recipeDetail.servings })}</span>}
                            </div>
                          </div>
                        </div>

                        {/* Nutrition */}
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                          <div className="rounded-xl p-2 bg-primary/10 text-center">
                            <p className="text-sm font-bold text-primary">{Math.round(recipeDetail.nutrition?.calories || 0)}</p>
                            <p className="text-xs text-muted-foreground uppercase">kcal</p>
                          </div>
                          <div className="rounded-xl p-2 bg-red-500/10 text-center">
                            <p className="text-sm font-bold text-red-500">{Math.round(recipeDetail.nutrition?.protein || 0)}g</p>
                            <p className="text-xs text-muted-foreground uppercase">{t('diet.protein')}</p>
                          </div>
                          <div className="rounded-xl p-2 bg-yellow-500/10 text-center">
                            <p className="text-sm font-bold text-yellow-500">{Math.round(recipeDetail.nutrition?.carbs || 0)}g</p>
                            <p className="text-xs text-muted-foreground uppercase">{t('diet.carbs')}</p>
                          </div>
                          <div className="rounded-xl p-2 bg-blue-500/10 text-center">
                            <p className="text-sm font-bold text-blue-500">{Math.round(recipeDetail.nutrition?.fat || 0)}g</p>
                            <p className="text-xs text-muted-foreground uppercase">{t('diet.fat')}</p>
                          </div>
                        </div>

                        {/* Ingredients */}
                        {recipeDetail.ingredients?.length > 0 && (
                          <div className="rounded-xl border border-border/50 overflow-hidden">
                            <div className="px-3 py-2 bg-muted/30 border-b border-border/50">
                              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t('diet.ingredients')}</p>
                            </div>
                            <div className="divide-y divide-border/30">
                              {recipeDetail.ingredients.map((ing, i) => (
                                <div key={i} className="px-3 py-2 text-xs">
                                  {ing.ingredient_description || `${ing.number_of_units || ''} ${ing.measurement_description || ''} ${ing.food_name}`.trim()}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Directions */}
                        {recipeDetail.directions?.length > 0 && (
                          <div className="rounded-xl border border-border/50 overflow-hidden">
                            <div className="px-3 py-2 bg-muted/30 border-b border-border/50">
                              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t('diet.directions')}</p>
                            </div>
                            <div className="divide-y divide-border/30">
                              {recipeDetail.directions.map((step, i) => (
                                <div key={i} className="px-3 py-2 text-xs flex gap-2">
                                  <span className="font-bold text-primary shrink-0">{step.number}.</span>
                                  <span>{step.description}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Link to FatSecret */}
                        {recipeDetail.recipe_url && (
                          <a href={recipeDetail.recipe_url} target="_blank" rel="noopener noreferrer" className="text-xs text-primary hover:underline flex items-center gap-1">
                            {t('diet.viewFullRecipe')}
                          </a>
                        )}
                      </div>
                    ) : null}
                  </div>
                )}
              </div>
            )}


            {/* ---- QUICK MEALS TAB ---- */}
            {addFoodTab === 'quick' && (
              <div data-testid="quick-tab-content">
                <QuickAddMealCard onLog={handleQuickAdd} />
                {mealTemplates.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-10 text-center">
                    <Bookmark className="h-10 w-10 text-muted-foreground/30 mb-3" />
                    <p className="font-medium text-muted-foreground text-sm">{t('diet.noQuickMeals')}</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {t('diet.quickMealSaveHint').split('<')[0]} <BookmarkPlus className="inline h-4 w-4" /> {t('diet.quickMealSaveHint').split('>').pop()}
                    </p>
                  </div>
                ) : (
                  <div className="space-y-2 pb-2">
                    {mealTemplates.map(template => (
                      <Card key={template.id} className="border-border/50 hover:border-primary/50 transition-colors" data-testid={`quick-meal-${template.id}`}>
                        <CardContent className="p-3">
                          <div className="flex items-start justify-between mb-1.5">
                            <div>
                              <div className="flex items-center gap-1.5">
                                <Zap className="h-3.5 w-3.5 text-primary" />
                                <span className="font-semibold text-sm">{template.name}</span>
                              </div>
                              <p className="text-xs text-muted-foreground mt-0.5">
                                {MEAL_TYPES.find(m => m.value === template.meal_type)?.icon} {MEAL_TYPES.find(m => m.value === template.meal_type)?.label} - {template.foods.length} item{template.foods.length !== 1 ? 's' : ''}
                              </p>
                            </div>
                            <Badge className="bg-primary/10 text-primary border-primary/30 text-xs">
                              {Math.round(template.total_calories)} kcal
                            </Badge>
                          </div>
                          <div className="space-y-0.5 mb-2">
                            {template.foods.map((food, idx) => (
                              <div key={idx} className="flex justify-between text-xs text-muted-foreground">
                                <span>{food.food_name} x{food.quantity}</span>
                                <span>{Math.round(food.calories * food.quantity)} kcal</span>
                              </div>
                            ))}
                          </div>
                          <div className="flex items-center justify-between text-xs text-muted-foreground mb-2 pt-1.5 border-t border-border/50">
                            <span>P: {Math.round(template.total_protein)}g</span>
                            <span>C: {Math.round(template.total_carbs)}g</span>
                            <span>F: {Math.round(template.total_fat)}g</span>
                          </div>
                          <div className="flex gap-2">
                              <Button size="sm" className="flex-1 bg-primary hover:bg-primary/90 h-11 md:h-8 text-xs" onClick={() => handleLogTemplate(template, selectedMealType)} data-testid={`log-quick-meal-${template.id}`}>
                                <Plus className="h-3 w-3 mr-1" /> {t('diet.logThisMeal')}
                              </Button>
                            <Button size="sm" variant="ghost" className="h-10 md:h-8" tooltip={t('diet.editQuickMeal.edit')} aria-label={t('diet.editQuickMeal.edit')} onClick={() => openEditTemplate(template)} data-testid={`edit-quick-meal-${template.id}`}>
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button size="sm" variant="ghost" className="text-destructive hover:text-destructive h-10 md:h-8" onClick={() => handleDeleteTemplate(template.id)} data-testid={`delete-quick-meal-${template.id}`}>
                              <X className="h-4 w-4" />
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </div>
            )}

          </div>

          {/* Selected Food Panel — Fixed at Bottom */}
          {selectedFood && (
            <div className="px-4 py-2 border-t border-border/50 shrink-0 space-y-2 bg-background">
              <div className="flex items-center justify-between">
                <div className="min-w-0 flex-1">
                  <p className="font-semibold text-sm truncate">{selectedFood.name}</p>
                  <p className="text-xs text-muted-foreground">
                    {selectedFood.brand ? `${selectedFood.brand} · ` : ''}
                    1 serving = {selectedServing?.metric_serving_amount ? `${selectedServing.metric_serving_amount}${selectedServing.metric_serving_unit || 'g'}` : `${selectedFood.serving_size || 100}${selectedFood.serving_unit || 'g'}`}
                  </p>
                </div>
                <Button variant="ghost" size="sm" className="text-xs h-11 md:h-8 px-2 text-primary shrink-0" onClick={() => setShowNutritionDetail(!showNutritionDetail)} data-testid="toggle-nutrition-btn">
                  {showNutritionDetail ? t('common.less') : t('common.more')}
                </Button>
              </div>

              {/* Serving Size Picker + Per 100g Toggle */}
              {selectedFood.servings?.length > 1 && (
                <div className="flex items-center gap-1.5">
                  <Select value={selectedServing?.serving_id || ''} onValueChange={handleServingChange} className="flex-1">
                    <SelectTrigger className="text-xs flex-1" data-testid="serving-size-select">
                      <SelectValue placeholder={t('diet.selectServing')} />
                    </SelectTrigger>
                    <SelectContent>
                      {selectedFood.servings.map(s => (
                        <SelectItem key={s.serving_id} value={s.serving_id} className="text-xs">
                          {s.description} ({s.calories} kcal)
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {selectedFood.servings.some(s => s.metric_amount === 100 && s.metric_unit === 'g') && (
                    <Button
                      variant={selectedServing?.metric_amount === 100 && selectedServing?.metric_unit === 'g' ? 'default' : 'outline'}
                      size="sm"
                      className={`h-8 px-2 text-xs shrink-0 ${selectedServing?.metric_amount === 100 && selectedServing?.metric_unit === 'g' ? 'bg-primary hover:bg-primary/90 text-white' : ''}`}
                      data-testid="per-100g-toggle"
                      onClick={() => {
                        const per100g = selectedFood.servings.find(s => s.metric_amount === 100 && s.metric_unit === 'g');
                        if (per100g) handleServingChange(per100g.serving_id);
                      }}
                    >
                      /100g
                    </Button>
                  )}
                </div>
              )}

              {/* Quantity + Total Weight + Macros */}
              <div className="flex items-center gap-2">
                <div className="flex items-center gap-1 shrink-0">
                  <Button variant="outline" size="icon" className="h-11 w-11 md:h-8 md:w-8" onClick={() => setQuantity(Math.max(0.5, quantity - 0.5))}>-</Button>
                  <Input
                    type="text"
                    inputMode="decimal"
                    value={quantityInput}
                    onChange={(e) => {
                      const raw = e.target.value;
                      // Only allow digits, one comma/dot, nothing else
                      if (!/^[0-9]*[.,]?[0-9]*$/.test(raw)) return;
                      setQuantityInput(raw);
                      const n = parseFloat(raw.replace(',', '.'));
                      if (!isNaN(n) && n >= 0) setQuantity(n);
                    }}
                    onBlur={() => {
                      const n = parseFloat(String(quantityInput).replace(',', '.'));
                      if (isNaN(n) || n < 0.01) {
                        setQuantity(0.01);
                        setQuantityInput('0.01');
                      } else {
                        setQuantityInput(String(n));
                      }
                    }}
                    onFocus={(e) => e.target.select()}
                    className="w-14 h-11 md:h-8 text-center text-sm font-medium px-1 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                    data-testid="quantity-input"
                  />
                  <Button variant="outline" size="icon" className="h-11 w-11 md:h-8 md:w-8" onClick={() => setQuantity((parseFloat(quantity) || 0) + 0.5)}>+</Button>
                </div>
                {/* Total weight indicator */}
                <span className="text-xs text-muted-foreground shrink-0 bg-muted/50 px-2 py-0.5 rounded" data-testid="total-weight-display">
                  = {(() => {
                    const servingSize = selectedServing?.metric_serving_amount || selectedFood.serving_size || 100;
                    const servingUnit = selectedServing?.metric_serving_unit || selectedFood.serving_unit || 'g';
                    return `${Math.round(servingSize * quantity * 10) / 10}${servingUnit}`;
                  })()}
                </span>
              </div>
              {/* Macros row */}
              <div className="flex gap-2 text-xs justify-between">
                <span className="font-bold text-primary">{Math.round(selectedFood.calories * quantity)} kcal</span>
                <span className="text-red-500">P: {(selectedFood.protein * quantity).toFixed(1)}g</span>
                <span className="text-yellow-500">C: {(selectedFood.carbs * quantity).toFixed(1)}g</span>
                <span className="text-blue-500">F: {(selectedFood.fat * quantity).toFixed(1)}g</span>
              </div>

              {/* Detailed Nutrition Panel */}
              {showNutritionDetail && (
                <div className="border-t border-border/50 pt-2 space-y-1 text-xs">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-0.5">
                    {selectedFood.saturated_fat != null && <div className="flex justify-between"><span className="text-muted-foreground">{t('diet.saturatedFat')}</span><span>{(selectedFood.saturated_fat * quantity).toFixed(1)}g</span></div>}
                    {selectedFood.fiber != null && <div className="flex justify-between"><span className="text-muted-foreground">{t('diet.fiber')}</span><span>{(selectedFood.fiber * quantity).toFixed(1)}g</span></div>}
                    {selectedFood.sugar != null && <div className="flex justify-between"><span className="text-muted-foreground">{t('diet.sugar')}</span><span>{(selectedFood.sugar * quantity).toFixed(1)}g</span></div>}
                    {selectedFood.cholesterol != null && <div className="flex justify-between"><span className="text-muted-foreground">{t('diet.cholesterol')}</span><span>{Math.round(selectedFood.cholesterol * quantity)}mg</span></div>}
                    {selectedFood.sodium != null && <div className="flex justify-between"><span className="text-muted-foreground">{t('diet.sodium')}</span><span>{Math.round(selectedFood.sodium * quantity)}mg</span></div>}
                    {selectedFood.potassium != null && <div className="flex justify-between"><span className="text-muted-foreground">{t('diet.potassium')}</span><span>{Math.round(selectedFood.potassium * quantity)}mg</span></div>}
                  </div>
                </div>
              )}

              {/* Action Button — Add to Queue */}
              <Button
                onClick={addFoodToQueue}
                disabled={!selectedFood}
                className="w-full bg-primary hover:bg-primary/90 h-9"
                data-testid="add-to-queue-btn"
              >
                <Plus className="h-4 w-4 mr-1" /> {t('diet.addToQueue')}
              </Button>
            </div>
          )}

          {/* Pending Foods Queue — Fixed at very bottom */}
          {pendingFoods.length > 0 && (
            <div className="px-4 py-2 border-t-2 border-primary/30 shrink-0 bg-primary/10">
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-xs font-semibold text-primary">{t('diet.queueLabel', { count: pendingFoods.length })}</span>
                <span className="text-xs text-muted-foreground">
                  {Math.round(pendingFoods.reduce((s, f) => s + f.calories, 0))} kcal · P:{Math.round(pendingFoods.reduce((s, f) => s + f.protein, 0))}g · C:{Math.round(pendingFoods.reduce((s, f) => s + f.carbs, 0))}g · F:{Math.round(pendingFoods.reduce((s, f) => s + f.fat, 0))}g
                </span>
              </div>
              <div className="space-y-1 max-h-[100px] overflow-auto mb-2">
                {pendingFoods.map((pf, idx) => (
                  <div key={idx} className="flex items-center justify-between text-xs bg-background rounded px-2 py-1">
                    <div className="min-w-0 flex-1">
                      <span className="font-medium truncate block">{pf.food_name}</span>
                      <span className="text-xs text-muted-foreground">{pf.servings}x · {Math.round(pf.calories)}kcal · {MEAL_TYPES.find(m => m.value === pf.meal_type)?.icon}</span>
                    </div>
                    <Button variant="ghost" size="icon" className="h-11 w-11 md:h-8 md:w-8 shrink-0 text-destructive" onClick={() => removePendingFood(idx)} data-testid={`remove-queue-${idx}`} tooltip={t('tooltips.remove')}>
                      <X className="h-3 w-3" />
                    </Button>
                  </div>
                ))}
              </div>
              <Button
                onClick={saveAllPendingFoods}
                className="w-full bg-primary hover:bg-primary/90 h-9 font-semibold"
                data-testid="save-all-foods-btn"
              >
                <Check className="h-4 w-4 mr-1" /> {pendingFoods.length > 1 ? t('diet.saveAllBtnPlural', { count: pendingFoods.length }) : t('diet.saveAllBtn', { count: pendingFoods.length })}
              </Button>
            </div>
          )}
      </FullScreenPage>

      {/* ==================== MOVE / COPY MEAL DIALOG ==================== */}
      <Dialog open={showMoveMealDialog} onOpenChange={setShowMoveMealDialog}>
        <DialogContent className="max-w-sm" compact>
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
              {t('diet.moveCopyMealTitle', { meal: MEAL_TYPES.find(m => m.value === moveMealSource)?.label || '' })}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            {/* Action toggle */}
            <div className="flex items-center gap-2 p-1 bg-muted/50 rounded-lg">
              <button
                type="button"
                onClick={() => setMoveMealAction('move')}
                className={`flex-1 h-9 rounded-md text-sm font-medium transition-colors ${moveMealAction === 'move' ? 'bg-primary text-primary-foreground shadow' : 'text-muted-foreground hover:text-foreground'}`}
                data-testid="move-action-move"
              >
                {t('diet.moveAction')}
              </button>
              <button
                type="button"
                onClick={() => setMoveMealAction('copy')}
                className={`flex-1 h-9 rounded-md text-sm font-medium transition-colors ${moveMealAction === 'copy' ? 'bg-primary text-primary-foreground shadow' : 'text-muted-foreground hover:text-foreground'}`}
                data-testid="move-action-copy"
              >
                {t('diet.copyAction')}
              </button>
            </div>

            {/* Target meal list */}
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-wide text-muted-foreground">{t('diet.selectTargetMeal')}</Label>
              <div className="space-y-1.5">
                {MEAL_TYPES.filter(m => m.value !== moveMealSource).map(mt => {
                  const targetCal = (foodLog?.entries || [])
                    .filter(e => e.meal_type === mt.value)
                    .reduce((s, e) => s + (e.calories || 0), 0);
                  const isSelected = moveMealTarget === mt.value;
                  return (
                    <button
                      key={mt.value}
                      type="button"
                      onClick={() => setMoveMealTarget(mt.value)}
                      className={`w-full flex items-center justify-between min-h-[48px] px-3 rounded-lg border transition-colors ${isSelected ? 'border-primary bg-primary/10' : 'border-border/50 bg-muted/30 hover:bg-muted/60'}`}
                      data-testid={`move-target-${mt.value}`}
                    >
                      <div className="flex items-center gap-2">
                        <span className="text-lg">{mt.icon}</span>
                        <span className="font-medium text-sm">{mt.label}</span>
                      </div>
                      <span className="text-xs text-muted-foreground">{Math.round(targetCal)} kcal</span>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
          <DialogFooter className="flex-row gap-2">
            <Button variant="outline" className="flex-1" onClick={() => setShowMoveMealDialog(false)}>
              {t('common.cancel')}
            </Button>
            <Button
              className="flex-1 bg-primary hover:bg-primary/90"
              disabled={!moveMealTarget || moveMealMutation.isLoading}
              onClick={() => moveMealMutation.mutate({ source: moveMealSource, target: moveMealTarget, action: moveMealAction })}
              data-testid="move-meal-confirm"
            >
              {moveMealMutation.isLoading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                moveMealTarget
                  ? (moveMealAction === 'move'
                      ? t('diet.moveToMeal', { meal: MEAL_TYPES.find(m => m.value === moveMealTarget)?.label })
                      : t('diet.copyToMeal', { meal: MEAL_TYPES.find(m => m.value === moveMealTarget)?.label }))
                  : (moveMealAction === 'move' ? t('diet.moveAction') : t('diet.copyAction'))
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ==================== EDIT QUICK MEAL MODAL ==================== */}
      <EditQuickMealModal
        open={showEditQuickMeal}
        template={editingTemplate}
        onClose={closeEditTemplate}
        onSaved={() => queryClient.invalidateQueries({ queryKey: ['mealTemplates'] })}
      />

      {/* ==================== SAVE MEAL TEMPLATE DIALOG ==================== */}
      <Dialog open={showSaveTemplateDialog} onOpenChange={setShowSaveTemplateDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
              {t('diet.saveQuickMealTitle')}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>{t('diet.mealName')}</Label>
              <Input
                value={templateName}
                onChange={(e) => setTemplateName(e.target.value)}
                placeholder={t('diet.mealTemplatePlaceholder')}
                data-testid="template-name-input"
              />
            </div>
            <div className="p-3 rounded-lg bg-muted/50">
              <p className="text-xs font-medium text-muted-foreground mb-2">
                {t('diet.savingItemsFrom', { count: (foodLog?.entries?.filter(e => e.meal_type === templateMealType) || []).length, meal: MEAL_TYPES.find(m => m.value === templateMealType)?.label })}:
              </p>
              {(foodLog?.entries?.filter(e => e.meal_type === templateMealType) || []).map((entry, idx) => (
                <div key={idx} className="flex justify-between text-xs py-0.5">
                  <span>{entry.food_name} x{entry.quantity}</span>
                  <span className="text-muted-foreground">{Math.round(entry.calories)} kcal</span>
                </div>
              ))}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSaveTemplateDialog(false)}>{t('common.cancel')}</Button>
            <Button
              onClick={handleSaveMealTemplate}
              disabled={!templateName.trim() || saveMealTemplateMutation.isPending}
              className="bg-primary hover:bg-primary/90"
              data-testid="confirm-save-template-btn"
            >
              <Bookmark className="h-4 w-4 mr-1" /> {t('diet.saveQuickMealBtn')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      {/* ==================== NUTRITION INSIGHTS DIALOG ==================== */}
      <FullScreenPage open={showNutritionInsights} onClose={() => insightsDialog.close()} title={t('diet.nutritionInsightsTitle')}>
        <div className="pb-2 border-b border-border/50 shrink-0 mb-3">
          <Tabs value={insightsTab} onValueChange={setInsightsTab} className="w-full">
            <TabsList className="grid w-full grid-cols-3 h-8">
              <TabsTrigger value="calories" className="text-xs" data-testid="insights-calories-tab">{t('diet.caloriesTab')}</TabsTrigger>
              <TabsTrigger value="nutrients" className="text-xs" data-testid="insights-nutrients-tab">{t('diet.nutrientsTab')}</TabsTrigger>
              <TabsTrigger value="macros" className="text-xs" data-testid="insights-macros-tab">{t('diet.macrosTab')}</TabsTrigger>
            </TabsList>
          </Tabs>
        </div>
        <div className="py-3">
            {(() => {
              const entries = foodLog?.entries || [];
              const microEntries = enrichedEntries || entries;
              const target = {
                calories: foodLog?.target_calories || 2000,
                protein: foodLog?.target_protein || 150,
                carbs: foodLog?.target_carbs || 200,
                fat: foodLog?.target_fat || 70,
              };
              const byMeal = {};
              MEAL_TYPES.forEach(mt => { byMeal[mt.value] = { label: mt.label, icon: mt.icon, calories: 0, protein: 0, carbs: 0, fat: 0 }; });
              entries.forEach(e => {
                const m = byMeal[e.meal_type] || byMeal.snack;
                m.calories += e.calories || 0;
                m.protein += e.protein || 0;
                m.carbs += e.carbs || 0;
                m.fat += e.fat || 0;
              });
              const totals = { calories: 0, protein: 0, carbs: 0, fat: 0, fiber: 0, sugar: 0, sat_fat: 0, sodium: 0, cholesterol: 0 };
              microEntries.forEach(e => {
                totals.calories += e.calories || 0;
                totals.protein += e.protein || 0;
                totals.carbs += e.carbs || 0;
                totals.fat += e.fat || 0;
                totals.fiber += e.fiber || 0;
                totals.sugar += e.sugar || 0;
                totals.sat_fat += e.saturated_fat || 0;
                totals.sodium += e.sodium || 0;
                totals.cholesterol += e.cholesterol || 0;
              });
              const mealColors = { breakfast: '#FF5C00', lunch: '#3B82F6', dinner: '#8B5CF6', snack: '#10B981' };

              // Generate day-specific actionable tips from remaining macros
              const remaining = { cal: target.calories - totals.calories, pro: target.protein - totals.protein, carbs: target.carbs - totals.carbs, fat: target.fat - totals.fat };
              const allGoalsMet = remaining.cal >= -100 && remaining.cal <= 150 && remaining.pro >= -5 && remaining.pro <= 10;
              const smartTips = [];
              if (allGoalsMet) {
                smartTips.push({ icon: '🎉', text: t('diet.tipGoalsMet') });
              } else {
                if (remaining.pro > 20) smartTips.push({ icon: '🍗', text: t('diet.tipProteinShort', { g: Math.round(remaining.pro) }) });
                else if (remaining.pro > 5) smartTips.push({ icon: '🥚', text: t('diet.tipProteinAlmost', { g: Math.round(remaining.pro) }) });
                else if (remaining.pro < -10) smartTips.push({ icon: '✅', text: t('diet.tipProteinSmashed', { g: Math.round(-remaining.pro) }) });
                if (remaining.cal > 300) smartTips.push({ icon: '🍽️', text: t('diet.tipCalRemaining', { kcal: Math.round(remaining.cal) }) });
                else if (remaining.cal > 50) smartTips.push({ icon: '🥑', text: t('diet.tipCalLight', { kcal: Math.round(remaining.cal) }) });
                else if (remaining.cal < -150) smartTips.push({ icon: '⚠️', text: t('diet.tipCalOver', { kcal: Math.round(-remaining.cal) }) });
                if (remaining.fat < -15) smartTips.push({ icon: '💧', text: t('diet.tipFatHigh') });
                if (remaining.carbs < -30) smartTips.push({ icon: '🥦', text: t('diet.tipCarbsOver') });
              }

              return (
                <>
                  {/* SMART TIPS */}
                  {smartTips.length > 0 && (
                    <div className="mb-4 space-y-2">
                      {smartTips.map((tip, i) => (
                        <div key={i} className="flex gap-2 items-start px-3 py-2 rounded-lg bg-muted/50 text-sm">
                          <span className="text-base shrink-0">{tip.icon}</span>
                          <span className="text-muted-foreground leading-snug">{tip.text}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  {/* CALORIES TAB */}
                  {insightsTab === 'calories' && (
                    <div className="space-y-4" data-testid="insights-calories-content">
                      {/* Donut Chart */}
                      <div className="relative flex justify-center">
                        <svg viewBox="0 0 160 160" className="w-44 h-44">
                          {(() => {
                            const meals = Object.entries(byMeal).filter(([,v]) => v.calories > 0);
                            const total = Math.max(meals.reduce((s, [,v]) => s + v.calories, 0), 1);
                            let cumAngle = -90;
                            return meals.map(([key, val]) => {
                              const pct = val.calories / total;
                              const angle = pct * 360;
                              const startRad = (cumAngle * Math.PI) / 180;
                              const endRad = ((cumAngle + angle) * Math.PI) / 180;
                              cumAngle += angle;
                              const largeArc = angle > 180 ? 1 : 0;
                              const x1 = 80 + 60 * Math.cos(startRad);
                              const y1 = 80 + 60 * Math.sin(startRad);
                              const x2 = 80 + 60 * Math.cos(endRad);
                              const y2 = 80 + 60 * Math.sin(endRad);
                              return (
                                <path key={key} d={`M80,80 L${x1},${y1} A60,60 0 ${largeArc},1 ${x2},${y2} Z`}
                                  fill={mealColors[key] || '#666'} opacity={0.85} />
                              );
                            });
                          })()}
                          <circle cx="80" cy="80" r="38" fill="hsl(var(--background))" />
                          <text x="80" y="76" textAnchor="middle" className="fill-foreground text-[18px] font-bold">{Math.round(totals.calories)}</text>
                          <text x="80" y="92" textAnchor="middle" className="fill-muted-foreground text-xs">kcal</text>
                        </svg>
                      </div>
                      {/* Legend */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        {Object.entries(byMeal).map(([key, val]) => (
                          <div key={key} className="flex items-center gap-2 text-xs">
                            <div className="w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: mealColors[key] }} />
                            <span className="text-muted-foreground">{val.icon} {val.label}</span>
                            <span className="ml-auto font-semibold">{Math.round(val.calories)}</span>
                          </div>
                        ))}
                      </div>
                      {/* Summary */}
                      <div className="rounded-lg bg-muted/50 p-3 space-y-1.5">
                        <div className="flex justify-between text-sm"><span className="text-muted-foreground">{t('diet.goalLabel')}</span><span className="font-semibold">{target.calories} kcal</span></div>
                        <div className="flex justify-between text-sm"><span className="text-muted-foreground">{t('diet.totalLabel')}</span><span className="font-semibold text-primary">{Math.round(totals.calories)} kcal</span></div>
                        <div className="flex justify-between text-sm"><span className="text-muted-foreground">{t('diet.remainingLabel')}</span><span className={`font-semibold ${target.calories - totals.calories >= 0 ? 'text-green-500' : 'text-red-500'}`}>{Math.round(target.calories - totals.calories)} kcal</span></div>
                      </div>
                    </div>
                  )}

                  {/* NUTRIENTS TAB */}
                  {insightsTab === 'nutrients' && (
                    <div className="space-y-1" data-testid="insights-nutrients-content">
                      <div className="grid grid-cols-4 gap-2 text-xs text-muted-foreground font-semibold uppercase pb-1 border-b border-border/50 px-1">
                        <span></span><span className="text-right">{t('diet.totalLabel')}</span><span className="text-right">{t('diet.goalLabel')}</span><span className="text-right">{t('diet.leftLabel')}</span>
                      </div>
                      {[
                        { label: t('diet.calories'), total: totals.calories, goal: target.calories, unit: 'kcal', color: 'text-primary' },
                        { label: t('diet.protein'), total: totals.protein, goal: target.protein, unit: 'g', color: 'text-red-500' },
                        { label: t('diet.carbs'), total: totals.carbs, goal: target.carbs, unit: 'g', color: 'text-yellow-500' },
                        { label: t('diet.fat'), total: totals.fat, goal: target.fat, unit: 'g', color: 'text-blue-500' },
                        { label: t('diet.fiber'), total: totals.fiber, goal: 25, unit: 'g', color: 'text-green-500' },
                        { label: t('diet.sugar'), total: totals.sugar, goal: 50, unit: 'g', color: 'text-pink-500' },
                        { label: t('diet.satFatG'), total: totals.sat_fat, goal: 20, unit: 'g', color: 'text-orange-400' },
                        { label: t('diet.sodium'), total: totals.sodium, goal: 2300, unit: 'mg', color: 'text-gray-500' },
                        { label: t('diet.cholesterol'), total: totals.cholesterol, goal: 300, unit: 'mg', color: 'text-purple-400' },
                      ].map(row => {
                        const remaining = row.goal - row.total;
                        const pct = Math.min((row.total / row.goal) * 100, 100);
                        return (
                          <div key={row.label} className="py-1.5 px-1">
                            <div className="grid grid-cols-4 gap-2 text-xs items-center">
                              <span className={`font-medium ${row.color}`}>{row.label}</span>
                              <span className="text-right font-semibold">{Math.round(row.total)}{row.unit}</span>
                              <span className="text-right text-muted-foreground">{row.goal}{row.unit}</span>
                              <span className={`text-right font-medium ${remaining >= 0 ? 'text-green-500' : 'text-red-500'}`}>{Math.round(remaining)}{row.unit}</span>
                            </div>
                            <div className="w-full h-1.5 bg-muted rounded-full mt-1">
                              <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, backgroundColor: pct > 100 ? '#EF4444' : pct > 80 ? '#F59E0B' : '#22C55E' }} />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {/* MACROS TAB */}
                  {insightsTab === 'macros' && (
                    <div className="space-y-4" data-testid="insights-macros-content">
                      {/* Donut */}
                      <div className="relative flex justify-center">
                        <svg viewBox="0 0 160 160" className="w-44 h-44">
                          {(() => {
                            const macroTotal = Math.max(totals.protein + totals.carbs + totals.fat, 1);
                            const macros = [
                              { key: 'carbs', val: totals.carbs, color: '#EAB308' },
                              { key: 'fat', val: totals.fat, color: '#3B82F6' },
                              { key: 'protein', val: totals.protein, color: '#EF4444' },
                            ];
                            let cumAngle = -90;
                            return macros.map(m => {
                              const pct = m.val / macroTotal;
                              const angle = pct * 360;
                              const startRad = (cumAngle * Math.PI) / 180;
                              const endRad = ((cumAngle + angle) * Math.PI) / 180;
                              cumAngle += angle;
                              const largeArc = angle > 180 ? 1 : 0;
                              const x1 = 80 + 60 * Math.cos(startRad);
                              const y1 = 80 + 60 * Math.sin(startRad);
                              const x2 = 80 + 60 * Math.cos(endRad);
                              const y2 = 80 + 60 * Math.sin(endRad);
                              return <path key={m.key} d={`M80,80 L${x1},${y1} A60,60 0 ${largeArc},1 ${x2},${y2} Z`} fill={m.color} opacity={0.85} />;
                            });
                          })()}
                          <circle cx="80" cy="80" r="38" fill="hsl(var(--background))" />
                          <text x="80" y="85" textAnchor="middle" className="fill-muted-foreground text-xs">{t('diet.macrosDonut')}</text>
                        </svg>
                      </div>
                      {/* Macro details */}
                      <div className="space-y-3">
                        {[
                          { label: t('diet.carbs'), val: totals.carbs, goal: target.carbs, color: '#EAB308', bgColor: 'bg-yellow-500' },
                          { label: t('diet.fat'), val: totals.fat, goal: target.fat, color: '#3B82F6', bgColor: 'bg-blue-500' },
                          { label: t('diet.protein'), val: totals.protein, goal: target.protein, color: '#EF4444', bgColor: 'bg-red-500' },
                        ].map(m => {
                          const macroTotal = Math.max(totals.protein + totals.carbs + totals.fat, 1);
                          const pct = Math.round((m.val / macroTotal) * 100);
                          const goalPct = Math.round((m.goal / (target.protein + target.carbs + target.fat)) * 100);
                          return (
                            <div key={m.label} className="flex items-center gap-3">
                              <div className={`w-3 h-3 rounded-full ${m.bgColor} shrink-0`} />
                              <div className="flex-1">
                                <div className="flex justify-between text-xs">
                                  <span className="font-medium">{m.label}</span>
                                  <span className="text-muted-foreground">{Math.round(m.val)}g / {Math.round(m.goal)}g</span>
                                </div>
                                <div className="flex items-center gap-2 text-xs mt-0.5">
                                  <span className="font-bold text-lg" style={{ color: m.color }}>{pct}%</span>
                                  <span className="text-muted-foreground text-xs">{t('diet.goalPct', { pct: goalPct })}</span>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </>
              );
            })()}
        </div>
      </FullScreenPage>

      {/* Custom Food Dialog */}
      <Dialog open={showCustomFood} onOpenChange={(open) => { setShowCustomFood(open); if (!open) { if (editingFoodId) customFoodDialog.close(); setEditingFoodId(null); setShowCustomFoodMore(false); } }}>
        <DialogContent className="max-h-[85dvh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
              {editingFoodId ? t('diet.editFoodTitle') : t('diet.createCustomFoodTitle')}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            {/* ====== BASIC (required) ====== */}
            {/* The main name field IS the Portuguese name — this is a PT-first app.
                Users get a single unambiguous "Food Name (Portuguese)" input in the
                basic section, and an optional "Name (English)" translation in the
                More section below. No third generic `name` field. */}
            <div className="space-y-2">
              <Label>{t('diet.foodNamePortuguese')}</Label>
              <Input
                value={customFood.name}
                onChange={(e) => setCustomFood(p => ({ ...p, name: e.target.value, name_pt: e.target.value }))}
                placeholder="e.g. Batido de Proteína Caseiro"
                data-testid="custom-food-name"
              />
            </div>
            <div className="space-y-2">
              <Label className="flex items-center gap-2"><Scan className="h-4 w-4 text-primary" /> {t('diet.barcode', 'Barcode')}</Label>
              <Input
                value={customFood.barcode}
                onChange={(e) => setCustomFood(p => ({ ...p, barcode: e.target.value }))}
                placeholder={t('diet.barcodePlaceholder', 'Optional — e.g. 5601234567890')}
                data-testid="custom-food-barcode"
                inputMode="numeric"
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t('diet.servingSize')}</Label>
                <Input type="number" value={customFood.serving_size} onChange={(e) => setCustomFood(p => ({ ...p, serving_size: e.target.value }))} />
              </div>
              <div className="space-y-2">
                <Label>{t('diet.unit')}</Label>
                <Select value={customFood.serving_unit} onValueChange={(v) => setCustomFood(p => ({ ...p, serving_unit: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="g">{t('diet.gramsUnit')}</SelectItem>
                    <SelectItem value="ml">{t('diet.mlUnit')}</SelectItem>
                    <SelectItem value="oz">{t('diet.ozUnit')}</SelectItem>
                    <SelectItem value="cup">{t('diet.cupUnit')}</SelectItem>
                    <SelectItem value="piece">{t('diet.pieceUnit')}</SelectItem>
                    <SelectItem value="scoop">{t('diet.scoopUnit')}</SelectItem>
                    <SelectItem value="serving">{t('diet.servingUnit')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="flex items-center gap-2"><Flame className="h-4 w-4 text-primary" /> {t('diet.calories')}</Label>
                <Input type="number" value={customFood.calories} onChange={(e) => setCustomFood(p => ({ ...p, calories: e.target.value }))} placeholder="0" data-testid="custom-food-calories" />
              </div>
              <div className="space-y-2">
                <Label className="flex items-center gap-2"><Beef className="h-4 w-4 text-red-500" /> {t('diet.protein')} (g)</Label>
                <Input type="number" value={customFood.protein} onChange={(e) => setCustomFood(p => ({ ...p, protein: e.target.value }))} placeholder="0" />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="flex items-center gap-2"><Wheat className="h-4 w-4 text-yellow-500" /> {t('diet.carbs')} (g)</Label>
                <Input type="number" value={customFood.carbs} onChange={(e) => setCustomFood(p => ({ ...p, carbs: e.target.value }))} placeholder="0" />
              </div>
              <div className="space-y-2">
                <Label className="flex items-center gap-2"><Droplets className="h-4 w-4 text-blue-500" /> {t('diet.fat')} (g)</Label>
                <Input type="number" value={customFood.fat} onChange={(e) => setCustomFood(p => ({ ...p, fat: e.target.value }))} placeholder="0" />
              </div>
            </div>

            {/* ====== MORE (optional, collapsible) ====== */}
            <button
              type="button"
              onClick={() => setShowCustomFoodMore(v => !v)}
              className="w-full flex items-center justify-between py-2 px-3 rounded-lg border border-dashed border-[#FF5C00]/40 text-[#FF5C00] hover:bg-[#FF5C00]/5 transition-colors text-sm font-medium"
              data-testid="custom-food-more-toggle"
            >
              <span>{t('diet.moreDetails')} {!showCustomFoodMore && <span className="text-xs text-muted-foreground font-normal">{t('diet.moreDetailsOptional')}</span>}</span>
              {showCustomFoodMore ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            </button>

            {showCustomFoodMore && (
              <div className="space-y-4 pt-2 border-t border-border/50" data-testid="custom-food-more-section">
                <p className="text-xs text-muted-foreground">
                  {t('diet.helpOtherUsers')}
                </p>

                {/* English translation (Portuguese name is the main field in the Basic section) */}
                <div className="space-y-2">
                  <Label>{t('diet.nameEnglishTranslation')}</Label>
                  <Input value={customFood.name_en} onChange={(e) => setCustomFood(p => ({ ...p, name_en: e.target.value }))} placeholder="e.g. Whole Egg (Boiled)" data-testid="custom-food-name-en" />
                  <p className="text-xs text-muted-foreground">{t('diet.nameEnHelp')}</p>
                </div>

                {/* Category + Subcategory */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>{t('common.category')}</Label>
                    <Select value={customFood.category || undefined} onValueChange={(v) => setCustomFood(p => ({ ...p, category: v }))}>
                      <SelectTrigger data-testid="custom-food-category"><SelectValue placeholder={t('diet.categoryPickerPlaceholder')} /></SelectTrigger>
                      <SelectContent className="max-h-64">
                        {categories.filter(c => c !== 'Your Foods').map(c => (
                          <SelectItem key={c} value={c}>{c}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>{t('diet.subcategory')}</Label>
                    <Input value={customFood.subcategory} onChange={(e) => setCustomFood(p => ({ ...p, subcategory: e.target.value }))} placeholder={t('diet.subcategoryPlaceholder')} data-testid="custom-food-subcategory" />
                  </div>
                </div>

                {/* Brand + Cooking method */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>{t('diet.brand')}</Label>
                    <Input value={customFood.brand} onChange={(e) => setCustomFood(p => ({ ...p, brand: e.target.value }))} placeholder="e.g. Beyond Meat" data-testid="custom-food-brand" />
                  </div>
                  <div className="space-y-2">
                    <Label>{t('diet.cookingMethod')}</Label>
                    <Select value={customFood.cooking_method || undefined} onValueChange={(v) => setCustomFood(p => ({ ...p, cooking_method: v }))}>
                      <SelectTrigger data-testid="custom-food-cooking-method"><SelectValue placeholder={t('diet.cookingMethodNone')} /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="raw">{t('diet.cookingRaw')}</SelectItem>
                        <SelectItem value="boiled">{t('diet.cookingBoiled')}</SelectItem>
                        <SelectItem value="grilled">{t('diet.cookingGrilled')}</SelectItem>
                        <SelectItem value="roasted">{t('diet.cookingRoasted')}</SelectItem>
                        <SelectItem value="baked">{t('diet.cookingBaked')}</SelectItem>
                        <SelectItem value="fried">{t('diet.cookingFried')}</SelectItem>
                        <SelectItem value="air-fried">{t('diet.cookingAirFried')}</SelectItem>
                        <SelectItem value="steamed">{t('diet.cookingSteamed')}</SelectItem>
                        <SelectItem value="poached">{t('diet.cookingPoached')}</SelectItem>
                        <SelectItem value="scrambled">{t('diet.cookingScrambled')}</SelectItem>
                        <SelectItem value="smoked">{t('diet.cookingSmoked')}</SelectItem>
                        <SelectItem value="pan-fried">{t('diet.cookingPanFried')}</SelectItem>
                        <SelectItem value="stewed">{t('diet.cookingStewed')}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Detailed nutrition */}
                <div>
                  <p className="text-base md:text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">{t('diet.detailedNutritionPerServing')}</p>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    <div className="space-y-1">
                      <Label className="text-base md:text-xs">{t('diet.fiberG')}</Label>
                      <Input type="number" value={customFood.fiber} onChange={(e) => setCustomFood(p => ({ ...p, fiber: e.target.value }))} placeholder="0" />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-base md:text-xs">{t('diet.sugarG')}</Label>
                      <Input type="number" value={customFood.sugar} onChange={(e) => setCustomFood(p => ({ ...p, sugar: e.target.value }))} placeholder="0" />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-base md:text-xs">{t('diet.satFatG')}</Label>
                      <Input type="number" value={customFood.saturated_fat} onChange={(e) => setCustomFood(p => ({ ...p, saturated_fat: e.target.value }))} placeholder="0" />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-base md:text-xs">{t('diet.sodiumMg')}</Label>
                      <Input type="number" value={customFood.sodium} onChange={(e) => setCustomFood(p => ({ ...p, sodium: e.target.value }))} placeholder="0" />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-base md:text-xs">{t('diet.cholesterolMg')}</Label>
                      <Input type="number" value={customFood.cholesterol} onChange={(e) => setCustomFood(p => ({ ...p, cholesterol: e.target.value }))} placeholder="0" />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-base md:text-xs">{t('diet.potassiumMg')}</Label>
                      <Input type="number" value={customFood.potassium} onChange={(e) => setCustomFood(p => ({ ...p, potassium: e.target.value }))} placeholder="0" />
                    </div>
                  </div>
                </div>

                {/* Allergens (multi-chip) */}
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-wide text-muted-foreground">{t('diet.allergens')}</Label>
                  <div className="flex flex-wrap gap-1.5">
                    {['gluten','dairy','eggs','soy','nuts','peanuts','shellfish','fish','sesame','sulphites','mustard','celery','lupin','molluscs'].map(a => {
                      const active = customFood.allergens.includes(a);
                      return (
                        <Badge
                          key={a}
                          variant={active ? 'default' : 'outline'}
                          className={`cursor-pointer text-xs ${active ? 'bg-red-500/20 text-red-400 border-red-500/40' : ''}`}
                          onClick={() => setCustomFood(p => ({
                            ...p,
                            allergens: active ? p.allergens.filter(x => x !== a) : [...p.allergens, a],
                          }))}
                        >
                          {a}
                        </Badge>
                      );
                    })}
                  </div>
                </div>

                {/* Dietary preferences (multi-chip) */}
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-wide text-muted-foreground">{t('diet.dietaryPreferences')}</Label>
                  <div className="flex flex-wrap gap-1.5">
                    {['vegan','vegetarian','pescatarian','gluten-free','dairy-free','keto','paleo','halal','kosher','low-carb','high-protein','low-fat'].map(p => {
                      const active = customFood.preferences.includes(p);
                      return (
                        <Badge
                          key={p}
                          variant={active ? 'default' : 'outline'}
                          className={`cursor-pointer text-xs ${active ? 'bg-emerald-500/20 text-emerald-400 border-emerald-500/40' : ''}`}
                          onClick={() => setCustomFood(prev => ({
                            ...prev,
                            preferences: active ? prev.preferences.filter(x => x !== p) : [...prev.preferences, p],
                          }))}
                        >
                          {p}
                        </Badge>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowCustomFood(false); if (editingFoodId) customFoodDialog.close(); setEditingFoodId(null); setShowCustomFoodMore(false); }}>{t('common.cancel')}</Button>
            <Button onClick={handleCreateCustomFood} className="bg-primary hover:bg-primary/90" disabled={!customFood.name.trim()} data-testid="save-custom-food-btn">
              {t('diet.saveToYourFoods')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ==================== EDIT DIET LOG DIALOG ==================== */}
      <Dialog open={showEditDialog} onOpenChange={(open) => { if (!open) editEntryDialog.close(); }}>
        <DialogContent className="max-w-xl max-h-[85dvh] overflow-y-auto" data-testid="edit-diet-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
              EDIT <span className="text-primary">{t('diet.dietLog')}</span>
              <span className="text-sm font-normal text-muted-foreground ml-2">{formatDateWithMonth(selectedDate)}</span>
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-3">
            {activeMealList.map(mealType => {
              const entries = editEntries.filter(e => e.meal_type === mealType.value);
              if (entries.length === 0) return null;
              return (
                <div key={mealType.value} className="space-y-2">
                  <p className="text-sm font-semibold flex items-center gap-2">
                    <span>{mealType.icon}</span> {mealType.label}
                  </p>
                  {entries.map((entry) => {
                    const realIdx = editEntries.indexOf(entry);
                    return (
                      <div key={realIdx} className="flex items-center gap-2 p-2 rounded-lg bg-muted/50">
                        <div className="flex-1 min-w-0">
                          <p className="text-base md:text-sm font-medium truncate">{entry.food_name}</p>
                          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-1">
                            <div>
                              <Label className="text-base md:text-xs text-muted-foreground">{t('diet.qty')}</Label>
                              <Input type="number" step="0.5" value={entry.quantity} className="text-base md:text-xs"
                                onChange={(e) => updateEditEntry(realIdx, 'quantity', parseFloat(e.target.value) || 0)}
                                data-testid={`edit-qty-${realIdx}`} />
                            </div>
                            <div>
                              <Label className="text-base md:text-xs text-muted-foreground">{t('diet.cals')}</Label>
                              <Input type="number" value={Math.round(entry.calories)} className="text-base md:text-xs"
                                onChange={(e) => updateEditEntry(realIdx, 'calories', parseFloat(e.target.value) || 0)}
                                data-testid={`edit-cal-${realIdx}`} />
                            </div>
                            <div>
                              <Label className="text-base md:text-xs text-muted-foreground">{t('diet.protein')}</Label>
                              <Input type="number" value={Math.round(entry.protein)} className="text-base md:text-xs"
                                onChange={(e) => updateEditEntry(realIdx, 'protein', parseFloat(e.target.value) || 0)}
                                data-testid={`edit-protein-${realIdx}`} />
                            </div>
                            <div>
                              <Label className="text-base md:text-xs text-muted-foreground">{t('diet.carbs')}</Label>
                              <Input type="number" value={Math.round(entry.carbs)} className="text-base md:text-xs"
                                onChange={(e) => updateEditEntry(realIdx, 'carbs', parseFloat(e.target.value) || 0)}
                                data-testid={`edit-carbs-${realIdx}`} />
                            </div>
                          </div>
                        </div>
                        <Button variant="ghost" size="icon" className="h-11 w-11 md:h-8 md:w-8 text-destructive hover:text-destructive shrink-0"
                          onClick={() => removeEditEntry(realIdx)} data-testid={`remove-entry-${realIdx}`} tooltip={t('tooltips.remove')}>
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    );
                  })}
                </div>
              );
            })}
            {editEntries.length === 0 && (
              <p className="text-center text-muted-foreground py-4 text-sm">{t('diet.noEntriesLeft')}</p>
            )}
          </div>

          <DialogFooter className="flex-col sm:flex-row gap-2 mt-4">
            <Button variant="destructive" onClick={handleDeleteLog} disabled={deleteLogMutation.isPending} className="sm:mr-auto" data-testid="delete-diet-log-btn">
              {deleteLogMutation.isPending ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />{t('common.deleting')}</> : <><Trash2 className="h-4 w-4 mr-2" />{t('diet.deleteEntireLog')}</>}
            </Button>
            <Button variant="outline" onClick={() => editEntryDialog.close()}>{t('common.cancel')}</Button>
            <Button className="bg-primary hover:bg-primary/90" onClick={handleSaveEditLog} disabled={saveEditLogMutation.isPending} data-testid="save-diet-edit-btn">
              {saveEditLogMutation.isPending ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />{t('common.saving')}</> : t('common.saveChanges')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ==================== BARCODE SCANNER DIALOG ==================== */}
      <BarcodeScanner
        open={showBarcodeScanner}
        onClose={() => setShowBarcodeScanner(false)}
        onScan={handleBarcodeScanned}
      />

      {/* ==================== COMPLETE DAY CONFIRM DIALOG ==================== */}
      <Dialog open={showCompleteConfirm} onOpenChange={setShowCompleteConfirm}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
              <CheckCircle className="h-5 w-5 text-green-500" />
              {t('diet.completeDayTitle')}
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">{t('diet.completeDayConfirm')}</p>
          <DialogFooter className="flex gap-2 sm:gap-2 mt-2">
            <Button variant="outline" onClick={() => setShowCompleteConfirm(false)}>{t('common.cancel')}</Button>
            <Button className="bg-green-600 hover:bg-green-700 text-white" onClick={handleCompleteDay} disabled={completingDay}>
              {completingDay ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <CheckCircle className="h-4 w-4 mr-1" />}
              {t('diet.completeBtn')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ==================== UNLOCK DAY CONFIRM DIALOG ==================== */}
      <Dialog open={showUnlockConfirm} onOpenChange={setShowUnlockConfirm}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
              <Lock className="h-5 w-5 text-primary" />
              {t('diet.unlockDayTitle')}
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">{t('diet.unlockDayConfirm')}</p>
          <DialogFooter className="flex gap-2 sm:gap-2 mt-2">
            <Button variant="outline" onClick={() => setShowUnlockConfirm(false)}>{t('common.cancel')}</Button>
            <Button className="bg-primary hover:bg-[#e05200] text-white" onClick={handleUnlockDay} disabled={unlockingDay}>
              {unlockingDay ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Lock className="h-4 w-4 mr-1" />}
              {t('diet.unlockBtn')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ==================== DIET DAY STORY GENERATOR ==================== */}
      {showDietDayStory && (
        <DietDayStoryGenerator
          foodLog={foodLog}
          targets={targets}
          date={selectedDate}
          user={user}
          onBehalfOf={onBehalfOf}
          onClose={() => setShowDietDayStory(false)}
        />
      )}

      {/* ==================== SHARE MEAL PREVIEW DIALOG ==================== */}
      <Dialog open={showSharePreview} onOpenChange={setShowSharePreview}>
        <DialogContent className="sm:max-w-md" data-testid="share-meal-preview-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
              SHARE <span className="text-primary">MEAL</span>
            </DialogTitle>
          </DialogHeader>
          {sharePreviewData && (
            <div className="space-y-4">
              {/* Post Preview Card */}
              <div className="rounded-xl border border-border/50 overflow-hidden bg-muted/30">
                {/* Photo area */}
                <div className="relative">
                  {sharePhoto ? (
                    <div className="relative aspect-video w-full overflow-hidden bg-black">
                      <img src={sharePhoto} alt="Meal" className="w-full h-full object-cover" />
                      <UITooltip>
                        <TooltipTrigger asChild>
                          <button
                            onClick={() => setSharePhoto(null)}
                            className="absolute top-2 right-2 p-1.5 rounded-full bg-black/60 text-white hover:bg-black/80 transition-colors"
                            data-testid="remove-share-photo-btn"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </TooltipTrigger>
                        <TooltipContent>{t('tooltips.removePhoto')}</TooltipContent>
                      </UITooltip>
                    </div>
                  ) : (
                    <label className="block cursor-pointer aspect-video w-full bg-muted/50 border-b border-dashed border-border flex items-center justify-center hover:bg-muted/80 transition-colors">
                      <div className="flex flex-col items-center gap-2 text-muted-foreground">
                        <Camera className="h-8 w-8" />
                        <span className="text-base md:text-sm font-medium">{t('diet.addMealPhoto')}</span>
                        <span className="text-base md:text-xs">{t('diet.optionalLabel')}</span>
                      </div>
                      <input
                        type="file"
                        accept="image/*"
                        className="sr-only"
                        onChange={async (e) => {
                          const file = e.target.files?.[0];
                          if (!file) return;
                          const { compressImage } = await import('../utils/compressImage');
                          const compressed = await compressImage(file);
                          setSharePhoto(compressed);
                          e.target.value = '';
                        }}
                        data-testid="share-photo-input"
                      />
                    </label>
                  )}
                </div>

                {/* Post content preview */}
                <div className="p-4 space-y-3">
                  <div className="flex items-center gap-2">
                    <span className="text-lg">{sharePreviewData.meal_icon}</span>
                    <span className="font-semibold">{sharePreviewData.meal_label}</span>
                    <Badge variant="outline" className="text-primary border-primary/30 ml-auto">
                      {Math.round(sharePreviewData.calories)} kcal
                    </Badge>
                  </div>

                  {/* Foods list */}
                  <div className="space-y-1">
                    {sharePreviewData.foods.map((food, i) => (
                      <div key={i} className="flex justify-between text-xs">
                        <span className="text-muted-foreground">{food.name}</span>
                        <span className="font-medium">{food.calories} kcal</span>
                      </div>
                    ))}
                  </div>

                  {/* Macros bar */}
                  <div className="flex justify-between text-xs pt-2 border-t border-border/50 font-medium">
                    <span className="text-red-500">P: {Math.round(sharePreviewData.protein)}g</span>
                    <span className="text-yellow-500">C: {Math.round(sharePreviewData.carbs)}g</span>
                    <span className="text-blue-500">F: {Math.round(sharePreviewData.fat)}g</span>
                  </div>
                </div>
              </div>
            </div>
          )}
          <DialogFooter className="flex gap-2 sm:gap-2">
            <Button variant="outline" onClick={() => setShowSharePreview(false)} data-testid="cancel-share-btn">
              {t('common.cancel')}
            </Button>
            <Button
              className="bg-blue-500 hover:bg-blue-600 text-white"
              disabled={sharingPost}
              onClick={async () => {
                setSharingPost(true);
                try {
                  const mealPostUrl = onBehalfOf
                    ? `/social/posts/meal?on_behalf_of=${onBehalfOf}`
                    : `/social/posts/meal`;
                  await client.post(mealPostUrl, {
                    ...sharePreviewData,
                    image: sharePhoto || undefined,
                  });
                  toast.success(t('diet.mealShared'));
                  setShowSharePreview(false);
                } catch { toast.error(t('diet.failedShareMeal')); }
                finally { setSharingPost(false); }
              }}
              data-testid="confirm-share-btn"
            >
              {sharingPost ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Share2 className="h-4 w-4 mr-1" />}
              {t('diet.shareToFeed')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Food Detail Preview Dialog */}
      <Dialog open={showFoodPreview} onOpenChange={(open) => { if (!open) previewDialog.close(); }}>
        <DialogContent className="max-w-lg max-h-[90dvh] overflow-y-auto" data-testid="diet-food-preview-dialog">
          {previewFood && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2 text-lg font-bold uppercase tracking-wide" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                  <Apple className="h-5 w-5 text-primary" />
                  {previewFood.name || previewFood.food_name}
                </DialogTitle>
                <div className="flex flex-wrap gap-2 mt-2">
                  {previewFood.category && <Badge variant="secondary">{previewFood.category}</Badge>}
                  {previewFood.brand && <Badge variant="outline">{previewFood.brand}</Badge>}
                  {previewFood.source === 'fatsecret' && <Badge className="bg-green-500/10 text-green-400 border-green-500/30 text-xs">FatSecret</Badge>}
                  {previewFood.is_custom && <Badge className="bg-primary/10 text-primary border-primary/30">Custom</Badge>}
                  {previewFood.food_type && previewFood.food_type !== 'Generic' && <Badge variant="outline" className="text-xs">{previewFood.food_type}</Badge>}
                </div>
              </DialogHeader>

              <div className="space-y-4">
                {previewLoading && (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Loader2 className="h-4 w-4 animate-spin" /> {t('diet.loadingNutritionData')}
                  </div>
                )}

                {/* Logged Amount Info */}
                {previewFood._logged_quantity && (
                  <div className="text-sm text-muted-foreground bg-muted/30 rounded-lg px-3 py-2">
                    {t('diet.loggedTotals')}: {previewFood._logged_quantity} {previewFood._logged_unit || 'g'} ({previewFood._logged_servings || 1} {(previewFood._logged_servings || 1) !== 1 ? t('diet.servingsLabel') : t('diet.servingUnit')})
                  </div>
                )}

                {/* Serving Info */}
                <div className="text-sm text-muted-foreground">
                  {t('diet.perServingDetail', { serving: previewFood.serving_description || `${previewFood.serving_size || 100}${previewFood.serving_unit || 'g'}` })}
                </div>

                {/* Primary Macros */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  <div className="rounded-xl p-3 bg-primary/10 text-center">
                    <p className="text-lg font-black text-primary">{Math.round(previewFood.calories)}</p>
                    <p className="text-xs text-muted-foreground font-medium uppercase tracking-wide">kcal</p>
                  </div>
                  <div className="rounded-xl p-3 bg-red-500/10 text-center">
                    <p className="text-lg font-black text-red-500">{previewFood.protein}g</p>
                    <p className="text-xs text-muted-foreground font-medium uppercase tracking-wide">{t('diet.protein')}</p>
                  </div>
                  <div className="rounded-xl p-3 bg-yellow-500/10 text-center">
                    <p className="text-lg font-black text-yellow-500">{previewFood.carbs}g</p>
                    <p className="text-xs text-muted-foreground font-medium uppercase tracking-wide">{t('diet.carbs')}</p>
                  </div>
                  <div className="rounded-xl p-3 bg-blue-500/10 text-center">
                    <p className="text-lg font-black text-blue-500">{previewFood.fat}g</p>
                    <p className="text-xs text-muted-foreground font-medium uppercase tracking-wide">{t('diet.fat')}</p>
                  </div>
                </div>

                {/* Logged totals if different from base serving */}
                {previewFood._logged_servings && previewFood._logged_servings !== 1 && (
                  <div className="bg-muted/30 rounded-lg p-3">
                    <h4 className="text-xs font-semibold text-muted-foreground mb-2 uppercase tracking-wide">{t('diet.loggedTotals')}</h4>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-center text-sm">
                      <div><span className="font-bold text-primary">{previewFood._logged_calories}</span><p className="text-xs text-muted-foreground">kcal</p></div>
                      <div><span className="font-bold text-red-500">{previewFood._logged_protein}g</span><p className="text-xs text-muted-foreground">{t('diet.protein')}</p></div>
                      <div><span className="font-bold text-yellow-500">{previewFood._logged_carbs}g</span><p className="text-xs text-muted-foreground">{t('diet.carbs')}</p></div>
                      <div><span className="font-bold text-blue-500">{previewFood._logged_fat}g</span><p className="text-xs text-muted-foreground">{t('diet.fat')}</p></div>
                    </div>
                  </div>
                )}

                {/* Detailed Nutrition - always shown */}
                <div className="space-y-2">
                  <h4 className="text-sm font-semibold" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>{t('diet.detailedNutrition')}</h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1.5 text-sm">
                    <div className="flex justify-between"><span className="text-muted-foreground">{t('diet.saturatedFat')}</span><span>{previewFood.saturated_fat != null ? `${previewFood.saturated_fat}g` : '—'}</span></div>
                    <div className="flex justify-between"><span className="text-muted-foreground">{t('diet.transFat')}</span><span>{previewFood.trans_fat != null ? `${previewFood.trans_fat}g` : '—'}</span></div>
                    <div className="flex justify-between"><span className="text-muted-foreground">{t('diet.cholesterol')}</span><span>{previewFood.cholesterol != null ? `${Math.round(previewFood.cholesterol)}mg` : '—'}</span></div>
                    <div className="flex justify-between"><span className="text-muted-foreground">{t('diet.sodium')}</span><span>{previewFood.sodium != null ? `${Math.round(previewFood.sodium)}mg` : '—'}</span></div>
                    <div className="flex justify-between"><span className="text-muted-foreground">{t('diet.fiber')}</span><span>{previewFood.fiber != null ? `${previewFood.fiber}g` : '—'}</span></div>
                    <div className="flex justify-between"><span className="text-muted-foreground">{t('diet.sugar')}</span><span>{previewFood.sugar != null ? `${previewFood.sugar}g` : '—'}</span></div>
                    <div className="flex justify-between"><span className="text-muted-foreground">{t('diet.potassium')}</span><span>{previewFood.potassium != null ? `${Math.round(previewFood.potassium)}mg` : '—'}</span></div>
                    <div className="flex justify-between"><span className="text-muted-foreground">{t('diet.calcium')}</span><span>{previewFood.calcium != null ? `${Math.round(previewFood.calcium)}mg` : '—'}</span></div>
                    <div className="flex justify-between"><span className="text-muted-foreground">{t('diet.iron')}</span><span>{previewFood.iron != null ? `${previewFood.iron}mg` : '—'}</span></div>
                    <div className="flex justify-between"><span className="text-muted-foreground">{t('diet.vitaminA')}</span><span>{previewFood.vitamin_a != null && previewFood.vitamin_a > 0 ? `${Math.round(previewFood.vitamin_a)}%` : '—'}</span></div>
                    <div className="flex justify-between"><span className="text-muted-foreground">{t('diet.vitaminC')}</span><span>{previewFood.vitamin_c != null && previewFood.vitamin_c > 0 ? `${Math.round(previewFood.vitamin_c)}%` : '—'}</span></div>
                  </div>
                </div>

                {/* Serving Options */}
                {previewFood.servings?.length > 1 && (
                  <div className="space-y-2">
                    <h4 className="text-sm font-semibold" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>{t('diet.availableServings')}</h4>
                    <div className="space-y-1">
                      {previewFood.servings.map((s, i) => (
                        <div key={i} className="flex justify-between text-xs py-1.5 px-2 rounded bg-muted/50">
                          <span>{s.description}</span>
                          <span className="text-muted-foreground">{s.calories} kcal | P:{s.protein}g C:{s.carbs}g F:{s.fat}g</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Food URL */}
                {previewFood.food_url && (
                  <a href={previewFood.food_url} target="_blank" rel="noopener noreferrer" className="text-xs text-primary hover:underline">
                    {t('diet.viewOnFatSecret')}
                  </a>
                )}
              </div>

              <DialogFooter className="flex gap-2 sm:gap-2">
                <Button
                  variant="outline"
                  onClick={() => {
                    previewDialog.close();
                    const idx = previewFood._entry_index;
                    if (idx != null) {
                      setInlineEditIdx(idx);
                      const entry = foodLog?.entries?.[idx];
                      setInlineEditQty(entry?.servings || 1);
                    }
                  }}
                  className="flex-1"
                  data-testid="food-preview-edit-btn"
                >
                  <Pencil className="h-4 w-4 mr-1" /> {t('common.edit')}
                </Button>
                <Button onClick={() => previewDialog.close()} className="flex-1 bg-primary hover:bg-primary/90" data-testid="food-preview-close-btn">
                  {t('diet.gotIt')}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* ==================== MACRO SETUP WIZARD ==================== */}
      <MacroSetupWizard open={showMacroWizard} onClose={() => setShowMacroWizard(false)} />

      {/* ==================== DIET ACTIVATION PROMPT ==================== */}
      <Dialog open={showDietActivatePrompt} onOpenChange={setShowDietActivatePrompt}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>{t('diet.activateDiet')}</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            {t('diet.activatePromptMessage')}
          </p>
          <DialogFooter className="flex gap-2 sm:gap-2">
            <Button
              variant="outline"
              className="flex-1"
              onClick={() => {
                updateDietSettingsMutation.mutate({ diet_tracking_enabled: true, diet_reminders_enabled: false });
                setShowDietActivatePrompt(false);
              }}
            >
              {t('diet.noReminders')}
            </Button>
            <Button
              className="flex-1 bg-primary hover:bg-primary/90"
              onClick={() => {
                updateDietSettingsMutation.mutate({ diet_tracking_enabled: true, diet_reminders_enabled: true });
                setShowDietActivatePrompt(false);
              }}
            >
              {t('diet.ok')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ==================== FOOD ENTRY ACTION SHEET (long-press) ==================== */}
      <FoodEntryActionSheet
        entry={actionEntry?.entry || null}
        currentMealType={actionEntry?.mealType}
        open={!!actionEntry}
        onOpenChange={(o) => { if (!o) setActionEntry(null); }}
        onEdit={handleEditEntry}
        onDuplicate={handleDuplicateEntry}
        onDelete={handleDeleteEntry}
      />

      {/* ==================== DIET PLAN PROGRESS SHEET ==================== */}
      <Sheet open={showDietPlanProgress} onOpenChange={setShowDietPlanProgress}>
        <SheetContent side="right" className="w-full sm:max-w-md p-0 flex flex-col">
          <SheetHeader className="sm:p-4">
            <SheetTitle style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
              {t('diet.dietPlanProgress')}
            </SheetTitle>
          </SheetHeader>
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {dietPlanProgressLoading && !dietPlanProgress ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-primary" />
              </div>
            ) : dietPlanProgress?.has_plan ? (
              <>
                {/* Top progress bar */}
                <div className="rounded-lg border border-border/50 p-4 space-y-3">
                  <div className="flex items-end justify-between gap-2">
                    <div>
                      <p className="text-xs text-muted-foreground uppercase tracking-wide">{t('dietProgress.dayOf', { current: dietPlanProgress.days_elapsed, total: dietPlanProgress.total_days })}</p>
                      <p className="text-3xl font-bold font-mono">{dietPlanProgress.progress_percent}%</p>
                    </div>
                    <p className="text-xs text-muted-foreground text-right">
                      {dietPlanProgress.days_remaining > 0
                        ? t('diet.daysRemaining', { count: dietPlanProgress.days_remaining })
                        : t('diet.dietPlanCompleted')}
                    </p>
                  </div>
                  <div className="w-full h-2 bg-muted rounded-full overflow-hidden">
                    <div className="h-full rounded-full bg-primary transition-all" style={{ width: `${dietPlanProgress.progress_percent}%` }} />
                  </div>
                  <div className="flex justify-between text-[10px] text-muted-foreground">
                    <span>{formatDateWithMonth(dietPlanProgress.diet_start_date)}</span>
                    <span>{formatDateWithMonth(dietPlanProgress.diet_end_date)}</span>
                  </div>
                </div>

                {/* Stat cards grid */}
                <div className="grid grid-cols-2 gap-3">
                  {dietPlanProgress.weight_change_kg !== null && (
                    <div className="rounded-lg border border-border/50 p-3">
                      <div className="flex items-center gap-1.5 text-xs text-muted-foreground mb-1">
                        {dietPlanProgress.weight_change_kg < 0 ? <TrendingDown className="h-3.5 w-3.5 text-green-500" /> : dietPlanProgress.weight_change_kg > 0 ? <TrendingUp className="h-3.5 w-3.5 text-yellow-500" /> : <Target className="h-3.5 w-3.5" />}
                        <span>{t('dietProgress.weightChange')}</span>
                      </div>
                      <p className="text-2xl font-bold font-mono">
                        {dietPlanProgress.weight_change_kg > 0 ? '+' : ''}{dietPlanProgress.weight_change_kg} kg
                      </p>
                      <p className="text-[10px] text-muted-foreground mt-0.5">
                        {dietPlanProgress.starting_weight_kg} → {dietPlanProgress.current_weight_kg} kg
                      </p>
                    </div>
                  )}
                  <div className="rounded-lg border border-border/50 p-3">
                    <div className="flex items-center gap-1.5 text-xs text-muted-foreground mb-1">
                      <Calendar className="h-3.5 w-3.5 text-primary" />
                      <span>{t('dietProgress.daysLogged')}</span>
                    </div>
                    <p className="text-2xl font-bold font-mono">{dietPlanProgress.days_logged}</p>
                    <p className="text-[10px] text-muted-foreground mt-0.5">
                      {t('dietProgress.outOf', { total: dietPlanProgress.days_elapsed })}
                    </p>
                  </div>
                  <div className="rounded-lg border border-border/50 p-3">
                    <div className="flex items-center gap-1.5 text-xs text-muted-foreground mb-1">
                      <Target className="h-3.5 w-3.5 text-green-500" />
                      <span>{t('dietProgress.adherence')}</span>
                    </div>
                    <p className="text-2xl font-bold font-mono">{dietPlanProgress.adherence_percent}%</p>
                    <p className="text-[10px] text-muted-foreground mt-0.5">
                      {t('dietProgress.daysOnTarget', { count: dietPlanProgress.on_target_days })}
                    </p>
                  </div>
                  <div className="rounded-lg border border-border/50 p-3">
                    <div className="flex items-center gap-1.5 text-xs text-muted-foreground mb-1">
                      <Zap className="h-3.5 w-3.5 text-yellow-500" />
                      <span>{t('dietProgress.streak')}</span>
                    </div>
                    <p className="text-2xl font-bold font-mono">{dietPlanProgress.longest_streak}</p>
                    <p className="text-[10px] text-muted-foreground mt-0.5">
                      {t('dietProgress.currentStreak', { count: dietPlanProgress.current_streak })}
                    </p>
                  </div>
                </div>

                {/* Average daily macros */}
                {dietPlanProgress.days_logged > 0 && (
                  <div className="rounded-lg border border-border/50 p-4 space-y-2.5">
                    <p className="text-xs font-bold uppercase tracking-wide text-muted-foreground">
                      {t('dietProgress.averageDaily')}
                    </p>
                    {[
                      { label: t('diet.calories'), avg: dietPlanProgress.averages.calories, target: dietPlanProgress.targets.calories, unit: 'kcal', icon: Flame, textColor: 'text-primary', barColor: 'bg-primary' },
                      { label: t('diet.protein'), avg: dietPlanProgress.averages.protein, target: dietPlanProgress.targets.protein, unit: 'g', icon: Beef, textColor: 'text-red-500', barColor: 'bg-red-500' },
                      { label: t('diet.carbs'), avg: dietPlanProgress.averages.carbs, target: dietPlanProgress.targets.carbs, unit: 'g', icon: Wheat, textColor: 'text-yellow-500', barColor: 'bg-yellow-500' },
                      { label: t('diet.fat'), avg: dietPlanProgress.averages.fat, target: dietPlanProgress.targets.fat, unit: 'g', icon: Cookie, textColor: 'text-amber-500', barColor: 'bg-amber-500' },
                      ...(user?.water_tracking_enabled ? [{ label: t('dietProgress.water'), avg: dietPlanProgress.averages.water_ml, target: dietPlanProgress.targets.water_ml, unit: 'ml', icon: Droplets, textColor: 'text-blue-500', barColor: 'bg-blue-500' }] : []),
                    ].map(row => {
                      const pct = row.target > 0 ? Math.min(100, (row.avg / row.target) * 100) : 0;
                      const diff = Math.round(row.avg - row.target);
                      return (
                        <div key={row.label} className="space-y-1">
                          <div className="flex items-center justify-between text-sm">
                            <div className="flex items-center gap-1.5">
                              <row.icon className={`h-3.5 w-3.5 ${row.textColor}`} />
                              <span>{row.label}</span>
                            </div>
                            <div className="flex items-baseline gap-1">
                              <span className="font-mono">{Math.round(row.avg)}</span>
                              <span className="text-xs text-muted-foreground">/ {Math.round(row.target)} {row.unit}</span>
                              <span className={`text-[10px] font-mono ${diff < 0 ? 'text-orange-500' : diff > 0 ? 'text-blue-500' : 'text-muted-foreground'}`}>
                                ({diff > 0 ? '+' : ''}{diff})
                              </span>
                            </div>
                          </div>
                          <div className="w-full h-1.5 bg-muted rounded-full overflow-hidden">
                            <div className={`h-full rounded-full ${row.barColor}`} style={{ width: `${pct}%` }} />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </>
            ) : (
              <p className="text-sm text-muted-foreground text-center py-8">
                {t('dietProgress.noPlan')}
              </p>
            )}
          </div>
        </SheetContent>
      </Sheet>

      {/* ==================== DIET SETTINGS SHEET (gear button) ==================== */}
      <Sheet open={showDietSettingsSheet} onOpenChange={setShowDietSettingsSheet}>
        <SheetContent side="right" className="w-full sm:max-w-md p-0 flex flex-col">
          <SheetHeader className="sm:p-4">
            <SheetTitle style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
              {t('diet.dietSettings')}
            </SheetTitle>
          </SheetHeader>
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {(() => {
              const coachManaged = user?.coach_diet_active || (user?.coach_id && user?.macros_locked_by_coach);
              return (
                <>
                  {/* Diet Tracking */}
                  <div className="flex items-center justify-between gap-3 p-3 rounded-lg border border-border/50">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium">{t('diet.dietTracking')}</p>
                      <p className="text-xs text-muted-foreground">{t('diet.dietTrackingDesc')}</p>
                    </div>
                    {coachManaged ? (
                      <div className="flex items-center gap-1 text-xs text-muted-foreground shrink-0">
                        <Lock className="h-3.5 w-3.5 shrink-0" />
                        <span className="truncate max-w-[120px] sm:max-w-none">{t('diet.managedByCoach')}</span>
                      </div>
                    ) : (
                      <button
                        onClick={() => {
                          if (!user?.diet_tracking_enabled) {
                            setShowDietActivatePrompt(true);
                          } else {
                            updateDietSettingsMutation.mutate({ diet_tracking_enabled: false });
                          }
                        }}
                        className={`relative w-11 h-6 rounded-full transition-colors shrink-0 ${user?.diet_tracking_enabled ? 'bg-primary' : 'bg-muted-foreground/30'}`}
                      >
                        <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white transition-transform ${user?.diet_tracking_enabled ? 'translate-x-5' : ''}`} />
                      </button>
                    )}
                  </div>

                  {/* Daily Reminders — only when diet on and client manages */}
                  {user?.diet_tracking_enabled && !coachManaged && (
                    <div className="flex items-center justify-between gap-3 p-3 rounded-lg border border-border/50">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium">{t('diet.dailyReminders')}</p>
                        <p className="text-xs text-muted-foreground">{t('diet.dailyRemindersDesc')}</p>
                      </div>
                      <button
                        onClick={() => {
                          updateDietSettingsMutation.mutate({ diet_reminders_enabled: !user?.diet_reminders_enabled });
                        }}
                        className={`relative w-11 h-6 rounded-full transition-colors shrink-0 ${user?.diet_reminders_enabled !== false ? 'bg-primary' : 'bg-muted-foreground/30'}`}
                      >
                        <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white transition-transform ${user?.diet_reminders_enabled !== false ? 'translate-x-5' : ''}`} />
                      </button>
                    </div>
                  )}

                  {/* Coach locked reminders */}
                  {coachManaged && (
                    <div className="flex items-center justify-between gap-3 p-3 rounded-lg border border-border/50">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium">{t('diet.dailyReminders')}</p>
                        <p className="text-xs text-muted-foreground">{t('diet.dailyRemindersDesc')}</p>
                      </div>
                      <div className="flex items-center gap-1 text-xs text-muted-foreground shrink-0">
                        <Lock className="h-3.5 w-3.5 shrink-0" />
                        <span className="truncate max-w-[120px] sm:max-w-none">{t('diet.managedByCoach')}</span>
                      </div>
                    </div>
                  )}

                  {/* Water tracking toggle */}
                  <div className="flex items-center justify-between gap-3 p-3 rounded-lg border border-border/50">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium">{t('water.trackingLabel')}</p>
                      <p className="text-xs text-muted-foreground">{t('water.trackingDesc')}</p>
                    </div>
                    <button
                      onClick={() => updateDietSettingsMutation.mutate({ water_tracking_enabled: !user?.water_tracking_enabled })}
                      className={`relative w-11 h-6 rounded-full transition-colors shrink-0 ${user?.water_tracking_enabled ? 'bg-primary' : 'bg-muted-foreground/30'}`}
                    >
                      <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white transition-transform ${user?.water_tracking_enabled ? 'translate-x-5' : ''}`} />
                    </button>
                  </div>

                  {/* Daily water target — only shown when tracking is on */}
                  {user?.water_tracking_enabled && (
                    <div className="p-3 rounded-lg border border-border/50 space-y-2">
                      <div>
                        <p className="text-base md:text-sm font-medium">{t('water.settingsLabel')}</p>
                        <p className="text-base md:text-xs text-muted-foreground">{t('water.settingsDesc')}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Input
                          type="number"
                          inputMode="numeric"
                          min={500}
                          step={250}
                          value={waterTargetDraft}
                          onChange={(e) => setWaterTargetDraft(e.target.value)}
                          className="flex-1"
                        />
                        <Button
                          size="sm"
                          disabled={!waterTargetDraft || Number(waterTargetDraft) <= 0 || Number(waterTargetDraft) === user?.water_target_ml}
                          onClick={() => setWaterTargetMutation.mutate(Number(waterTargetDraft))}
                        >
                          {t('water.saveTarget')}
                        </Button>
                      </div>
                    </div>
                  )}

                  {/* Recalculate macros */}
                  <Button
                    variant="outline"
                    className="w-full justify-start"
                    onClick={() => {
                      setShowDietSettingsSheet(false);
                      if (coachManaged) {
                        setShowMacroLockWarning(true);
                      } else {
                        setShowMacroWizard(true);
                      }
                    }}
                  >
                    {coachManaged
                      ? <Lock className="h-4 w-4 mr-2" />
                      : <Calculator className="h-4 w-4 mr-2" />
                    }
                    {t('macroWizard.recalculate')}
                  </Button>
                </>
              );
            })()}
          </div>
        </SheetContent>
      </Sheet>

      {/* ==================== MACRO LOCK WARNING DIALOG ==================== */}
      <Dialog open={showMacroLockWarning} onOpenChange={setShowMacroLockWarning}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
              <Lock className="h-5 w-5 text-primary" />
              {t('macroWizard.lockTitle')}
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            {t('macroWizard.lockDescription')}
          </p>
          <DialogFooter className="flex gap-2 sm:gap-2">
            <Button variant="outline" onClick={() => setShowMacroLockWarning(false)}>
              {t('common.cancel')}
            </Button>
            <Button
              className="bg-primary hover:bg-primary/90 text-white"
              disabled={unlockingMacros}
              onClick={async () => {
                setUnlockingMacros(true);
                try {
                  await client.post('/diet/unlock-macros');
                  await refreshUser();
                  setShowMacroLockWarning(false);
                  setShowMacroWizard(true);
                  toast.success(t('macroWizard.unlocked'));
                } catch (err) {
                  toast.error(t('macroWizard.unlockError'));
                } finally {
                  setUnlockingMacros(false);
                }
              }}
            >
              {unlockingMacros ? t('common.loading') : t('macroWizard.unlockConfirm')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </div>
  );
}

/* ==================== WEEKLY NUTRITION SUMMARY COMPONENT ==================== */
function WeeklySummaryPanel({ summary }) {
  const { t } = useTranslation();
  const { averages, targets, adherence_percent, on_target_days, logged_days, daily_data, best_day, worst_day } = summary;

  const chartData = daily_data.map(d => {
    const dayLabel = new Date(d.date + 'T12:00:00').toLocaleDateString('en', { weekday: 'short' });
    return {
      day: dayLabel,
      calories: d.logged ? Math.round(d.calories) : 0,
      target: targets.calories,
      logged: d.logged,
      onTarget: d.on_target,
      date: d.date
    };
  });

  const adherenceColor = adherence_percent >= 70 ? 'text-green-500' : adherence_percent >= 40 ? 'text-yellow-500' : 'text-red-500';
  const adherenceBg = adherence_percent >= 70 ? 'bg-green-500/10' : adherence_percent >= 40 ? 'bg-yellow-500/10' : 'bg-red-500/10';

  const formatDay = (dateStr) => {
    if (!dateStr) return '-';
    return formatDate(dateStr + 'T12:00:00');
  };

  return (
    <Card className="border-border/50 overflow-hidden" data-testid="weekly-summary-panel">
      <div className="h-1 bg-gradient-to-r from-primary via-primary/90 to-primary" />
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
            <BarChart3 className="h-5 w-5 text-primary" />
            {t('diet.weeklySummaryTitle').split(' ')[0]} <span className="text-primary">{t('diet.weeklySummaryTitle').split(' ').slice(1).join(' ')}</span>
          </CardTitle>
          <Badge variant="outline" className="text-xs">
            <Calendar className="h-3 w-3 mr-1" />
            {t('diet.last7days')}
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-5">
        {/* Stats Row */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <div className={`p-3 rounded-lg ${adherenceBg} text-center`}>
            <p className={`text-2xl font-bold font-mono ${adherenceColor}`} data-testid="adherence-percent">
              {Math.round(adherence_percent)}%
            </p>
            <p className="text-xs text-muted-foreground">{t('diet.adherence')}</p>
          </div>
          <div className="p-3 rounded-lg bg-muted/50 text-center">
            <p className="text-2xl font-bold font-mono" data-testid="on-target-days">
              {on_target_days}<span className="text-sm text-muted-foreground">/7</span>
            </p>
            <p className="text-xs text-muted-foreground">{t('diet.daysOnTarget')}</p>
          </div>
          <div className="p-3 rounded-lg bg-muted/50 text-center">
            <p className="text-2xl font-bold font-mono" data-testid="logged-days">
              {logged_days}
            </p>
            <p className="text-xs text-muted-foreground">{t('diet.daysLogged')}</p>
          </div>
          <div className="p-3 rounded-lg bg-muted/50 text-center">
            <p className="text-2xl font-bold font-mono text-primary" data-testid="avg-calories">
              {Math.round(averages.calories)}
            </p>
            <p className="text-xs text-muted-foreground">{t('diet.avgCalories')}</p>
          </div>
        </div>

        {/* Daily Calories Chart */}
        <div className="h-[180px]" data-testid="weekly-calories-chart">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartData} margin={{ top: 5, right: 5, left: -20, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" className="stroke-border/30" vertical={false} />
              <XAxis dataKey="day" tick={{ fontSize: 12 }} className="fill-muted-foreground" />
              <YAxis tick={{ fontSize: 11 }} className="fill-muted-foreground" />
              <Tooltip
                contentStyle={{ background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: '8px', fontSize: 12 }}
                formatter={(value, name) => [value + ' kcal', name === 'calories' ? t('diet.intake') : t('diet.target')]}
              />
              <ReferenceLine y={targets.calories} stroke="#FF5C00" strokeDasharray="6 4" strokeWidth={2} label={{ value: `${t('diet.target')}: ${targets.calories}`, position: 'insideTopRight', fill: '#FF5C00', fontSize: 11 }} />
              <Bar dataKey="calories" radius={[4, 4, 0, 0]} maxBarSize={40}>
                {chartData.map((entry, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={!entry.logged ? 'hsl(var(--muted))' : entry.onTarget ? '#22c55e' : entry.calories > targets.calories ? '#ef4444' : '#FF5C00'}
                    opacity={entry.logged ? 1 : 0.3}
                  />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Macro Averages */}
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          <div className="p-3 rounded-lg bg-red-500/5 text-center">
            <p className="text-lg font-bold font-mono text-red-500" data-testid="avg-protein">{Math.round(averages.protein)}g</p>
            <p className="text-xs text-muted-foreground">{t('diet.avgProtein')}</p>
            <p className="text-xs text-muted-foreground">/ {targets.protein}g {t('diet.target').toLowerCase()}</p>
          </div>
          <div className="p-3 rounded-lg bg-yellow-500/5 text-center">
            <p className="text-lg font-bold font-mono text-yellow-500" data-testid="avg-carbs">{Math.round(averages.carbs)}g</p>
            <p className="text-xs text-muted-foreground">{t('diet.avgCarbs')}</p>
            <p className="text-xs text-muted-foreground">/ {targets.carbs}g {t('diet.target').toLowerCase()}</p>
          </div>
          <div className="p-3 rounded-lg bg-blue-500/5 text-center">
            <p className="text-lg font-bold font-mono text-blue-500" data-testid="avg-fat">{Math.round(averages.fat)}g</p>
            <p className="text-xs text-muted-foreground">{t('diet.avgFat')}</p>
            <p className="text-xs text-muted-foreground">/ {targets.fat}g {t('diet.target').toLowerCase()}</p>
          </div>
        </div>

        {/* Best / Worst Days */}
        {(best_day || worst_day) && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {best_day && (
              <div className="flex items-center gap-2 p-2 rounded-lg bg-green-500/5">
                <TrendingUp className="h-4 w-4 text-green-500 flex-shrink-0" />
                <div>
                  <p className="text-xs font-medium text-green-600">{t('diet.bestDay')}</p>
                  <p className="text-xs text-muted-foreground" data-testid="best-day">{formatDay(best_day)}</p>
                </div>
              </div>
            )}
            {worst_day && (
              <div className="flex items-center gap-2 p-2 rounded-lg bg-red-500/5">
                <TrendingDown className="h-4 w-4 text-red-500 flex-shrink-0" />
                <div>
                  <p className="text-xs font-medium text-red-600">{t('diet.needsImprovement')}</p>
                  <p className="text-xs text-muted-foreground" data-testid="worst-day">{formatDay(worst_day)}</p>
                </div>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
